import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, Calendar, Package, Clock, Phone, Mail, Globe, 
  CheckCircle, XCircle, Eye, FileText, Image, MessageCircle,
  Shield, Users, Filter, Upload, X, Download, Send, Search,
  Star, Trash2, AlertCircle, UserCheck, Activity, CreditCard,
  Plus, Edit, DollarSign, Briefcase, ChevronUp, ChevronDown,
  Type, ToggleLeft, ToggleRight, List, User, Play, Film, RefreshCw,
  Sun, Moon, Compass, MapPin, Heart, Minus, HardDrive, Edit3, Check, ChevronRight, Camera, Video, Receipt, Menu
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import '../styles/AdminPage.css';
import '../styles/AdminBookingsPro.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get the correct image URL
const getServiceImageUrl = (image) => {
  if (!image) return null;
  // If it's already a full URL, use it directly
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  // Otherwise, it's a local file - prepend the backend URL
  return `${BACKEND_URL}/api/uploads/${image}`;
};

// Service translation mapping (same as ServiceCard)
const serviceTranslationMap = {
  "Dubai Visa 14 Days": { nameKey: "dubaiVisa14Days", descKey: "desc14Days" },
  "Dubai Visa 30 Days": { nameKey: "dubaiVisa30Days", descKey: "desc30Days" },
  "Dubai Visa 60 Days": { nameKey: "dubaiVisa60Days", descKey: "desc60Days" },
  "Dubai Visa": { nameKey: "dubaiVisa30Days", descKey: "desc30Days" },
  "Freelance Visa": { nameKey: "freelanceVisaName", descKey: "descFreelance" },
  "30 Days Visa Change": { nameKey: "visaChange30Days", descKey: "descVisaChange30" },
  "60 Days Visa Change": { nameKey: "visaChange60Days", descKey: "descVisaChange60" },
  "Visa Change": { nameKey: "visaChange60Days", descKey: "descVisaChange60" }
};

// Beautiful "Last payment" pill — mirrors the "Added today" treatment from
// the Accounting / Services tabs. Reads paymentHistory[last].
const renderLastPaymentPill = (paymentHistory, testidSuffix = '') => {
  if (!paymentHistory || paymentHistory.length === 0) return null;
  const last = paymentHistory[paymentHistory.length - 1];
  if (!last || !last.date) return null;
  const d = new Date(last.date);
  if (isNaN(d)) return null;
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((today - d) / dayMs);
  const isYesterday = diffDays === 1 && !isToday;
  const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const whenText = isToday ? `today · ${timeStr}` : isYesterday ? 'yesterday' : dateStr;
  const amount = Number(last.amount) || 0;
  const isDeduction = amount < 0;
  const sign = isDeduction ? '−' : '+';
  const amountColor = isDeduction ? '#FF6347' : '#10B981';
  return (
    <span
      data-testid={`booking-last-payment${testidSuffix ? '-' + testidSuffix : ''}`}
      title={`${dateStr} at ${timeStr}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '4px 10px', borderRadius: '999px',
        fontSize: '11px', fontWeight: 600, letterSpacing: '0.2px',
        background: isToday
          ? 'linear-gradient(135deg, rgba(94,252,232,0.20), rgba(94,252,232,0.06))'
          : 'rgba(96,165,250,0.10)',
        color: isToday ? '#5EFCE8' : '#93C5FD',
        border: `1px solid ${isToday ? 'rgba(94,252,232,0.4)' : 'rgba(96,165,250,0.25)'}`,
        boxShadow: isToday ? '0 0 12px rgba(94,252,232,0.18)' : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Calendar size={11} style={{ opacity: 0.9 }} />
      <span style={{ color: amountColor, fontWeight: 700 }}>
        {sign} {Math.abs(amount).toLocaleString()} AED
      </span>
      <span style={{ opacity: 0.85 }}>· Added {whenText}</span>
    </span>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('bookings');
  
  // Upload modal state
  const [uploadModal, setUploadModal] = useState(null); // booking object or null
  const [freelanceStepModal, setFreelanceStepModal] = useState(null); // { booking, nextStep }
  const [freelanceStepFile, setFreelanceStepFile] = useState(null);
  const [freelanceStepUploading, setFreelanceStepUploading] = useState(false);
  const freelanceFileRef = React.useRef(null);
  const [balanceModal, setBalanceModal] = useState(null); // { bookingId, totalPrice, paidAmount }
  const [balanceInput, setBalanceInput] = useState('');
  const [deductModal, setDeductModal] = useState(null); // { bookingId, currentPrice, bookingNumber }
  const [deductInput, setDeductInput] = useState('');
  const [warningModal, setWarningModal] = useState(null);
  const [warningReason, setWarningReason] = useState('');
  const [warningSending, setWarningSending] = useState(false);
  // Invoice generation modal (for booking-attached invoices)
  const [bookingInvoiceModal, setBookingInvoiceModal] = useState(null); // { booking, amount, lineItems: [{description, qty, unit_price}], notes }
  const [invoiceGenerating, setInvoiceGenerating] = useState(false);
  const [editEmailModal, setEditEmailModal] = useState(null); // { bookingId, currentEmail }
  const [editEmailInput, setEditEmailInput] = useState('');
  const [editDetailsModal, setEditDetailsModal] = useState(null);
  const [csEditModal, setCsEditModal] = useState(null);
  const [csEditSaving, setCsEditSaving] = useState(false);
  const [editDetailsSaving, setEditDetailsSaving] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [localFiles, setLocalFiles] = useState(null);
  const [localFilesLoading, setLocalFilesLoading] = useState(false);
  const [selectedLocalFiles, setSelectedLocalFiles] = useState(new Set());
  const [deletingFile, setDeletingFile] = useState(null);
  const [seoPages, setSeoPages] = useState([]);
  const [seoSettings, setSeoSettings] = useState({});
  const [seoEditing, setSeoEditing] = useState(null);
  const [seoForm, setSeoForm] = useState({ title: '', description: '', keywords: '', ogImage: '', title_ru: '', description_ru: '', keywords_ru: '', title_uz: '', description_uz: '', keywords_uz: '' });
  const [seoSaving, setSeoSaving] = useState(false);
  const [seoUploading, setSeoUploading] = useState(false);
  const [seoTranslating, setSeoTranslating] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [locationLink, setLocationLink] = useState('');
  const fileInputRef = useRef(null);
  const serviceImageInputRef = useRef(null);
  const reviewImageInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingView, setBookingView] = useState('visa'); // 'visa', 'tour', 'freelance', 'family'
  const [showAdminBookingForm, setShowAdminBookingForm] = useState(false);
  const [adminBookingForm, setAdminBookingForm] = useState({
    name: '', mobile: '', email: '', nationality: '', companyName: '', date: '', servicePrice: '', service: '', agentName: '', agentPhone: '', passportNumber: ''
  });
  const [adminBookingSubmitting, setAdminBookingSubmitting] = useState(false);
  const [adminBookingPhoto, setAdminBookingPhoto] = useState(null);
  const adminBookingPhotoRef = useRef(null);
  
  // Document required modal
  const [docRequiredModal, setDocRequiredModal] = useState(null);
  const [requiredDocType, setRequiredDocType] = useState('passport');
  const [docReason, setDocReason] = useState('');
  
  // Service modal state
  const [serviceModal, setServiceModal] = useState(null); // 'add' | service object for edit | null
  const [serviceForm, setServiceForm] = useState({
    name: '',
    name_ru: '',
    name_uz: '',
    duration: '',
    duration_ru: '',
    duration_uz: '',
    price: '',
    description: '',
    description_ru: '',
    description_uz: '',
    icon: 'Plane',
    subtitle: '',
    subtitle_ru: '',
    subtitle_uz: '',
    category: ''
  });
  const [serviceImage, setServiceImage] = useState(null);
  const [serviceImagePreview, setServiceImagePreview] = useState(null);
  const [savingService, setSavingService] = useState(false);
  
  // Service Steps modal state
  const [stepsModal, setStepsModal] = useState(null); // service object | null
  const [serviceSteps, setServiceSteps] = useState([]);
  const [savingSteps, setSavingSteps] = useState(false);

  // Review modal state
  const [reviewModal, setReviewModal] = useState(null); // 'add' | review object for edit | null
  const [reviewForm, setReviewForm] = useState({
    author: '',
    rating: 5,
    text: '',
    instagram: '',
    tiktok: ''
  });
  const [reviewImage, setReviewImage] = useState(null);
  const [reviewImagePreview, setReviewImagePreview] = useState(null);
  const [reviewAvatar, setReviewAvatar] = useState(null);
  const [reviewAvatarPreview, setReviewAvatarPreview] = useState(null);
  const [reviewVideo, setReviewVideo] = useState(null);
  const [reviewVideoPreview, setReviewVideoPreview] = useState(null);
  const [savingReview, setSavingReview] = useState(false);

  // Text Sections state
  const [textSections, setTextSections] = useState([]);
  const [textSectionModal, setTextSectionModal] = useState(null); // 'add' | section object for edit | null
  const [textSectionForm, setTextSectionForm] = useState({
    text_en: '',
    text_ru: '',
    text_uz: '',
    isActive: true,
    placement: 'stories'
  });
  const [savingTextSection, setSavingTextSection] = useState(false);

  // Stories state
  const [stories, setStories] = useState([]);
  const [storyModal, setStoryModal] = useState(null); // 'add' | story object for edit | null
  const [storyForm, setStoryForm] = useState({
    title: '',
    active: true
  });
  const [storyMediaFiles, setStoryMediaFiles] = useState([]); // Multiple files
  const [storyMediaPreviews, setStoryMediaPreviews] = useState([]); // Multiple previews
  const [savingStory, setSavingStory] = useState(false);
  const [addingMedia, setAddingMedia] = useState(false);
  const storyMediaInputRef = useRef(null);
  const addMediaInputRef = useRef(null);

  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [invoiceModal, setInvoiceModal] = useState(null); // 'add' | null
  const [invoiceForm, setInvoiceForm] = useState({
    user_email: '',
    user_name: '',
    items: [{ description: '', quantity: 1, price: 0 }],
    notes: '',
    due_date: '',
    booking_id: ''
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const invoiceImageInputRef = useRef(null);

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState(null); // invoice object or null
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    notes: '',
    invoice_file: null
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Email state
  const [emailModal, setEmailModal] = useState(null); // 'compose' | null
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    subject: '',
    message: ''
  });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sentEmails, setSentEmails] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [selectedInboxEmail, setSelectedInboxEmail] = useState(null);
  const [inboxError, setInboxError] = useState('');
  const [emailSubTab, setEmailSubTab] = useState('inbox'); // 'inbox' | 'sent' | 'delivery-log'
  const [newEmailAlert, setNewEmailAlert] = useState(false);
  const [lastInboxCount, setLastInboxCount] = useState(0);
  const emailAttachmentRef = useRef(null);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [loadingDeliveryLogs, setLoadingDeliveryLogs] = useState(false);
  const [deliveryLogFilter, setDeliveryLogFilter] = useState('all');

  // Admin 2FA OTP state
  // Mobile: hamburger drawer for the admin tab navigation
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [otpStep, setOtpStep] = useState('password'); // 'password' | 'otp'
  const [otpRequestId, setOtpRequestId] = useState('');
  const [otpEmailHint, setOtpEmailHint] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);

  // Admin password — uses key from successful OTP verify (stored in
  // localStorage). Falls back to the build-time literal so the very first
  // login attempt works. After a successful OTP login, the dynamic key takes
  // over → no more brittle env/frontend mismatches.
  const ADMIN_KEY_FALLBACK = 'Registan@tourism123';
  const [adminKey, setAdminKey] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem('registanAdminKey')) || ADMIN_KEY_FALLBACK
  );
  // Keep ADMIN_PASSWORD identifier so existing axios calls keep working
  // without a sweeping rename.
  const ADMIN_PASSWORD = adminKey;

  // Global Visa applications state
  const [visaApplications, setVisaApplications] = useState([]);
  const [loadingVisaApps, setLoadingVisaApps] = useState(false);
  const [newVisaCount, setNewVisaCount] = useState(0);

  // Accounting state
  const [accountingEntries, setAccountingEntries] = useState([]);
  const [loadingAccounting, setLoadingAccounting] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ date: '', company: '', client: '', service: '', amount: '', type: 'expense' });
  const [servicePrices, setServicePrices] = useState({});
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [companySuggestions, setCompanySuggestions] = useState([]);

  // Client Services (Partial Payments) state
  const [clientServices, setClientServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [csForm, setCsForm] = useState({ client: '', client_email: '', service: '', total_amount: '', start_date: new Date().toISOString().split('T')[0], receiver: '', receiver_email: 'payment@registantours.com' });
  // Separate state for "Bookings → Services" sub-tab (source='booking') so
  // it never mixes with Accounting → Client Services.
  const [bookingServices, setBookingServices] = useState([]);
  const [loadingBookingServices, setLoadingBookingServices] = useState(false);
  const [bksForm, setBksForm] = useState({ client: '', client_email: '', service: '', total_amount: '', start_date: new Date().toISOString().split('T')[0] });
  const [csPayModal, setCsPayModal] = useState(null); // holds service id
  const [csPayMode, setCsPayMode] = useState('payment'); // 'payment' | 'deduction'
  const [csPayForm, setCsPayForm] = useState({ amount: '', date: '', invoice_file: null, reason: '' });
  // Add Extra Service modal (Bookings → Services)
  const [extraModal, setExtraModal] = useState(null); // service id
  const [extraForm, setExtraForm] = useState({ name: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [extraSubmitting, setExtraSubmitting] = useState(false);
  const [csPaySubmitting, setCsPaySubmitting] = useState(false);
  const [expandedService, setExpandedService] = useState(null);
  const [accountingView, setAccountingView] = useState('services'); // 'services' only

  // Tours state
  const [tours, setTours] = useState([]);
  const [tourModal, setTourModal] = useState(null); // 'add' | tour object for edit | null
  const [tourForm, setTourForm] = useState({ title: '', category: '', description: '', duration: '', price: '', highlights: '' });
  const [tourImage, setTourImage] = useState(null);
  const [tourImagePreview, setTourImagePreview] = useState(null);
  const [savingTour, setSavingTour] = useState(false);
  const tourImageInputRef = useRef(null);

  // Tour Bookings state
  const [tourBookings, setTourBookings] = useState([]);

  // Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamModal, setTeamModal] = useState(null); // 'add' | member object for edit | null
  const [teamForm, setTeamForm] = useState({ name: '', position_title: '', position_title_ru: '', position_title_uz: '', location: '', mobile: '', email: '', description: '', description_ru: '', description_uz: '', tiktok: '', instagram: '' });
  const [teamImage, setTeamImage] = useState(null);
  const [teamImagePreview, setTeamImagePreview] = useState(null);
  const [savingTeam, setSavingTeam] = useState(false);
  const teamImageInputRef = useRef(null);

  // Notification sound for new submissions
  const lastCountsRef = useRef({ bookings: 0, visaApps: 0, messages: 0 });

  const playBookingSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Bright two-tone chime for bookings/visa
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, ctx.currentTime);
      gain1.gain.setValueAtTime(0.3, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.3);
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1174, ctx.currentTime + 0.15);
      gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + 0.15);
      osc2.stop(ctx.currentTime + 0.5);
    } catch (e) {}
  };

  const playMessageSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Soft triple-pop for messages
      [0, 0.12, 0.24].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime([523, 659, 784][i], ctx.currentTime + delay);
        gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.15);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.15);
      });
    } catch (e) {}
  };

  // Poll for new submissions every 15 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const pollNewSubmissions = async () => {
      try {
        const [bookingsRes, visaRes, messagesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/bookings`, { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/admin/visa-applications`, { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/admin/messages`, { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }).catch(() => ({ data: [] }))
        ]);
        const newBookings = Array.isArray(bookingsRes.data) ? bookingsRes.data.length : 0;
        const newVisaApps = Array.isArray(visaRes.data) ? visaRes.data.length : 0;
        // Count total individual messages (initial + replies) from clients
        const allMsgs = Array.isArray(messagesRes.data) ? messagesRes.data : [];
        const totalMsgCount = allMsgs.reduce((sum, conv) => {
          const clientReplies = (conv.replies || []).filter(r => r.from !== 'admin').length;
          return sum + 1 + clientReplies;
        }, 0);
        const prev = lastCountsRef.current;
        if (prev.bookings > 0 || prev.visaApps > 0 || prev.messages > 0) {
          const hasNewBooking = newBookings > prev.bookings || newVisaApps > prev.visaApps;
          const newMsgDiff = totalMsgCount - prev.messages;
          if (hasNewBooking) playBookingSound();
          // Play message sound once for each new message
          if (newMsgDiff > 0) {
            const count = Math.min(newMsgDiff, 5);
            for (let i = 0; i < count; i++) {
              setTimeout(() => playMessageSound(), (hasNewBooking ? 600 : 0) + i * 500);
            }
          }
          if (newVisaApps > prev.visaApps) {
            setNewVisaCount(c => c + (newVisaApps - prev.visaApps));
          }
        }
        lastCountsRef.current = { bookings: newBookings, visaApps: newVisaApps, messages: totalMsgCount };
      } catch (e) {}
    };
    pollNewSubmissions();
    const interval = setInterval(pollNewSubmissions, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    const storedKey = localStorage.getItem('registanAdminKey');
    // Require BOTH an active session AND a stored admin key from a recent OTP
    // login. Old sessions without the key are force-logged-out so the user
    // does a fresh OTP login (which picks up the current backend password).
    if (adminAuth === 'true' && storedKey) {
      setIsAuthenticated(true);
      loadBookings();
      loadReviews();
      loadStats();
      loadServices();
      loadTours();
      loadTextSections();
      loadStories();
      loadInvoices();
      loadMessages();
      loadStorageStats();
      loadEmailStats();
      loadClientServices();
      loadBookingServices();
    } else {
      localStorage.removeItem('adminAuth');
      setLoading(false);
    }
  }, []);

  // Step 1: User enters password → request OTP via email
  const handleLogin = async (e) => {
    e.preventDefault();
    if (otpLoading) return;
    setError('');
    setOtpLoading(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/admin/auth/request-otp`, { password });
      if (resp.data && resp.data.success) {
        setOtpRequestId(resp.data.request_id);
        setOtpEmailHint(resp.data.email_hint || '');
        setOtpInput('');
        setOtpStep('otp');
        setOtpResendCooldown(30);
      } else {
        setError('Unable to send code. Try again.');
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        setError(detail || 'Invalid admin password.');
      } else if (detail) {
        setError(detail);
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Step 2: User enters OTP → verify
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpLoading) return;
    setError('');
    const code = otpInput.trim();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setOtpLoading(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/admin/auth/verify-otp`, {
        request_id: otpRequestId,
        otp: code,
      });
      if (resp.data && resp.data.success) {
        // Persist the backend-issued admin key so subsequent admin API calls
        // always use the correct password (resilient to frontend/env drift).
        if (resp.data.admin_key) {
          localStorage.setItem('registanAdminKey', resp.data.admin_key);
          setAdminKey(resp.data.admin_key);
        }
        localStorage.setItem('adminAuth', 'true');
        setIsAuthenticated(true);
        setOtpStep('password');
        setOtpRequestId('');
        setOtpInput('');
        setError('');
        loadBookings();
        loadReviews();
        loadStats();
        loadServices();
        loadTours();
        loadTextSections();
        loadStories();
        loadInvoices();
        loadMessages();
        loadStorageStats();
        loadEmailStats();
        loadClientServices();
        loadBookingServices();
      } else {
        setError('Verification failed. Try again.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Verification failed.';
      setError(detail);
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (otpResendCooldown > 0 || otpLoading) return;
    setError('');
    setOtpLoading(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/admin/auth/request-otp`, { password });
      if (resp.data && resp.data.success) {
        setOtpRequestId(resp.data.request_id);
        setOtpInput('');
        setOtpResendCooldown(30);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Could not resend code. Try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend cooldown timer
  useEffect(() => {
    if (otpResendCooldown <= 0) return;
    const t = setTimeout(() => setOtpResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendCooldown]);

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('registanAdminKey');
    setAdminKey(ADMIN_KEY_FALLBACK);
    setIsAuthenticated(false);
    setOtpStep('password');
    setPassword('');
    setOtpRequestId('');
    setOtpInput('');
    navigate('/');
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/bookings`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadStorageStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/storage-stats`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setStorageStats(response.data);
    } catch (error) {
      console.error('Error loading storage stats:', error);
    }
  };

  const loadEmailStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-stats`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setEmailStats(response.data);
    } catch (error) {
      console.error('Error loading email stats:', error);
    }
  };

  const loadLocalFiles = async () => {
    setLocalFilesLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/local-files`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setLocalFiles(response.data);
      setSelectedLocalFiles(new Set());
    } catch (error) {
      console.error('Error loading local files:', error);
    } finally {
      setLocalFilesLoading(false);
    }
  };

  const deleteLocalFile = async (filePath) => {
    if (!window.confirm(`Delete "${filePath}"? This cannot be undone.`)) return;
    setDeletingFile(filePath);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/local-files`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD },
        data: { path: filePath }
      });
      loadLocalFiles();
    } catch (error) {
      alert('Failed to delete file: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingFile(null);
    }
  };

  const deleteSelectedLocalFiles = async () => {
    if (selectedLocalFiles.size === 0) return;
    if (!window.confirm(`Delete ${selectedLocalFiles.size} selected files? This cannot be undone.`)) return;
    setDeletingFile('bulk');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/local-files/bulk`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD },
        data: { paths: Array.from(selectedLocalFiles) }
      });
      loadLocalFiles();
    } catch (error) {
      alert('Failed to delete files: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingFile(null);
    }
  };

  const toggleFileSelection = (path) => {
    setSelectedLocalFiles(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!localFiles?.files) return;
    if (selectedLocalFiles.size === localFiles.files.length) {
      setSelectedLocalFiles(new Set());
    } else {
      setSelectedLocalFiles(new Set(localFiles.files.map(f => f.path)));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  };

  const getFileIcon = (type) => {
    const icons = { jpg: '🖼', jpeg: '🖼', png: '🖼', webp: '🖼', mp4: '🎬', pdf: '📄' };
    return icons[type] || '📁';
  };

  const loadSeoSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/seo`);
      setSeoPages(res.data.pages || []);
      setSeoSettings(res.data.settings || {});
    } catch (e) { console.error('Error loading SEO:', e); }
  };

  const startEditSeo = (slug) => {
    const current = seoSettings[slug] || {};
    setSeoForm({
      title: current.title || '',
      description: current.description || '',
      keywords: current.keywords || '',
      ogImage: current.ogImage || '',
      mainImage: current.mainImage || '',
      mainImageAlt: current.mainImageAlt || '',
      mainImageTitle: current.mainImageTitle || '',
      title_ru: current.title_ru || '',
      description_ru: current.description_ru || '',
      keywords_ru: current.keywords_ru || '',
      title_uz: current.title_uz || '',
      description_uz: current.description_uz || '',
      keywords_uz: current.keywords_uz || ''
    });
    setSeoEditing(slug);
  };

  const saveSeo = async () => {
    if (!seoEditing) return;
    setSeoSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/seo/${seoEditing}`, seoForm, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setSeoSettings(prev => ({ ...prev, [seoEditing]: { ...seoForm, slug: seoEditing } }));
      setSeoEditing(null);
    } catch (e) {
      alert('Failed to save SEO: ' + (e.response?.data?.detail || e.message));
    } finally { setSeoSaving(false); }
  };

  const autoTranslateSeo = async () => {
    if (!seoForm.title && !seoForm.description && !seoForm.keywords) {
      alert('Please fill in at least the English Title, Description, or Keywords first.');
      return;
    }
    setSeoTranslating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/seo/auto-translate`, {
        title: seoForm.title,
        description: seoForm.description,
        keywords: seoForm.keywords,
      }, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      const { ru, uz } = res.data || {};
      setSeoForm(prev => ({
        ...prev,
        title_ru: ru?.title || prev.title_ru,
        description_ru: ru?.description || prev.description_ru,
        keywords_ru: ru?.keywords || prev.keywords_ru,
        title_uz: uz?.title || prev.title_uz,
        description_uz: uz?.description || prev.description_uz,
        keywords_uz: uz?.keywords || prev.keywords_uz,
      }));
    } catch (e) {
      alert('Auto-translate failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSeoTranslating(false); }
  };

  const uploadSeoImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSeoUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/seo/upload-image`, fd, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.url) {
        setSeoForm(prev => ({ ...prev, ogImage: res.data.url }));
      }
    } catch (e) {
      alert('Failed to upload image: ' + (e.response?.data?.detail || e.message));
    } finally { setSeoUploading(false); }
  };

  const uploadSeoMainImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSeoUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/seo/upload-image`, fd, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.url) {
        setSeoForm(prev => ({ ...prev, mainImage: res.data.url }));
      }
    } catch (e) {
      alert('Failed to upload image: ' + (e.response?.data?.detail || e.message));
    } finally { setSeoUploading(false); }
  };

  const handleAdminBookingSubmit = async () => {
    if (!adminBookingForm.name || !adminBookingForm.agentName || !adminBookingForm.agentPhone || !adminBookingForm.passportNumber) return;
    setAdminBookingSubmitting(true);
    try {
      const serviceName = bookingView === 'freelance' ? 'Freelance Visa' : 'Family Visa';
      const payload = {
        ...adminBookingForm,
        service: serviceName,
        servicePrice: adminBookingForm.servicePrice || '0',
        date: adminBookingForm.date || new Date().toISOString().split('T')[0]
      };

      // If freelance and photo, upload via with-documents endpoint to save photo to Supabase
      let newBookingId = null;
      if (bookingView === 'freelance' && adminBookingPhoto) {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => { if (v) formData.append(k, v); });
        formData.append('photo', adminBookingPhoto);
        const res = await axios.post(`${BACKEND_URL}/api/bookings/with-documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newBookingId = res.data?.booking?.id;
        // Save client photo URL in booking
        const photoUrl = res.data?.booking?.photoFile;
        if (newBookingId && photoUrl) {
          await axios.patch(
            `${BACKEND_URL}/api/admin/bookings/${newBookingId}/status`,
            { status: 'pending' },
            { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
          ).catch(() => {});
          // Save clientPhoto field
          await axios.put(
            `${BACKEND_URL}/api/admin/bookings/${newBookingId}`,
            { clientPhoto: photoUrl },
            { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
          ).catch(() => {});
        }
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/bookings`, payload);
        newBookingId = res.data?.booking?.id;
      }

      // Auto-generate application PDF for freelance with photo
      if (bookingView === 'freelance' && adminBookingPhoto && newBookingId) {
        try {
          const formData = new FormData();
          formData.append('client_photo', adminBookingPhoto);
          const pdfRes = await axios.post(
            `${BACKEND_URL}/api/admin/bookings/${newBookingId}/generate-application`,
            formData,
            { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }, responseType: 'blob' }
          );
          const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          const safeName = (adminBookingForm.name || 'Client').replace(/\s+/g, '_').substring(0, 20);
          link.setAttribute('download', `Application_${safeName}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (pdfErr) {
          console.error('PDF generation failed:', pdfErr);
          alert('Booking created, but PDF generation failed: ' + (pdfErr.response?.data?.detail || pdfErr.message));
        }
      }

      setShowAdminBookingForm(false);
      setAdminBookingForm({ name: '', mobile: '', email: '', nationality: '', date: '', servicePrice: '', service: '', agentName: '', agentPhone: '', passportNumber: '' });
      setAdminBookingPhoto(null);
      loadBookings();
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Failed to create booking: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAdminBookingSubmitting(false);
    }
  };

  const loadVisaApplications = async () => {
    try {
      setLoadingVisaApps(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/visa-applications`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setVisaApplications(response.data);
    } catch (error) {
      console.error('Error loading visa applications:', error);
    } finally {
      setLoadingVisaApps(false);
    }
  };

  const updateVisaAppStatus = async (id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/visa-applications/${id}`, { status }, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setVisaApplications(prev => prev.map(app => app.id === id ? { ...app, status } : app));
    } catch (error) {
      console.error('Error updating visa application:', error);
    }
  };

  const deleteVisaApp = async (id) => {
    if (!window.confirm('Delete this visa application?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/visa-applications/${id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setVisaApplications(prev => prev.filter(app => app.id !== id));
    } catch (error) {
      console.error('Error deleting visa application:', error);
    }
  };

  const loadAccounting = async () => {
    try {
      setLoadingAccounting(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/accounting`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setAccountingEntries(response.data);
      // Build service price memory: client+service -> last price
      const prices = {};
      const clients = new Set();
      const companies = new Set();
      response.data.forEach(e => {
        if (e.client) clients.add(e.client);
        if (e.company) companies.add(e.company);
        if (e.client && e.service) {
          prices[`${e.client}|||${e.service}`] = e.amount;
        }
      });
      setServicePrices(prices);
      setClientSuggestions([...clients]);
      setCompanySuggestions([...companies]);
    } catch (error) {
      console.error('Error loading accounting:', error);
    } finally {
      setLoadingAccounting(false);
    }
  };

  const addAccountingEntry = async () => {
    if (!expenseForm.date || !expenseForm.client || !expenseForm.amount) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/accounting`, expenseForm, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' }
      });
      setExpenseForm({ date: '', company: '', client: '', service: '', amount: '', type: 'expense' });
      setShowAddExpense(false);
      loadAccounting();
    } catch (error) {
      console.error('Error adding entry:', error);
    }
  };

  const handleServiceChange = (service) => {
    const key = `${expenseForm.client}|||${service}`;
    const savedPrice = servicePrices[key];
    setExpenseForm(prev => ({ ...prev, service, ...(savedPrice ? { amount: String(savedPrice) } : {}) }));
  };

  const handleClientChange = (client) => {
    setExpenseForm(prev => {
      const key = `${client}|||${prev.service}`;
      const savedPrice = servicePrices[key];
      return { ...prev, client, ...(savedPrice ? { amount: String(savedPrice) } : {}) };
    });
  };

  // Client Services API
  const loadClientServices = async () => {
    try {
      setLoadingServices(true);
      // Accounting tab: only fetch accounting-source records (legacy rows
      // without a source field are treated as accounting on the backend).
      const response = await axios.get(`${BACKEND_URL}/api/admin/client-services?source=accounting`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setClientServices(response.data);
    } catch (error) {
      console.error('Error loading client services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  // Bookings → Services API (source='booking') — kept separate from Accounting
  const loadBookingServices = async () => {
    try {
      setLoadingBookingServices(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/client-services?source=booking`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setBookingServices(response.data);
    } catch (error) {
      console.error('Error loading booking services:', error);
    } finally {
      setLoadingBookingServices(false);
    }
  };

  const createBookingService = async () => {
    if (!bksForm.client || !bksForm.service || !bksForm.total_amount) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/client-services`, { ...bksForm, source: 'booking' }, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' }
      });
      setBksForm({ client: '', client_email: '', service: '', total_amount: '', start_date: new Date().toISOString().split('T')[0] });
      loadBookingServices();
    } catch (error) {
      console.error('Error creating booking service:', error);
    }
  };

  const deleteBookingService = async (id) => {
    if (!window.confirm('Delete this service record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/client-services/${id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setBookingServices(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting booking service:', error);
    }
  };

  // Download branded PDF receipt for a client-service record (works from
  // either Bookings → Services or Accounting).
  const downloadServiceReceipt = async (id, clientName) => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/admin/client-services/${id}/receipt`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${(clientName || 'client').replace(/\s+/g, '_')}_${id.slice(-6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to download receipt';
      alert(msg);
    }
  };

  // Add an additional service line item (raises total + remaining)
  const addExtraServiceLine = async () => {
    if (!extraModal || extraSubmitting) return;
    const name = (extraForm.name || '').trim();
    const amt = parseFloat(extraForm.amount) || 0;
    if (!name) { alert('Service name is required'); return; }
    if (amt <= 0) { alert('Amount must be positive'); return; }
    setExtraSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/client-services/${extraModal}/extra`,
        { name, amount: amt, date: extraForm.date },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' } }
      );
      setExtraForm({ name: '', amount: '', date: new Date().toISOString().split('T')[0] });
      setExtraModal(null);
      // Refresh both lists since extras are shared via the underlying collection
      loadBookingServices();
      loadClientServices();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to add extra service';
      alert(msg);
    } finally {
      setExtraSubmitting(false);
    }
  };

  const createClientService = async () => {
    if (!csForm.client || !csForm.service || !csForm.total_amount) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/client-services`, csForm, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' }
      });
      setCsForm({ client: '', client_email: '', service: '', total_amount: '', start_date: new Date().toISOString().split('T')[0], receiver: '', receiver_email: 'payment@registantours.com' });
      setShowAddService(false);
      loadClientServices();
    } catch (error) {
      console.error('Error creating service:', error);
    }
  };

  const addServicePayment = async () => {
    if (!csPayModal || !csPayForm.amount || !csPayForm.date || csPaySubmitting) return;
    setCsPaySubmitting(true);
    try {
      const formData = new FormData();
      // Deduction = send negative amount. Backend interprets the sign as
      // payment vs correction and updates paid/remaining/status accordingly.
      const rawAmt = parseFloat(csPayForm.amount) || 0;
      const signedAmt = csPayMode === 'deduction' ? -Math.abs(rawAmt) : Math.abs(rawAmt);
      formData.append('amount', String(signedAmt));
      formData.append('date', csPayForm.date);
      if (csPayMode === 'deduction') {
        formData.append('reason', csPayForm.reason || '');
      }
      if (csPayForm.invoice_file) {
        formData.append('invoice_file', csPayForm.invoice_file);
      }
      await axios.post(`${BACKEND_URL}/api/admin/client-services/${csPayModal}/pay`, formData, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setCsPayForm({ amount: '', date: '', invoice_file: null, reason: '' });
      setCsPayMode('payment');
      setCsPayModal(null);
      // Refresh both lists (modal can be opened from either tab)
      loadClientServices();
      loadBookingServices();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Payment failed';
      alert(msg);
    } finally {
      setCsPaySubmitting(false);
    }
  };

  const openCsEdit = (svc) => setCsEditModal({
    id: svc.id, client: svc.client || '', client_email: svc.client_email || '', service: svc.service || '',
    start_date: svc.start_date || '', receiver: svc.receiver || '', receiver_email: svc.receiver_email || '',
    total_amount: String(svc.total_amount ?? ''), paid: svc.paid_amount || 0
  });

  const saveCsEdit = async () => {
    if (!csEditModal || csEditSaving) return;
    const { id, paid, ...fields } = csEditModal;
    setCsEditSaving(true);
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/admin/client-services/${id}`,
        { ...fields, total_amount: parseFloat(fields.total_amount) },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' } });
      setCsEditModal(null);
      loadClientServices();
      const sent = res.data.notified || [];
      alert(res.data.status === 'unchanged' ? 'No changes to save' : `Order updated${sent.length ? ` — email sent to ${sent.join(', ')}` : ''}`);
    } catch (error) {
      alert('Failed to update order: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCsEditSaving(false);
    }
  };

  const deleteClientService = async (id) => {
    if (!window.confirm('Delete this service record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/client-services/${id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setClientServices(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const deleteAccountingEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/accounting/${id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setAccountingEntries(prev => prev.filter(e => e.id !== id));
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };



  const loadReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/reviews`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setReviews(response.data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/services`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  // Tours functions
  const loadTours = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/tours`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setTours(response.data);
    } catch (error) {
      console.error('Error loading tours:', error);
    }
  };

  const openAddTourModal = () => {
    setTourForm({ title: '', category: '', description: '', duration: '', price: '', highlights: '' });
    setTourImage(null);
    setTourImagePreview(null);
    setTourModal('add');
  };

  const openEditTourModal = (tour) => {
    setTourForm({
      title: tour.title || '',
      category: tour.category || '',
      description: tour.description || '',
      duration: tour.duration || '',
      price: tour.price || '',
      highlights: (tour.highlights || []).join(', ')
    });
    setTourImage(null);
    setTourImagePreview(tour.image || null);
    setTourModal(tour);
  };

  const handleTourImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTourImage(file);
      setTourImagePreview(URL.createObjectURL(file));
    }
  };

  const saveTour = async () => {
    if (!tourForm.title) return;
    setSavingTour(true);
    try {
      const formData = new FormData();
      formData.append('title', tourForm.title);
      formData.append('category', tourForm.category);
      formData.append('description', tourForm.description);
      formData.append('duration', tourForm.duration);
      formData.append('price', tourForm.price || 0);
      formData.append('highlights', tourForm.highlights);
      if (tourImage) formData.append('image', tourImage);

      if (tourModal === 'add') {
        await axios.post(`${BACKEND_URL}/api/admin/tours`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/tours/${tourModal.id}`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
        });
      }
      setTourModal(null);
      loadTours();
    } catch (error) {
      console.error('Error saving tour:', error);
      alert('Failed to save tour');
    } finally {
      setSavingTour(false);
    }
  };

  const deleteTour = async (tour) => {
    if (!window.confirm(`Delete tour "${tour.title}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tours/${tour.id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      loadTours();
    } catch (error) {
      console.error('Error deleting tour:', error);
      alert('Failed to delete tour');
    }
  };

  // Tour Bookings functions
  const loadTourBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/tour-bookings`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setTourBookings(response.data);
    } catch (error) {
      console.error('Error loading tour bookings:', error);
    }
  };

  const updateTourBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tour-bookings/${bookingId}/status`, { status }, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      loadTourBookings();
    } catch (error) {
      console.error('Error updating tour booking status:', error);
    }
  };

  const deleteTourBooking = async (booking) => {
    if (!window.confirm(`Delete booking from "${booking.guest_name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tour-bookings/${booking.id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      loadTourBookings();
    } catch (error) {
      console.error('Error deleting tour booking:', error);
    }
  };

  // Team functions
  const loadTeam = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/team`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error loading team:', error);
    }
  };

  const openAddTeamModal = () => {
    setTeamForm({ name: '', position_title: '', position_title_ru: '', position_title_uz: '', location: '', mobile: '', email: '', description: '', description_ru: '', description_uz: '', tiktok: '', instagram: '' });
    setTeamImage(null);
    setTeamImagePreview(null);
    setTeamModal('add');
  };

  const openEditTeamModal = (member) => {
    setTeamForm({
      name: member.name || '',
      position_title: member.position_title || '',
      position_title_ru: member.position_title_ru || '',
      position_title_uz: member.position_title_uz || '',
      location: member.location || '',
      mobile: member.mobile || '',
      email: member.email || '',
      description: member.description || '',
      description_ru: member.description_ru || '',
      description_uz: member.description_uz || '',
      tiktok: member.tiktok || '',
      instagram: member.instagram || ''
    });
    setTeamImage(null);
    setTeamImagePreview(member.image || null);
    setTeamModal(member);
  };

  const handleTeamImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTeamImage(file);
      setTeamImagePreview(URL.createObjectURL(file));
    }
  };

  const saveTeamMember = async () => {
    if (!teamForm.name) return;
    setSavingTeam(true);
    try {
      const formData = new FormData();
      formData.append('name', teamForm.name);
      formData.append('position_title', teamForm.position_title);
      formData.append('position_title_ru', teamForm.position_title_ru || '');
      formData.append('position_title_uz', teamForm.position_title_uz || '');
      formData.append('location', teamForm.location);
      formData.append('mobile', teamForm.mobile);
      formData.append('email', teamForm.email);
      formData.append('description', teamForm.description);
      formData.append('description_ru', teamForm.description_ru || '');
      formData.append('description_uz', teamForm.description_uz || '');
      formData.append('tiktok', teamForm.tiktok);
      formData.append('instagram', teamForm.instagram);
      if (teamImage) formData.append('image', teamImage);

      if (teamModal === 'add') {
        await axios.post(`${BACKEND_URL}/api/admin/team`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/team/${teamModal.id}`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
        });
      }
      setTeamModal(null);
      loadTeam();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member');
    } finally {
      setSavingTeam(false);
    }
  };

  const deleteTeamMember = async (member) => {
    if (!window.confirm(`Delete team member "${member.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/team/${member.id}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      loadTeam();
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Failed to delete team member');
    }
  };

  const moveTeamMember = async (index, direction) => {
    const newMembers = [...teamMembers];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newMembers[index], newMembers[newIndex]] = [newMembers[newIndex], newMembers[index]];
    setTeamMembers(newMembers);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/team/reorder`, {
        member_ids: newMembers.map(m => m.id)
      }, { headers: { 'X-Admin-Key': ADMIN_PASSWORD } });
    } catch (error) {
      console.error('Error reordering team:', error);
      loadTeam();
      alert('Failed to reorder team');
    }
  };

  const loadTextSections = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/text-sections`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setTextSections(response.data);
    } catch (error) {
      console.error('Error loading text sections:', error);
    }
  };

  // Stories functions
  const loadStories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stories`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setStories(response.data);
    } catch (error) {
      console.error('Error loading stories:', error);
    }
  };

  const handleStoryMediaSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Check max limit (10 files)
      if (files.length > 10) {
        alert('Maximum 10 files allowed per story');
        return;
      }
      setStoryMediaFiles(files);
      const previews = files.map(file => ({
        url: URL.createObjectURL(file),
        type: file.type.startsWith('video/') ? 'video' : 'image',
        name: file.name
      }));
      setStoryMediaPreviews(previews);
    }
  };

  const handleAddMoreMedia = async (e, storyId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setAddingMedia(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('media', file));
      
      await axios.post(`${BACKEND_URL}/api/admin/stories/${storyId}/media`, formData, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      loadStories();
      alert(`Added ${files.length} media item(s)`);
    } catch (error) {
      console.error('Error adding media:', error);
      alert(error.response?.data?.detail || 'Failed to add media');
    } finally {
      setAddingMedia(false);
    }
  };

  const handleDeleteMediaItem = async (storyId, mediaIndex) => {
    if (!confirm('Delete this media item?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/stories/${storyId}/media/${mediaIndex}`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      loadStories();
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media item');
    }
  };

  const openStoryModal = (story = 'add') => {
    if (story === 'add') {
      setStoryForm({ title: '', active: true });
      setStoryMediaFiles([]);
      setStoryMediaPreviews([]);
    } else {
      setStoryForm({
        title: story.title || '',
        active: story.active !== false
      });
      // Show existing media as previews
      const previews = (story.media || []).map(m => ({
        url: getServiceImageUrl(m.url),
        type: m.type,
        existing: true
      }));
      setStoryMediaPreviews(previews);
      setStoryMediaFiles([]);
    }
    setStoryModal(story);
  };

  const handleSaveStory = async () => {
    setSavingStory(true);
    try {
      if (storyModal === 'add') {
        if (storyMediaFiles.length === 0) {
          alert('Please select at least one image or video');
          setSavingStory(false);
          return;
        }
        if (!storyForm.title.trim()) {
          alert('Please enter a story title');
          setSavingStory(false);
          return;
        }
        
        const formData = new FormData();
        formData.append('title', storyForm.title);
        storyMediaFiles.forEach(file => formData.append('media', file));
        
        await axios.post(`${BACKEND_URL}/api/admin/stories`, formData, {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Update title/active only
        const formData = new FormData();
        formData.append('title', storyForm.title);
        formData.append('active', storyForm.active);
        
        await axios.put(`${BACKEND_URL}/api/admin/stories/${storyModal.id}`, formData, {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      setStoryModal(null);
      loadStories();
    } catch (error) {
      console.error('Error saving story:', error);
      alert(error.response?.data?.detail || 'Failed to save story');
    } finally {
      setSavingStory(false);
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!confirm('Are you sure you want to delete this entire story?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/stories/${storyId}`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      loadStories();
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Failed to delete story');
    }
  };

  // Invoice functions
  const loadInvoices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/invoices`, {
        headers: {
          'X-Admin-Password': ADMIN_PASSWORD
        }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  // Email functions
  const loadSentEmails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/sent-emails`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setSentEmails(response.data);
    } catch (error) {
      console.error('Error loading sent emails:', error);
    }
  };

  const loadInboxEmails = async () => {
    setLoadingInbox(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/inbox`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      if (response.data.success) {
        setInboxEmails(response.data.emails);
        setInboxError('');
      }
    } catch (error) {
      console.error('Error loading inbox:', error);
      setInboxError(error.response?.data?.detail || 'Could not connect to the mailbox');
    } finally {
      setLoadingInbox(false);
    }
  };

  const loadDeliveryLogs = async (typeFilter) => {
    setLoadingDeliveryLogs(true);
    try {
      const filterParam = typeFilter || deliveryLogFilter;
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-delivery-logs`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD },
        params: { email_type: filterParam, limit: 200 }
      });
      if (response.data.success) {
        setDeliveryLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Error loading delivery logs:', error);
    } finally {
      setLoadingDeliveryLogs(false);
    }
  };

  const markEmailAsRead = async (emailId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/inbox/${emailId}/read`, {}, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      // Update local state
      setInboxEmails(prev => prev.map(email => 
        email.id === emailId ? { ...email, is_read: true } : email
      ));
      setLastInboxCount(c => Math.max(0, c - 1));
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };

  const openInboxEmail = (email) => {
    setSelectedInboxEmail(email);
    if (!email.is_read) {
      markEmailAsRead(email.id);
    }
  };

  const replyToEmail = (email) => {
    setEmailForm({
      to_email: email.from_email,
      subject: `Re: ${email.subject}`,
      message: `\n\n------- Original Message -------\nFrom: ${email.from_name} <${email.from_email}>\nDate: ${new Date(email.date).toLocaleString()}\nSubject: ${email.subject}\n\n${email.body}`
    });
    setSelectedInboxEmail(null);
    setEmailModal('compose');
  };

  const deleteInboxEmail = async (emailId) => {
    if (!window.confirm('Are you sure you want to delete this email?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/inbox/${emailId}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setInboxEmails(prev => prev.filter(e => e.id !== emailId));
      setSelectedInboxEmail(null);
    } catch (error) {
      console.error('Error deleting inbox email:', error);
      alert('Failed to delete email');
    }
  };

  const downloadInboxAttachment = async (emailId, idx, filename) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/inbox/${emailId}/attachments/${idx}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  const deleteSentEmail = async (emailId) => {
    if (!window.confirm('Are you sure you want to delete this sent email?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/sent-emails/${emailId}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setSentEmails(prev => prev.filter(e => e.id !== emailId));
    } catch (error) {
      console.error('Error deleting sent email:', error);
      alert('Failed to delete email');
    }
  };

  const checkForNewEmails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/inbox/count`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      if (response.data.success) {
        const newUnread = response.data.unread;
        if (newUnread > lastInboxCount && lastInboxCount > 0) {
          setNewEmailAlert(true);
          // Auto-hide alert after 10 seconds
          setTimeout(() => setNewEmailAlert(false), 10000);
        }
        setLastInboxCount(newUnread);
      }
    } catch (error) {
      console.error('Error checking for new emails:', error);
    }
  };

  // Poll for new emails every 30 seconds when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      checkForNewEmails();
      const interval = setInterval(checkForNewEmails, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, lastInboxCount]);

  const handleEmailAttachment = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      // Keep the raw File object so we can stream it via multipart upload
      // (avoids the 33% base64 inflation that causes "Network Error" when
      // proxies/ingresses block large JSON bodies).
      setEmailAttachments(prev => [...prev, {
        filename: file.name,
        content_type: file.type,
        size: file.size,
        file, // raw File reference
      }]);
    });
  };

  const removeEmailAttachment = (index) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const sendAdminEmail = async () => {
    if (!emailForm.to_email || !emailForm.subject || !emailForm.message) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSendingEmail(true);
    try {
      const totalSize = emailAttachments.reduce((acc, a) => acc + (a.size || 0), 0);
      const LARGE_THRESHOLD = 8 * 1024 * 1024; // >8MB → use chunked upload to bypass proxy limits
      
      const responsesArray = [];
      
      let queuedInBackground = false;
      
      if (emailAttachments.length === 0) {
        // No attachments → simple JSON path
        const r1 = await axios.post(`${BACKEND_URL}/api/admin/send-email`, {
          to_email: emailForm.to_email,
          subject: emailForm.subject,
          message: emailForm.message,
          attachments: null,
        }, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' },
        });
        responsesArray.push(r1.data);
      } else if (totalSize > LARGE_THRESHOLD) {
        // Large attachments → chunked upload to bypass proxy/ingress body-size
        // limits. Each chunk is ~4MB, well under any 10-25MB ingress cap.
        const CHUNK_SIZE = 4 * 1024 * 1024;
        const uploadIds = [];
        for (const att of emailAttachments) {
          if (!att.file) continue;
          const file = att.file;
          const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
          const uploadId = `e${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
          for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const blob = file.slice(start, end);
            const fd = new FormData();
            fd.append('upload_id', uploadId);
            fd.append('chunk_index', String(i));
            fd.append('total_chunks', String(totalChunks));
            fd.append('filename', att.filename);
            fd.append('content_type', att.content_type || 'application/octet-stream');
            fd.append('chunk', blob, `chunk-${i}`);
            await axios.post(`${BACKEND_URL}/api/admin/email-attachment/chunk`, fd, {
              headers: { 'X-Admin-Key': ADMIN_PASSWORD },
              timeout: 120000,
              maxContentLength: 10 * 1024 * 1024,
              maxBodyLength: 10 * 1024 * 1024,
            });
          }
          uploadIds.push(uploadId);
        }
        const sendFd = new FormData();
        sendFd.append('to_email', emailForm.to_email);
        sendFd.append('subject', emailForm.subject);
        sendFd.append('message', emailForm.message);
        sendFd.append('upload_ids', uploadIds.join(','));
        await axios.post(`${BACKEND_URL}/api/admin/send-email-chunked`, sendFd, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD },
          timeout: 30000, // returns in <1s now (BackgroundTasks)
        });
        queuedInBackground = true;
      } else {
        // Small attachments → single multipart upload (faster than chunked)
        const formData = new FormData();
        formData.append('to_email', emailForm.to_email);
        formData.append('subject', emailForm.subject);
        formData.append('message', emailForm.message);
        emailAttachments.forEach(att => {
          if (att.file) formData.append('files', att.file, att.filename);
        });
        const r2 = await axios.post(`${BACKEND_URL}/api/admin/send-email-multipart`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD },
          timeout: 120000,
          maxContentLength: 50 * 1024 * 1024,
          maxBodyLength: 50 * 1024 * 1024,
        });
        responsesArray.push(r2.data);
      }
      
      // Build a Telegram-mirror status note from the synchronous response
      let tgNote = '';
      const tgInfo = responsesArray[0]?.telegram_mirror;
      if (queuedInBackground) {
        // Chunked path is async — check link status separately
        try {
          const ls = await axios.get(`${BACKEND_URL}/api/admin/telegram-link-status`, {
            params: { email: emailForm.to_email },
            headers: { 'X-Admin-Key': ADMIN_PASSWORD },
          });
          tgNote = ls.data?.linked
            ? `\n\n📲 Telegram: client IS linked — documents will mirror to their Telegram in the background.`
            : `\n\n⚠️ Telegram NOT linked: ${ls.data?.hint || 'Client must press /start on @Visadubaibot first.'}`;
        } catch {
          tgNote = '';
        }
      } else if (tgInfo) {
        if (tgInfo.sent) {
          tgNote = `\n\n📲 Telegram: ✅ Mirrored to chat ${tgInfo.chat_id}.`;
        } else if (tgInfo.attempted) {
          tgNote = `\n\n📲 Telegram: ⚠️ Send failed — ${tgInfo.reason || 'unknown error'}.`;
        } else {
          tgNote = `\n\n📲 Telegram: ⚠️ Skipped — client not linked. Ask them to press /start on @Visadubaibot.`;
        }
      }
      
      alert((queuedInBackground
        ? 'Email queued for delivery — large attachments are sent in the background. Check the Email Log in a few minutes to confirm delivery.'
        : 'Email sent successfully!') + tgNote);
      setEmailModal(null);
      setEmailForm({ to_email: '', subject: '', message: '' });
      setEmailAttachments([]);
      loadSentEmails();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailComposer = (prefilledEmail = '') => {
    setEmailForm({
      to_email: prefilledEmail,
      subject: '',
      message: ''
    });
    setEmailAttachments([]);
    setEmailModal('compose');
  };

  // Messages functions
  const loadMessages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/messages`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // Auto-refresh messages when on messages tab
  useEffect(() => {
    let intervalId;
    if (isAuthenticated && activeTab === 'messages') {
      // Refresh every 5 seconds
      intervalId = setInterval(() => {
        loadMessages();
      }, 5000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAuthenticated, activeTab]);

  // Auto-refresh stats for real-time online users
  useEffect(() => {
    let statsIntervalId;
    if (isAuthenticated) {
      // Refresh stats every 30 seconds
      statsIntervalId = setInterval(() => {
        loadStats();
      }, 30000);
    }
    return () => {
      if (statsIntervalId) {
        clearInterval(statsIntervalId);
      }
    };
  }, [isAuthenticated]);

  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const handleReply = async (messageId) => {
    if (!replyText.trim()) return;
    
    setSendingReply(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/messages/${messageId}/reply`,
        { reply: replyText },
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'application/json'
          }
        }
      );
      setReplyText('');
      setReplyingTo(null);
      loadMessages();
      alert('Reply sent successfully!');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/messages/${messageId}/read`,
        {},
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD
          }
        }
      );
      if (response.data.success) {
        loadMessages();
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
      alert('Failed to mark as read');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/messages/${messageId}`,
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD
          }
        }
      );
      loadMessages();
      alert('Message deleted successfully!');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  const openAddInvoiceModal = () => {
    setInvoiceForm({
      user_email: '',
      user_name: '',
      items: [{ description: '', quantity: 1, price: 0 }],
      notes: '',
      due_date: '',
      booking_id: ''
    });
    setInvoiceModal('add');
  };

  const closeInvoiceModal = () => {
    setInvoiceModal(null);
    setInvoiceForm({
      user_email: '',
      user_name: '',
      items: [{ description: '', quantity: 1, price: 0 }],
      notes: '',
      due_date: '',
      booking_id: ''
    });
  };

  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: '', quantity: 1, price: 0 }]
    });
  };

  const removeInvoiceItem = (index) => {
    if (invoiceForm.items.length > 1) {
      const newItems = invoiceForm.items.filter((_, i) => i !== index);
      setInvoiceForm({ ...invoiceForm, items: newItems });
    }
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const calculateInvoiceTotal = () => {
    return invoiceForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const saveInvoice = async () => {
    if (!invoiceForm.user_email || !invoiceForm.user_name) {
      alert('Please enter user email and name');
      return;
    }
    if (invoiceForm.items.some(item => !item.description || item.price <= 0)) {
      alert('Please fill all item details with valid prices');
      return;
    }

    setSavingInvoice(true);
    try {
      const formData = new FormData();
      formData.append('user_email', invoiceForm.user_email);
      formData.append('user_name', invoiceForm.user_name);
      formData.append('items', JSON.stringify(invoiceForm.items));
      formData.append('notes', invoiceForm.notes || '');
      formData.append('due_date', invoiceForm.due_date || '');
      if (invoiceForm.booking_id) {
        formData.append('booking_id', invoiceForm.booking_id);
      }

      await axios.post(`${BACKEND_URL}/api/admin/invoices`, formData, {
        headers: {
          'X-Admin-Password': ADMIN_PASSWORD
        }
      });

      closeInvoiceModal();
      loadInvoices();
      loadBookings(); // Reload bookings to refresh state
      alert('Invoice created and sent successfully!');
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Failed to save invoice');
    } finally {
      setSavingInvoice(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      const formData = new FormData();
      formData.append('status', status);
      
      const response = await axios.put(`${BACKEND_URL}/api/admin/invoices/${invoiceId}`, formData, {
        headers: {
          'X-Admin-Password': ADMIN_PASSWORD
        }
      });
      
      if (response.data.success) {
        loadInvoices();
        // If marking as paid, reload bookings to update status
        if (status === 'paid') {
          loadBookings();
          alert(`Invoice marked as paid. Payment confirmation email sent to customer.`);
        }
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to update invoice: ${errorMessage}`);
    }
  };

  // Open payment modal for an invoice
  const openPaymentModal = (invoice) => {
    const remaining = (invoice.total || 0) - (invoice.paid_amount || 0);
    setPaymentForm({
      amount: remaining.toString(),
      payment_method: 'cash',
      notes: '',
      invoice_file: null
    });
    setPaymentModal(invoice);
  };

  // Submit payment for invoice
  const submitPayment = async () => {
    if (!paymentModal) return;
    
    const amount = parseFloat(paymentForm.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    setSavingPayment(true);
    try {
      const formData = new FormData();
      formData.append('amount', amount.toString());
      formData.append('payment_method', paymentForm.payment_method);
      if (paymentForm.notes) {
        formData.append('notes', paymentForm.notes);
      }
      if (paymentForm.invoice_file) {
        formData.append('invoice_file', paymentForm.invoice_file);
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/invoices/${paymentModal.id}/payment`,
        formData,
        {
          headers: {
            'X-Admin-Password': ADMIN_PASSWORD
          }
        }
      );

      if (response.data.success) {
        const remaining = response.data.remaining_balance;
        if (remaining <= 0) {
          alert(`Payment of AED ${amount.toLocaleString()} recorded. Invoice fully paid!`);
        } else {
          alert(`Payment of AED ${amount.toLocaleString()} recorded. Remaining balance: AED ${remaining.toLocaleString()}`);
        }
        loadInvoices();
        loadBookings();
        setPaymentModal(null);
        setPaymentForm({ amount: '', payment_method: 'cash', notes: '', invoice_file: null });
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to record payment: ${errorMessage}`);
    } finally {
      setSavingPayment(false);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/invoices/${invoiceId}`, {
        headers: {
          'X-Admin-Password': ADMIN_PASSWORD
        }
      });
      loadInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice');
    }
  };

  // Text Section modal handlers
  const openAddTextSectionModal = () => {
    setTextSectionForm({
      text_en: '',
      text_ru: '',
      text_uz: '',
      isActive: true,
      placement: 'stories'
    });
    setTextSectionModal('add');
  };

  const openEditTextSectionModal = (section) => {
    setTextSectionForm({
      text_en: section.text_en || section.text || '',
      text_ru: section.text_ru || '',
      text_uz: section.text_uz || '',
      isActive: section.isActive !== false,
      placement: section.placement || 'stories'
    });
    setTextSectionModal(section);
  };

  const closeTextSectionModal = () => {
    setTextSectionModal(null);
    setTextSectionForm({
      text_en: '',
      text_ru: '',
      text_uz: '',
      isActive: true,
      placement: 'stories'
    });
  };

  const saveTextSection = async () => {
    if (!textSectionForm.text_en.trim()) {
      alert('Please enter the English text');
      return;
    }

    setSavingTextSection(true);
    try {
      if (textSectionModal === 'add') {
        await axios.post(`${BACKEND_URL}/api/admin/text-sections`, {
          text_en: textSectionForm.text_en,
          text_ru: textSectionForm.text_ru,
          text_uz: textSectionForm.text_uz,
          isActive: textSectionForm.isActive,
          placement: textSectionForm.placement
        }, {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'application/json'
          }
        });
        alert('Text section created successfully!');
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/text-sections/${textSectionModal.id}`, {
          text_en: textSectionForm.text_en,
          text_ru: textSectionForm.text_ru,
          text_uz: textSectionForm.text_uz,
          isActive: textSectionForm.isActive,
          placement: textSectionForm.placement
        }, {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'application/json'
          }
        });
        alert('Text section updated successfully!');
      }

      closeTextSectionModal();
      loadTextSections();
    } catch (error) {
      console.error('Error saving text section:', error);
      alert('Failed to save text section');
    } finally {
      setSavingTextSection(false);
    }
  };

  const deleteTextSection = async (section) => {
    if (!window.confirm(`Are you sure you want to delete this text section?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/text-sections/${section.id}`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setTextSections(prev => prev.filter(s => s.id !== section.id));
      alert('Text section deleted successfully');
    } catch (error) {
      console.error('Error deleting text section:', error);
      alert('Failed to delete text section');
    }
  };

  const moveTextSection = async (index, direction) => {
    const newSections = [...textSections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setTextSections(newSections);
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/text-sections/reorder`, {
        section_ids: newSections.map(s => s.id)
      }, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
    } catch (error) {
      console.error('Error reordering text sections:', error);
      loadTextSections();
      alert('Failed to reorder text sections');
    }
  };

  const toggleTextSectionActive = async (section) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/text-sections/${section.id}`, {
        isActive: !section.isActive
      }, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD,
          'Content-Type': 'application/json'
        }
      });
      loadTextSections();
    } catch (error) {
      console.error('Error toggling text section:', error);
      alert('Failed to toggle text section');
    }
  };

  // Service modal handlers
  const openAddServiceModal = () => {
    setServiceForm({
      name: '',
      name_ru: '',
      name_uz: '',
      duration: '',
      duration_ru: '',
      duration_uz: '',
      price: '',
      description: '',
      description_ru: '',
      description_uz: '',
      icon: 'Plane',
      subtitle: '',
      subtitle_ru: '',
      subtitle_uz: '',
      category: ''
    });
    setServiceImage(null);
    setServiceImagePreview(null);
    setServiceModal('add');
  };

  const openEditServiceModal = (service) => {
    setServiceForm({
      name: service.name || '',
      name_ru: service.name_ru || '',
      name_uz: service.name_uz || '',
      duration: service.duration || '',
      duration_ru: service.duration_ru || '',
      duration_uz: service.duration_uz || '',
      price: service.price || '',
      description: service.description || '',
      description_ru: service.description_ru || '',
      description_uz: service.description_uz || '',
      icon: service.icon || 'Plane',
      subtitle: service.subtitle || '',
      subtitle_ru: service.subtitle_ru || '',
      subtitle_uz: service.subtitle_uz || '',
      category: service.category || ''
    });
    setServiceImage(null);
    setServiceImagePreview(service.image ? getServiceImageUrl(service.image) : null);
    setServiceModal(service);
  };

  const closeServiceModal = () => {
    setServiceModal(null);
    setServiceForm({
      name: '',
      name_ru: '',
      name_uz: '',
      duration: '',
      duration_ru: '',
      duration_uz: '',
      price: '',
      description: '',
      description_ru: '',
      description_uz: '',
      icon: 'Plane',
      subtitle: '',
      subtitle_ru: '',
      subtitle_uz: '',
      category: ''
    });
    setServiceImage(null);
    setServiceImagePreview(null);
    if (serviceImageInputRef.current) serviceImageInputRef.current.value = '';
  };

  const handleServiceImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setServiceImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setServiceImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleServiceFormChange = (field, value) => {
    setServiceForm(prev => ({ ...prev, [field]: value }));
  };

  const saveService = async () => {
    if (!serviceForm.name || !serviceForm.price || !serviceForm.duration || !serviceForm.description) {
      alert('Please fill in all required fields (Name, Price, Duration, Description)');
      return;
    }

    setSavingService(true);
    try {
      const formData = new FormData();
      formData.append('name', serviceForm.name);
      formData.append('name_ru', serviceForm.name_ru || '');
      formData.append('name_uz', serviceForm.name_uz || '');
      formData.append('duration', serviceForm.duration);
      formData.append('duration_ru', serviceForm.duration_ru || '');
      formData.append('duration_uz', serviceForm.duration_uz || '');
      formData.append('price', serviceForm.price);
      formData.append('description', serviceForm.description);
      formData.append('description_ru', serviceForm.description_ru || '');
      formData.append('description_uz', serviceForm.description_uz || '');
      formData.append('icon', serviceForm.icon);
      if (serviceForm.subtitle) formData.append('subtitle', serviceForm.subtitle);
      if (serviceForm.subtitle_ru) formData.append('subtitle_ru', serviceForm.subtitle_ru);
      if (serviceForm.subtitle_uz) formData.append('subtitle_uz', serviceForm.subtitle_uz);
      if (serviceForm.category) formData.append('category', serviceForm.category);
      if (serviceImage) formData.append('image', serviceImage);

      if (serviceModal === 'add') {
        // Create new service
        await axios.post(`${BACKEND_URL}/api/admin/services`, formData, {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert('Service created successfully!');
      } else {
        // Update existing service
        await axios.put(`${BACKEND_URL}/api/admin/services/${serviceModal.id}`, formData, {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert('Service updated successfully!');
      }

      closeServiceModal();
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service');
    } finally {
      setSavingService(false);
    }
  };

  const deleteService = async (service) => {
    if (!window.confirm(`Are you sure you want to delete the service "${service.name}"?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/services/${service.id}`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      setServices(prev => prev.filter(s => s.id !== service.id));
      alert('Service deleted successfully');
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Failed to delete service');
    }
  };

  // Steps modal functions
  const openStepsModal = (service) => {
    setStepsModal(service);
    setServiceSteps(service.steps || []);
  };

  const closeStepsModal = () => {
    setStepsModal(null);
    setServiceSteps([]);
  };

  const addStep = () => {
    setServiceSteps([...serviceSteps, { icon: 'FileText', title: '', description: '', title_ru: '', description_ru: '', title_uz: '', description_uz: '' }]);
  };

  const removeStep = (index) => {
    setServiceSteps(serviceSteps.filter((_, i) => i !== index));
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...serviceSteps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setServiceSteps(newSteps);
  };

  const saveSteps = async () => {
    // Validate modal state
    if (!stepsModal || !stepsModal.id) {
      alert('Error: No service selected');
      return;
    }
    
    // Validate steps
    for (const step of serviceSteps) {
      if (!step.title || !step.description) {
        alert('Please fill in all step titles and descriptions');
        return;
      }
    }

    setSavingSteps(true);
    try {
      const url = `${BACKEND_URL}/api/admin/services/${stepsModal.id}/steps`;
      console.log('Saving steps to:', url);
      console.log('Steps data:', serviceSteps);
      
      const response = await axios.put(
        url,
        { steps: serviceSteps },
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Save response:', response.data);
      alert('Steps saved successfully!');
      closeStepsModal();
      loadServices();
    } catch (error) {
      console.error('Error saving steps:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to save steps: ${errorMsg}`);
    } finally {
      setSavingSteps(false);
    }
  };

  // Move service up or down
  const moveService = async (index, direction) => {
    const newServices = [...services];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap positions
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];
    
    // Update UI immediately
    setServices(newServices);
    
    // Save new order to backend
    try {
      await axios.post(`${BACKEND_URL}/api/admin/services/reorder`, {
        service_ids: newServices.map(s => s.id)
      }, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
    } catch (error) {
      console.error('Error reordering services:', error);
      // Revert on error
      loadServices();
      alert('Failed to reorder services');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      alert('Review deleted successfully');
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Failed to delete review');
    }
  };

  const removeReviewMedia = async (reviewId, mediaType) => {
    if (!window.confirm(`Remove this ${mediaType} from the review?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}/media/${mediaType}`, {
        headers: { 'X-Admin-Key': ADMIN_PASSWORD }
      });
      setReviews(prev => prev.map(r => {
        if (r.id !== reviewId) return r;
        const updated = { ...r };
        if (mediaType === 'image') { delete updated.image_url; delete updated.image; }
        if (mediaType === 'video') { delete updated.video_url; }
        if (mediaType === 'avatar') { delete updated.avatar_url; }
        return updated;
      }));
    } catch (error) {
      console.error('Error removing media:', error);
      alert('Failed to remove ' + mediaType);
    }
  };

  // Review modal handlers
  const openAddReviewModal = () => {
    setReviewForm({
      author: '',
      rating: 5,
      text: '',
      instagram: '',
      tiktok: ''
    });
    setReviewImage(null);
    setReviewImagePreview(null);
    setReviewAvatar(null);
    setReviewAvatarPreview(null);
    setReviewVideo(null);
    setReviewVideoPreview(null);
    setReviewModal('add');
  };

  const openEditReviewModal = (review) => {
    setReviewForm({
      author: review.author || '',
      rating: review.rating || 5,
      text: review.text || '',
      instagram: review.instagram || '',
      tiktok: review.tiktok || ''
    });
    setReviewImage(null);
    setReviewAvatar(null);
    setReviewVideo(null);
    // Set image preview
    if (review.image_url) {
      const imageUrl = review.image_url.startsWith('http') ? review.image_url : `${BACKEND_URL}${review.image_url}`;
      setReviewImagePreview(imageUrl);
    } else if (review.image) {
      const imageUrl = review.image.startsWith('http') ? review.image : `${BACKEND_URL}/api/uploads/${review.image}`;
      setReviewImagePreview(imageUrl);
    } else {
      setReviewImagePreview(null);
    }
    // Set avatar preview
    setReviewAvatarPreview(review.avatar_url || null);
    // Set video preview
    setReviewVideoPreview(review.video_url || null);
    setReviewModal(review);
  };

  const closeReviewModal = () => {
    setReviewModal(null);
    setReviewForm({ author: '', rating: 5, text: '', instagram: '', tiktok: '' });
    setReviewImage(null);
    setReviewImagePreview(null);
    setReviewAvatar(null);
    setReviewAvatarPreview(null);
    setReviewVideo(null);
    setReviewVideoPreview(null);
    if (reviewImageInputRef.current) reviewImageInputRef.current.value = '';
  };

  const handleReviewImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setReviewImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setReviewImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleReviewFormChange = (field, value) => {
    setReviewForm(prev => ({ ...prev, [field]: value }));
  };

  const saveReview = async () => {
    if (!reviewForm.author || !reviewForm.text) {
      alert('Please fill in author name and review text');
      return;
    }

    setSavingReview(true);
    try {
      const formData = new FormData();
      formData.append('author', reviewForm.author);
      formData.append('rating', reviewForm.rating);
      formData.append('text', reviewForm.text);
      formData.append('instagram', reviewForm.instagram || '');
      formData.append('tiktok', reviewForm.tiktok || '');
      if (reviewImage) formData.append('image', reviewImage);
      if (reviewAvatar) formData.append('avatar', reviewAvatar);
      if (reviewVideo) formData.append('video', reviewVideo);

      if (reviewModal === 'add') {
        await axios.post(`${BACKEND_URL}/api/admin/reviews`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
        });
        alert('Review created successfully!');
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/reviews/${reviewModal.id}`, formData, {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' }
        });
        alert('Review updated successfully!');
      }

      closeReviewModal();
      loadReviews();
    } catch (error) {
      console.error('Error saving review:', error);
      alert('Failed to save review');
    } finally {
      setSavingReview(false);
    }
  };

  // Delete booking (admin)
  const deleteBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to DELETE booking #${booking.bookingNumber}?\n\nThis action cannot be undone and will also delete all associated documents.`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/${booking.id}`, {
        headers: {
          'X-Admin-Key': ADMIN_PASSWORD
        }
      });
      
      // Remove from local state
      setBookings(prev => prev.filter(b => b.id !== booking.id));
      alert(`Booking #${booking.bookingNumber} deleted successfully`);
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  // --- Invoice Generation ---
  const openInvoiceModal = (booking) => {
    // Parse total price from servicePrice; paidAmount is the running total of payments
    const parsePrice = (p) => {
      const n = parseFloat(String(p || '').replace(/[^0-9.]/g, '')) || 0;
      return n;
    };
    const totalPrice = parsePrice(booking.servicePrice);
    const paidAmount = Number(booking.paidAmount) || 0;
    setBookingInvoiceModal({
      booking,
      totalPrice,
      paidAmount,
      lineItems: [
        { description: booking.service || booking.serviceName || 'Visa / Service', qty: 1, unit_price: totalPrice },
      ],
      notes: '',
    });
  };

  const addInvoiceLineItem = () => {
    if (!bookingInvoiceModal) return;
    setBookingInvoiceModal({
      ...bookingInvoiceModal,
      lineItems: [...bookingInvoiceModal.lineItems, { description: '', qty: 1, unit_price: 0 }],
    });
  };

  const removeInvoiceLineItem = (idx) => {
    if (!bookingInvoiceModal) return;
    setBookingInvoiceModal({
      ...bookingInvoiceModal,
      lineItems: bookingInvoiceModal.lineItems.filter((_, i) => i !== idx),
    });
  };

  const updateInvoiceLineItem = (idx, field, value) => {
    if (!bookingInvoiceModal) return;
    const next = [...bookingInvoiceModal.lineItems];
    next[idx] = { ...next[idx], [field]: value };
    setBookingInvoiceModal({ ...bookingInvoiceModal, lineItems: next });
  };

  const invoiceSubtotal = () => {
    if (!bookingInvoiceModal) return 0;
    return bookingInvoiceModal.lineItems.reduce((sum, li) => {
      const qty = parseFloat(li.qty) || 0;
      const price = parseFloat(li.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  };

  const generateInvoice = async () => {
    if (!bookingInvoiceModal || invoiceGenerating) return;
    const { booking, lineItems, notes, totalPrice, paidAmount } = bookingInvoiceModal;
    setInvoiceGenerating(true);
    try {
      const payload = {
        total_price: totalPrice != null ? parseFloat(totalPrice) || 0 : null,
        amount_paid: paidAmount != null ? parseFloat(paidAmount) || 0 : null,
        line_items: lineItems
          .filter((li) => (li.description || '').trim())
          .map((li) => ({
            description: li.description,
            qty: parseFloat(li.qty) || 1,
            unit_price: parseFloat(li.unit_price) || 0,
          })),
        notes: (notes || '').trim(),
      };
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/bookings/${booking.id}/invoice`,
        payload,
        {
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' },
          responseType: 'blob',
        }
      );
      // Trigger browser download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${booking.bookingNumber || booking.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setBookingInvoiceModal(null);
    } catch (e) {
      console.error('Invoice generation failed:', e);
      alert('Failed to generate invoice. Please try again.');
    } finally {
      setInvoiceGenerating(false);
    }
  };

  const openDocRequiredModal = (booking) => {
    setDocRequiredModal(booking);
    setRequiredDocType('passport');
    setDocReason('');
  };

  const submitDocumentRequired = async () => {
    if (!docRequiredModal) return;
    
    if (!docReason.trim()) {
      alert('Please provide a reason for the document requirement');
      return;
    }
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${docRequiredModal.id}/status`,
        { 
          status: 'document_required',
          requiredDocument: requiredDocType,
          reason: docReason
        },
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD
          }
        }
      );
      
      setBookings(prev => prev.map(b => 
        b.id === docRequiredModal.id 
          ? { ...b, status: 'document_required', requiredDocument: requiredDocType, documentReason: docReason }
          : b
      ));
      
      setDocRequiredModal(null);
      setDocReason('');
      alert('Document requirement sent to user');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    // If document_required, open modal instead
    if (newStatus === 'document_required') {
      const booking = bookings.find(b => b.id === bookingId);
      openDocRequiredModal(booking);
      return;
    }
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD
          }
        }
      );
      // Update local state
      setBookings(prev => 
        prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b)
      );
      
      // Show success message for paid status
      if (newStatus === 'paid') {
        alert('Booking marked as paid. Payment confirmation email sent to customer.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to update status: ${errorMessage}`);
    }
  };

  // Handle file selection for admin document upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setUploadPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setUploadPreview(null);
    }
  };

  // Upload admin document to booking
  const handleAdminUpload = async () => {
    if (!uploadFile || !uploadModal) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', uploadFile);
      if (locationLink.trim()) {
        formData.append('location_link', locationLink.trim());
      }

      await axios.post(
        `${BACKEND_URL}/api/admin/bookings/${uploadModal.id}/document`,
        formData,
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update local state
      setBookings(prev =>
        prev.map(b => b.id === uploadModal.id 
          ? { ...b, adminDocument: uploadFile.name } 
          : b
        )
      );

      alert('Document uploaded successfully!');
      closeUploadModal();
      loadBookings(); // Reload to get updated data
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Delete admin document
  const handleDeleteAdminDocument = async (booking) => {
    if (!confirm(`Are you sure you want to delete the admin document for booking #${booking.bookingNumber}?`)) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/bookings/${booking.id}/document`,
        {
          headers: {
            'X-Admin-Key': ADMIN_PASSWORD
          }
        }
      );

      // Update local state
      setBookings(prev =>
        prev.map(b => b.id === booking.id 
          ? { ...b, adminDocument: null } 
          : b
        )
      );

      alert('Document deleted successfully!');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const openUploadModal = (booking) => {
    setUploadModal(booking);
    setUploadFile(null);
    setUploadPreview(null);
  };

  const closeUploadModal = () => {
    setUploadModal(null);
    setUploadFile(null);
    setUploadPreview(null);
    setLocationLink('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Freelance step modal handlers
  const stepLabels = {
    visa_approved: 'Visa Approved',
    option_change: 'Option Change',
    medical_test: 'Medical Test',
    residence_visa: 'Residence Visa',
    emirates_id: 'Emirates ID',
    ready: 'Ready'
  };

  const openFreelanceStepModal = (booking, nextStep) => {
    setFreelanceStepModal({ booking, nextStep });
    setFreelanceStepFile(null);
  };

  const handleFreelanceStepConfirm = async () => {
    if (!freelanceStepModal) return;
    const { booking, nextStep } = freelanceStepModal;
    setFreelanceStepUploading(true);
    try {
      // Upload document if provided
      if (freelanceStepFile) {
        const formData = new FormData();
        formData.append('document', freelanceStepFile);
        formData.append('step', nextStep);
        await axios.post(
          `${BACKEND_URL}/api/admin/bookings/${booking.id}/step-document`,
          formData,
          { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' } }
        );
      }
      // Update status
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${booking.id}/status`,
        { status: nextStep },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
      );
      setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: nextStep, freelanceStep: nextStep } : b));
      setFreelanceStepModal(null);
      setFreelanceStepFile(null);
    } catch (error) {
      console.error('Error updating freelance step:', error);
      alert('Failed to update status: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFreelanceStepUploading(false);
    }
  };

  // Generate Application handler - uses saved photo from booking
  const handleGenerateApplication = async (booking) => {
    try {
      const formData = new FormData();
      // No photo needed - backend will use saved clientPhoto/photoFile from booking
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/bookings/${booking.id}/generate-application`,
        formData,
        { 
          headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'multipart/form-data' },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (booking.name || 'Client').replace(/\s+/g, '_').substring(0, 20);
      link.setAttribute('download', `Application_${safeName}_${booking.bookingNumber || booking.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating application:', error);
      alert('Failed to generate application: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Send Application to client email - one click
  const handleSendApplication = async (booking) => {
    if (!booking.email) {
      alert('This client has no email address. Please add an email first.');
      return;
    }
    if (!window.confirm(`Send application PDF to ${booking.email}?`)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/bookings/${booking.id}/send-application`,
        {},
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
      );
      alert(res.data?.message || 'Application sent successfully!');
    } catch (error) {
      console.error('Error sending application:', error);
      alert('Failed to send: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBalanceUpdate = async () => {
    if (!balanceModal || !balanceInput) return;
    const amount = parseFloat(balanceInput);
    if (isNaN(amount) || amount === 0) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${balanceModal.bookingId}/payment`,
        { amount },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
      );
      setBookings(prev => prev.map(b => b.id === balanceModal.bookingId 
        ? { ...b, paidAmount: Math.max(0, (b.paidAmount || 0) + amount) } : b));
      setBalanceModal(null);
      setBalanceInput('');
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment');
    }
  };


  const openEditDetails = (b) => setEditDetailsModal({
    bookingId: b.id, name: b.name || '', nationality: b.nationality || '',
    passportNumber: b.passportNumber || '', agentName: b.agentName || '', agentPhone: b.agentPhone || ''
  });

  const handleSaveDetails = async () => {
    if (!editDetailsModal || !editDetailsModal.name.trim() || editDetailsSaving) return;
    const { bookingId, ...fields } = editDetailsModal;
    setEditDetailsSaving(true);
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}/details`, fields,
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' } }
      );
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...res.data.updated } : b));
      setEditDetailsModal(null);
    } catch (error) {
      alert('Failed to update booking: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEditDetailsSaving(false);
    }
  };

  const handleEditEmail = async () => {
    if (!editEmailModal || !editEmailInput.trim()) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${editEmailModal.bookingId}/email`,
        { email: editEmailInput.trim() },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' } }
      );
      setBookings(prev => prev.map(b => b.id === editEmailModal.bookingId ? { ...b, email: editEmailInput.trim() } : b));
      setEditEmailModal(null);
      setEditEmailInput('');
    } catch (error) {
      alert('Failed to update email: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSendWarning = async () => {
    if (!warningModal || !warningReason.trim() || warningSending) return;
    setWarningSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/bookings/${warningModal.bookingId}/warning`,
        { reason: warningReason.trim() },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD, 'Content-Type': 'application/json' } }
      );
      setWarningModal(null);
      setWarningReason('');
    } catch (error) {
      alert('Failed to send warning: ' + (error.response?.data?.detail || error.message));
    } finally {
      setWarningSending(false);
    }
  };


  const handleDeductPrice = async () => {
    if (!deductModal || !deductInput) return;
    const amount = parseFloat(deductInput);
    if (isNaN(amount) || amount === 0) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/${deductModal.bookingId}/deduct-price`,
        { amount },
        { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
      );
      setBookings(prev => prev.map(b => b.id === deductModal.bookingId 
        ? { ...b, servicePrice: String(Math.max(0, (parseFloat(String(b.servicePrice || '0').replace(/[^0-9.]/g, '')) || 0) + amount)) } : b));
      setDeductModal(null);
      setDeductInput('');
    } catch (error) {
      console.error('Error adjusting price:', error);
      alert('Failed to adjust price');
    }
  };


  // Avatar background colors matching DashboardPage
  const AVATAR_BACKGROUNDS = {
    avatar1: 'linear-gradient(135deg, #5EFCE8 0%, #00B4D8 100%)',
    avatar2: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
    avatar3: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
    avatar4: 'linear-gradient(135deg, #A855F7 0%, #6366F1 100%)',
    avatar5: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
    avatar6: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  };

  const getAvatarBackground = (avatarId) => {
    return AVATAR_BACKGROUNDS[avatarId] || 'linear-gradient(135deg, #5EFCE8 0%, #00B4D8 100%)';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#00FF7F';
      case 'checked': return '#00BFFF';
      case 'rejected': return '#FF4444';
      case 'document_required': return '#FF8C00';
      case 'pending_payment': return '#A855F7';
      case 'paid': return '#10B981';
      case 'completed': return '#5EFCE8';
      case 'visa_approved': return '#00FF7F';
      case 'option_change': return '#FFD700';
      case 'medical_test': return '#FF69B4';
      case 'residence_visa': return '#00BFFF';
      case 'emirates_id': return '#9B59B6';
      case 'ready': return '#00FF88';
      case 'pending': 
      default: return '#FFA500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle size={16} />;
      case 'checked': return <Eye size={16} />;
      case 'rejected': return <XCircle size={16} />;
      case 'document_required': return <AlertCircle size={16} />;
      case 'pending_payment': return <CreditCard size={16} />;
      case 'paid': return <DollarSign size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      case 'visa_approved': return <CheckCircle size={16} />;
      case 'option_change': return <RefreshCw size={16} />;
      case 'medical_test': return <Heart size={16} />;
      case 'residence_visa': return <FileText size={16} />;
      case 'emirates_id': return <CreditCard size={16} />;
      case 'ready': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'document_required': return 'Doc Required';
      case 'pending_payment': return 'Pending Payment';
      case 'paid': return 'Paid';
      case 'visa_approved': return 'Visa Approved';
      case 'option_change': return 'Option Change';
      case 'medical_test': return 'Medical Test';
      case 'residence_visa': return 'Residence Visa';
      case 'emirates_id': return 'Emirates ID';
      case 'ready': return 'Ready';
      default: return status?.charAt(0).toUpperCase() + status?.slice(1);
    }
  };

  // Filter bookings by status, search query, and visa type
  const freelanceSteps = ['visa_approved', 'option_change', 'medical_test', 'medical_done', 'emirates_id', 'residence_visa', 'ready'];
  const filteredBookings = bookings.filter(b => {
    const effectiveStep = b.freelanceStep || b.status;
    const matchesFilter = filter === 'all' || 
      (freelanceSteps.includes(filter) ? effectiveStep === filter : b.status === filter);
    const matchesSearch = searchQuery === '' || 
      b.bookingNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.mobile?.includes(searchQuery);
    const matchesVisaType = bookingView === 'visa' ? true :
      bookingView === 'freelance' ? (b.service?.toLowerCase().includes('freelance')) :
      bookingView === 'family' ? (b.service?.toLowerCase().includes('family')) : true;
    return matchesFilter && matchesSearch && matchesVisaType;
  });

  // Get the base bookings for the current visa type filter
  const visaTypeBookings = bookingView === 'freelance' ? bookings.filter(b => b.service?.toLowerCase().includes('freelance')) :
    bookingView === 'family' ? bookings.filter(b => b.service?.toLowerCase().includes('family')) : bookings;

  const statusCounts = {
    all: visaTypeBookings.length,
    pending: visaTypeBookings.filter(b => b.status === 'pending').length,
    checked: visaTypeBookings.filter(b => b.status === 'checked').length,
    approved: visaTypeBookings.filter(b => b.status === 'approved').length,
    rejected: visaTypeBookings.filter(b => b.status === 'rejected').length,
    document_required: visaTypeBookings.filter(b => b.status === 'document_required').length,
    pending_payment: visaTypeBookings.filter(b => b.status === 'pending_payment').length,
    paid: visaTypeBookings.filter(b => b.status === 'paid').length,
    visa_approved: visaTypeBookings.filter(b => (b.freelanceStep || b.status) === 'visa_approved').length,
    option_change: visaTypeBookings.filter(b => (b.freelanceStep || b.status) === 'option_change').length,
    medical_test: visaTypeBookings.filter(b => (b.freelanceStep || b.status) === 'medical_test').length,
    residence_visa: visaTypeBookings.filter(b => (b.freelanceStep || b.status) === 'residence_visa').length,
    emirates_id: visaTypeBookings.filter(b => (b.freelanceStep || b.status) === 'emirates_id').length,
    ready: visaTypeBookings.filter(b => (b.freelanceStep || b.status) === 'ready').length,
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-bg-effects">
          <div className="admin-glow-orb admin-glow-1"></div>
          <div className="admin-glow-orb admin-glow-2"></div>
          <div className="admin-glow-orb admin-glow-3"></div>
          <div className="admin-grid-lines"></div>
        </div>
        <div className="admin-login-container">
          <div className="admin-login-header">
            <div className="admin-brand-animation">
              <div className="admin-brand-ring admin-ring-1"></div>
              <div className="admin-brand-ring admin-ring-2"></div>
              <div className="admin-brand-ring admin-ring-3"></div>
              <Shield size={40} className="admin-icon" />
            </div>
            <h1 className="admin-brand-title">
              <span className="admin-brand-r">R</span>egistan
              <span className="admin-brand-dot">.</span>
            </h1>
            <p className="admin-brand-subtitle">Tourism Management</p>
            <div className="admin-brand-divider">
              <span></span>
              <span className="admin-brand-divider-text">ADMIN</span>
              <span></span>
            </div>
          </div>
          {otpStep === 'password' ? (
            <form onSubmit={handleLogin} className="admin-login-form" data-testid="admin-password-form">
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  autoFocus
                  data-testid="admin-password-input"
                />
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 12px', borderRadius: '10px',
                background: 'rgba(94,252,232,0.06)',
                border: '1px solid rgba(94,252,232,0.18)',
                color: '#9BB7C8', fontSize: '12px', lineHeight: 1.45,
                margin: '4px 0 14px',
              }}>
                <Shield size={14} style={{ color: '#5EFCE8', flexShrink: 0 }} />
                <span>
                  Two-factor authentication is enabled. A 6-digit code will be sent to your admin email.
                </span>
              </div>
              {error && <p className="error-message" data-testid="admin-login-error">{error}</p>}
              <button type="submit" className="btn-primary" disabled={otpLoading || !password} data-testid="admin-send-code-btn">
                {otpLoading ? 'Sending code…' : 'Send Verification Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="admin-login-form" data-testid="admin-otp-form">
              <div style={{
                textAlign: 'center', padding: '14px 12px', borderRadius: '12px',
                background: 'rgba(94,252,232,0.06)',
                border: '1px solid rgba(94,252,232,0.18)',
                marginBottom: '16px',
              }}>
                <Mail size={22} style={{ color: '#5EFCE8', marginBottom: '8px' }} />
                <div style={{ fontSize: '13px', color: '#E5E7EB', fontWeight: 600, marginBottom: '4px' }}>
                  Check your email for the 6-digit code
                </div>
                <div style={{ fontSize: '11px', color: '#9BA9C3' }}>
                  Sent to <strong style={{ color: '#5EFCE8' }}>{otpEmailHint || 'admin email'}</strong> · expires in 5 minutes
                </div>
              </div>
              <div className="form-group">
                <label>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={6}
                  value={otpInput}
                  onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  autoFocus
                  data-testid="admin-otp-input"
                  style={{
                    textAlign: 'center',
                    fontSize: '22px',
                    letterSpacing: '8px',
                    fontWeight: 700,
                  }}
                />
              </div>
              {error && <p className="error-message" data-testid="admin-otp-error">{error}</p>}
              <button
                type="submit"
                className="btn-primary"
                disabled={otpLoading || otpInput.length !== 6}
                data-testid="admin-verify-otp-btn"
              >
                {otpLoading ? 'Verifying…' : 'Verify & Login'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', fontSize: '12px' }}>
                <button
                  type="button"
                  onClick={() => { setOtpStep('password'); setError(''); setOtpInput(''); setOtpRequestId(''); }}
                  style={{ background: 'none', border: 'none', color: '#9BA9C3', cursor: 'pointer', padding: 0 }}
                  data-testid="admin-otp-back-btn"
                >
                  ← Change password
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={otpResendCooldown > 0 || otpLoading}
                  style={{
                    background: 'none', border: 'none',
                    color: otpResendCooldown > 0 ? '#5E6B85' : '#5EFCE8',
                    cursor: otpResendCooldown > 0 ? 'not-allowed' : 'pointer', padding: 0,
                    fontWeight: 600,
                  }}
                  data-testid="admin-resend-otp-btn"
                >
                  {otpResendCooldown > 0 ? `Resend in ${otpResendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
          <button className="btn-back-home" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      {/* Header */}
      <header className="admin-header">
        <button
          className="admin-mobile-menu-btn"
          onClick={() => setMobileMenuOpen(v => !v)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          data-testid="admin-mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <div className="admin-header-left">
          <Shield size={24} className="admin-icon" />
          <h1>Admin Dashboard</h1>
          <span className="admin-badge" style={{ cursor: 'pointer', background: activeTab === 'send-email' && emailSubTab === 'delivery-log' ? 'rgba(0, 255, 209, 0.18)' : 'rgba(0, 255, 209, 0.08)', border: '1px solid rgba(0, 255, 209, 0.25)', color: '#2E7D32', marginLeft: '12px' }}
            onClick={() => { setActiveTab('send-email'); setEmailSubTab('delivery-log'); loadDeliveryLogs(); }} data-testid="email-log-header-badge">
            <Activity size={16} />
            Email Log
          </span>
          {storageStats && (
            <span className="admin-badge" style={{ cursor: 'pointer', background: activeTab === 'storage' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.12)', border: '1px solid rgba(139, 92, 246, 0.25)', color: '#8B5CF6', marginLeft: '12px' }}
              onClick={() => { setActiveTab('storage'); loadStorageStats(); loadLocalFiles(); }} data-testid="storage-badge">
              <HardDrive size={16} />
              {(storageStats.totalSize / (1024 * 1024)).toFixed(1)} MB
            </span>
          )}
          {emailStats && (
            <span className="admin-badge" style={{ cursor: 'pointer', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.25)', color: emailStats.sent_today >= emailStats.daily_limit * 0.9 ? '#FF6347' : '#3B82F6', marginLeft: '8px' }}
              onClick={loadEmailStats} data-testid="email-stats-badge"
              title={`Sent today: ${emailStats.sent_today}/${emailStats.daily_limit} | Mailbox: ${emailStats.quota_used ? (emailStats.quota_used / (1024*1024)).toFixed(1) + ' MB / ' + (emailStats.quota_limit / (1024*1024*1024)).toFixed(0) + ' GB' : 'N/A'} | Messages: ${emailStats.msg_used || 0}/${(emailStats.msg_limit || 0).toLocaleString()}`}>
              <Mail size={16} />
              {emailStats.sent_today}/{emailStats.daily_limit}
              {emailStats.quota_limit > 0 && (
                <span style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.7, borderLeft: '1px solid rgba(59,130,246,0.3)', paddingLeft: '6px' }}>
                  {(emailStats.quota_used / (1024*1024)).toFixed(1)}/{(emailStats.quota_limit / (1024*1024*1024)).toFixed(0)}GB
                </span>
              )}
            </span>
          )}
        </div>
        <div className="admin-header-right">
          <span className="admin-badge" style={{ cursor: 'pointer', background: activeTab === 'seo' ? 'rgba(16, 185, 129, 0.2)' : undefined, borderColor: activeTab === 'seo' ? 'rgba(16, 185, 129, 0.4)' : undefined }}
            onClick={() => { setActiveTab('seo'); loadSeoSettings(); }} data-testid="seo-header-badge">
            <Search size={16} style={{ color: '#10B981' }} />
            SEO
          </span>
          {stats && (
            <>
              <span className="admin-badge online">
                <Activity size={16} />
                {stats.online_users} Online
              </span>
              <span className="admin-badge">
                <UserCheck size={16} />
                {stats.total_users} Users
              </span>
            </>
          )}
          <span className="admin-badge">
            <Package size={16} />
            {bookings.length} Bookings
          </span>
          <span className="admin-badge">
            <Star size={16} />
            {reviews.length} Reviews
          </span>
          <button className="btn-theme-toggle" onClick={toggleTheme} data-testid="admin-theme-toggle"
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              background: theme === 'dark' ? 'rgba(255,184,0,0.12)' : 'rgba(96,165,250,0.12)',
              border: `1px solid ${theme === 'dark' ? 'rgba(255,184,0,0.25)' : 'rgba(96,165,250,0.25)'}`,
              color: theme === 'dark' ? '#FFB800' : '#60A5FA',
              transition: 'all 0.3s ease'
            }}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className={`admin-tabs ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {mobileMenuOpen && (
          <div className="admin-tabs-mobile-header">
            <span>Menu</span>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="admin-tabs-mobile-close"
              aria-label="Close menu"
              data-testid="admin-mobile-menu-close"
            >
              <X size={20} />
            </button>
          </div>
        )}
        <button 
          className={`admin-tab ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => { setActiveTab('bookings'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-bookings"
        >
          <Package size={18} />
          Bookings
        </button>
        <button 
          className={`admin-tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => { setActiveTab('services'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-services"
        >
          <Briefcase size={18} />
          Services
        </button>
        <button 
          className={`admin-tab ${activeTab === 'tours' ? 'active' : ''}`}
          onClick={() => { setActiveTab('tours'); loadTours(); setMobileMenuOpen(false); }}
          data-testid="admin-tab-tours"
        >
          <Compass size={18} />
          Tours
        </button>
        <button 
          className={`admin-tab ${activeTab === 'team' ? 'active' : ''}`}
          onClick={() => { setActiveTab('team'); loadTeam(); setMobileMenuOpen(false); }}
          data-testid="admin-tab-team"
        >
          <Users size={18} />
          Team
        </button>
        <button 
          className={`admin-tab ${activeTab === 'text-sections' ? 'active' : ''}`}
          onClick={() => { setActiveTab('text-sections'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-text-sections"
        >
          <Type size={18} />
          Text Sections
        </button>
        <button 
          className={`admin-tab ${activeTab === 'stories' ? 'active' : ''}`}
          onClick={() => { setActiveTab('stories'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-stories"
        >
          <Film size={18} />
          Stories
        </button>
        <button 
          className={`admin-tab ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => { setActiveTab('reviews'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-reviews"
        >
          <Star size={18} />
          Reviews
        </button>
        <button 
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => { setActiveTab('users'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-users"
        >
          <Users size={18} />
          Users
        </button>
        <button
          className={`admin-tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => { setActiveTab('invoices'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-invoices"
        >
          <CreditCard size={18} />
          Invoices
        </button>
        <button
          className={`admin-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => { setActiveTab('messages'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-messages"
        >
          <MessageCircle size={18} />
          Messages
          {messages.filter(m => m.status === 'unread').length > 0 && (
            <span className="tab-badge">{messages.filter(m => m.status === 'unread').length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'send-email' ? 'active' : ''}`}
          onClick={() => { setActiveTab('send-email'); loadSentEmails(); loadInboxEmails(); setMobileMenuOpen(false); }}
          data-testid="admin-tab-send-email"
        >
          <Mail size={18} />
          Send Email
          {lastInboxCount > 0 && (
            <span className="tab-badge" data-testid="inbox-unread-tab-badge">{lastInboxCount}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'statements' ? 'active' : ''}`}
          onClick={() => { setActiveTab('statements'); setMobileMenuOpen(false); }}
          data-testid="admin-tab-statements"
        >
          <FileText size={18} />
          Statements
        </button>
        <button
          className={`admin-tab ${activeTab === 'global-visa' ? 'active' : ''}`}
          onClick={() => { setActiveTab('global-visa'); loadVisaApplications(); setNewVisaCount(0); setMobileMenuOpen(false); }}
          data-testid="admin-tab-global-visa"
          style={{ position: 'relative' }}
        >
          <Globe size={18} />
          Global Visa
          {newVisaCount > 0 && (
            <span data-testid="global-visa-badge" style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              minWidth: '20px',
              height: '20px',
              borderRadius: '10px',
              background: '#FF4D6A',
              color: '#fff',
              fontSize: '11px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 5px',
              boxShadow: '0 2px 8px rgba(255, 77, 106, 0.5)',
              animation: 'pulse-badge 1.5s infinite'
            }}>
              {newVisaCount}
            </span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'accounting' ? 'active' : ''}`}
          onClick={() => { setActiveTab('accounting'); loadAccounting(); loadClientServices(); setMobileMenuOpen(false); }}
          data-testid="admin-tab-accounting"
        >
          <CreditCard size={18} />
          Accounting
        </button>

        {/* Mobile-only quick actions (visible when drawer is open on small screens) */}
        <div className="admin-tabs-mobile-footer">
          <div className="admin-tabs-mobile-stats">
            <span><Package size={14} /> {bookings.length} Bookings</span>
            <span><Star size={14} /> {reviews.length} Reviews</span>
            {stats && <span><Activity size={14} /> {stats.online_users || 0} Online</span>}
          </div>
          <button
            className="admin-tabs-mobile-action theme"
            onClick={() => { toggleTheme(); }}
            data-testid="admin-mobile-theme-toggle"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <button
            className="admin-tabs-mobile-action logout"
            onClick={handleLogout}
            data-testid="admin-mobile-logout"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {activeTab === 'bookings' && (
        <>
      {/* Search Bar */}
      <div className="admin-search-bar">
        <div className="search-input-wrapper">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder={bookingView === 'visa' ? "Search by booking number, name, or phone..." : "Search tour bookings..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>
              <X size={18} />
            </button>
          )}
        </div>
        {searchQuery && bookingView === 'visa' && (
          <span className="search-results-count">
            Found {filteredBookings.length} result{filteredBookings.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Booking View Toggle */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '30px', marginBottom: '16px', padding: '0 0 0 4px', flexWrap: 'wrap' }} data-testid="booking-view-toggle">
        <button onClick={() => setBookingView('visa')} data-testid="booking-view-visa"
          style={{
            padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--border-color)', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', transition: 'all 0.2s',
            background: bookingView === 'visa' ? 'var(--brand-primary, #2E7D32)' : 'transparent',
            color: bookingView === 'visa' ? '#000' : 'var(--text-secondary)'
          }}>
          <Package size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          All Visa Bookings
        </button>
        <button onClick={() => setBookingView('freelance')} data-testid="booking-view-freelance"
          style={{
            padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--border-color)', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', transition: 'all 0.2s',
            background: bookingView === 'freelance' ? 'var(--brand-primary, #2E7D32)' : 'transparent',
            color: bookingView === 'freelance' ? '#000' : 'var(--text-secondary)'
          }}>
          <Briefcase size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Freelance Visa ({bookings.filter(b => b.service?.toLowerCase().includes('freelance')).length})
        </button>
        <button onClick={() => setBookingView('family')} data-testid="booking-view-family"
          style={{
            padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--border-color)', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', transition: 'all 0.2s',
            background: bookingView === 'family' ? 'var(--brand-primary, #2E7D32)' : 'transparent',
            color: bookingView === 'family' ? '#000' : 'var(--text-secondary)'
          }}>
          <Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Family Visa ({bookings.filter(b => b.service?.toLowerCase().includes('family')).length})
        </button>
        <button onClick={() => { setBookingView('tour'); loadTourBookings(); }} data-testid="booking-view-tour"
          style={{
            padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--border-color)', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', transition: 'all 0.2s',
            background: bookingView === 'tour' ? 'var(--brand-primary, #2E7D32)' : 'transparent',
            color: bookingView === 'tour' ? '#000' : 'var(--text-secondary)'
          }}>
          <Compass size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Tour Bookings ({tourBookings.length})
        </button>
        <button onClick={() => { setBookingView('services'); loadBookingServices(); }} data-testid="booking-view-services"
          style={{
            padding: '10px 20px', borderRadius: '10px', border: '1px solid var(--border-color)', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', transition: 'all 0.2s',
            background: bookingView === 'services' ? 'var(--brand-primary, #2E7D32)' : 'transparent',
            color: bookingView === 'services' ? '#000' : 'var(--text-secondary)'
          }}>
          <Receipt size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Services ({bookingServices.length})
        </button>
      </div>

      {(bookingView === 'visa' || bookingView === 'freelance' || bookingView === 'family') && (<>

      {/* Admin Add Booking Button & Form - only for Freelance/Family */}
      {(bookingView === 'freelance' || bookingView === 'family') && (
        <div style={{ marginBottom: '20px' }}>
          {!showAdminBookingForm ? (
            <button onClick={() => setShowAdminBookingForm(true)} data-testid="admin-add-booking-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px',
                background: 'var(--brand-primary, #2E7D32)', color: '#000', border: 'none',
                borderRadius: '10px', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
              }}>
              <Plus size={18} /> Add {bookingView === 'freelance' ? 'Freelance' : 'Family'} Visa Booking
            </button>
          ) : (
            <div style={{
              background: 'var(--card-bg, #1a1a2e)', border: '1px solid var(--border-color, #333)',
              borderRadius: '14px', padding: '24px', maxWidth: '700px'
            }} data-testid="admin-booking-form">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px' }}>
                  <Briefcase size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  New {bookingView === 'freelance' ? 'Freelance' : 'Family'} Visa Booking
                </h3>
                <button onClick={() => setShowAdminBookingForm(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Full Name *</label>
                  <input type="text" value={adminBookingForm.name} placeholder="Full name" data-testid="admin-booking-name"
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Email</label>
                  <input type="email" value={adminBookingForm.email} placeholder="email@example.com" data-testid="admin-booking-email"
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, email: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Nationality</label>
                  <input type="text" value={adminBookingForm.nationality} placeholder="e.g. Uzbekistan" data-testid="admin-booking-nationality"
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, nationality: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Date</label>
                  <input type="date" value={adminBookingForm.date} data-testid="admin-booking-date"
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, date: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Service Price</label>
                  <input type="text" value={adminBookingForm.servicePrice} placeholder="e.g. 2500 AED" data-testid="admin-booking-price"
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, servicePrice: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Agent Name <span style={{ color: '#FF6347' }}>*</span></label>
                  <input type="text" value={adminBookingForm.agentName} placeholder="Agent name" data-testid="admin-booking-agent" required
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, agentName: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${!adminBookingForm.agentName ? 'rgba(255,99,71,0.4)' : 'var(--border-color)'}`, background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Agent Phone <span style={{ color: '#FF6347' }}>*</span></label>
                  <input type="text" value={adminBookingForm.agentPhone} placeholder="e.g. +971501234567" data-testid="admin-booking-agent-phone" required
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, agentPhone: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${!adminBookingForm.agentPhone ? 'rgba(255,99,71,0.4)' : 'var(--border-color)'}`, background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Passport Number <span style={{ color: '#FF6347' }}>*</span></label>
                  <input type="text" value={adminBookingForm.passportNumber} placeholder="e.g. AB1234567" data-testid="admin-booking-passport-number" required
                    onChange={(e) => setAdminBookingForm({ ...adminBookingForm, passportNumber: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${!adminBookingForm.passportNumber ? 'rgba(255,99,71,0.4)' : 'var(--border-color)'}`, background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                {bookingView === 'freelance' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>
                      <Camera size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      Client Photo <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(for Application PDF)</span>
                    </label>
                    {!adminBookingPhoto ? (
                      <div
                        onClick={() => adminBookingPhotoRef.current?.click()}
                        data-testid="admin-booking-photo-upload"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                          padding: '20px', borderRadius: '10px', cursor: 'pointer',
                          border: '2px dashed var(--border-color, #333)',
                          background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-muted)',
                          fontSize: '13px', transition: 'border-color 0.2s'
                        }}
                      >
                        <Camera size={22} /> Click to upload client photo (JPG/PNG)
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img
                          src={URL.createObjectURL(adminBookingPhoto)}
                          alt="Client"
                          style={{ width: '60px', height: '75px', borderRadius: '8px', objectFit: 'cover', border: '2px solid var(--brand-primary, #2E7D32)' }}
                        />
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>{adminBookingPhoto.name}</div>
                          <button
                            onClick={() => { setAdminBookingPhoto(null); if (adminBookingPhotoRef.current) adminBookingPhotoRef.current.value = ''; }}
                            style={{ marginTop: '4px', background: 'rgba(255,99,71,0.12)', color: '#FF6347', border: '1px solid rgba(255,99,71,0.3)', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            <X size={12} /> Remove
                          </button>
                        </div>
                      </div>
                    )}
                    <input
                      ref={adminBookingPhotoRef} type="file" accept="image/jpeg,image/png,image/jpg"
                      onChange={(e) => { if (e.target.files[0]) setAdminBookingPhoto(e.target.files[0]); }}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowAdminBookingForm(false)}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 500 }}>
                  Cancel
                </button>
                <button onClick={handleAdminBookingSubmit} disabled={adminBookingSubmitting || !adminBookingForm.name || !adminBookingForm.agentName || !adminBookingForm.agentPhone || !adminBookingForm.passportNumber}
                  data-testid="admin-booking-submit"
                  style={{
                    padding: '10px 24px', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer',
                    background: (!adminBookingForm.name || !adminBookingForm.mobile) ? 'var(--text-muted)' : 'var(--brand-primary, #2E7D32)',
                    color: '#000', opacity: adminBookingSubmitting ? 0.6 : 1
                  }}>
                  {adminBookingSubmitting ? 'Submitting...' : 'Add Booking'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="admin-stats">
        <div className={`stat-card ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          <span className="stat-number">{statusCounts.all}</span>
          <span className="stat-label">All Bookings</span>
        </div>
        <div className={`stat-card pending ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
          <span className="stat-number">{statusCounts.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        {bookingView === 'freelance' ? (<>
          <div className={`stat-card approved ${filter === 'visa_approved' ? 'active' : ''}`} onClick={() => setFilter('visa_approved')}>
            <span className="stat-number">{statusCounts.visa_approved}</span>
            <span className="stat-label">Visa Approved</span>
          </div>
          <div className={`stat-card checked ${filter === 'option_change' ? 'active' : ''}`} onClick={() => setFilter('option_change')} style={filter === 'option_change' ? { borderColor: '#FFD700' } : {}}>
            <span className="stat-number" style={{ color: '#FFD700' }}>{statusCounts.option_change}</span>
            <span className="stat-label">Option Change</span>
          </div>
          <div className={`stat-card ${filter === 'medical_test' ? 'active' : ''}`} onClick={() => setFilter('medical_test')} style={filter === 'medical_test' ? { borderColor: '#FF69B4' } : {}}>
            <span className="stat-number" style={{ color: '#FF69B4' }}>{statusCounts.medical_test}</span>
            <span className="stat-label">Medical Test</span>
          </div>
          <div className={`stat-card ${filter === 'emirates_id' ? 'active' : ''}`} onClick={() => setFilter('emirates_id')} style={filter === 'emirates_id' ? { borderColor: '#9B59B6' } : {}}>
            <span className="stat-number" style={{ color: '#9B59B6' }}>{statusCounts.emirates_id}</span>
            <span className="stat-label">Emirates ID</span>
          </div>
          <div className={`stat-card checked ${filter === 'residence_visa' ? 'active' : ''}`} onClick={() => setFilter('residence_visa')}>
            <span className="stat-number">{statusCounts.residence_visa}</span>
            <span className="stat-label">Residence Visa</span>
          </div>
          <div className={`stat-card ${filter === 'ready' ? 'active' : ''}`} onClick={() => setFilter('ready')} style={filter === 'ready' ? { borderColor: '#00FF88' } : {}}>
            <span className="stat-number" style={{ color: '#00FF88' }}>{statusCounts.ready}</span>
            <span className="stat-label">Ready</span>
          </div>
        </>) : (<>
          <div className={`stat-card checked ${filter === 'checked' ? 'active' : ''}`} onClick={() => setFilter('checked')}>
            <span className="stat-number">{statusCounts.checked}</span>
            <span className="stat-label">Checked</span>
          </div>
          <div className={`stat-card doc-required ${filter === 'document_required' ? 'active' : ''}`} onClick={() => setFilter('document_required')}>
            <span className="stat-number">{statusCounts.document_required}</span>
            <span className="stat-label">Doc Required</span>
          </div>
          <div className={`stat-card approved ${filter === 'approved' ? 'active' : ''}`} onClick={() => setFilter('approved')}>
            <span className="stat-number">{statusCounts.approved}</span>
            <span className="stat-label">Approved</span>
          </div>
          <div className={`stat-card rejected ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')}>
            <span className="stat-number">{statusCounts.rejected}</span>
            <span className="stat-label">Rejected</span>
          </div>
          <div className={`stat-card pending-payment ${filter === 'pending_payment' ? 'active' : ''}`} onClick={() => setFilter('pending_payment')}>
            <span className="stat-number">{statusCounts.pending_payment}</span>
            <span className="stat-label">Pending Payment</span>
          </div>
          <div className={`stat-card paid ${filter === 'paid' ? 'active' : ''}`} onClick={() => setFilter('paid')}>
            <span className="stat-number">{statusCounts.paid}</span>
            <span className="stat-label">Paid</span>
          </div>
        </>)}
      </div>

      {/* Content */}
      <div className="admin-content">
        {loading ? (
          <div className="loading-state">Loading bookings...</div>
        ) : filteredBookings.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <h3>No bookings found</h3>
            <p>There are no {filter !== 'all' ? filter : ''} bookings yet.</p>
          </div>
        ) : (
          <div className="bookings-table-container">
            <table className="bookings-table bookings-table-pro" data-testid="bookings-table">
              <thead>
                <tr>
                  <th>Booking #</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Date</th>
                  <th>Documents</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr key={booking.id} className="booking-row" data-testid={`booking-row-${booking.id}`}>
                    <td className="booking-number-cell" data-label="Booking #">
                      <span className="booking-num">{booking.bookingNumber}</span>
                      <span className="booking-date-small">
                        {new Date(booking.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="customer-cell" data-label="Customer">
                      <div className="customer-info-row">
                        {booking.userProfilePhoto ? (
                          <img 
                            src={getServiceImageUrl(booking.userProfilePhoto)}
                            alt={booking.name}
                            className="customer-photo"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        {booking.userAvatar ? (
                          <div 
                            className="customer-avatar"
                            style={{ 
                              display: booking.userProfilePhoto ? 'none' : 'flex',
                              background: getAvatarBackground(booking.userAvatar),
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: '#000'
                            }}
                          >
                            {booking.name?.charAt(0)?.toUpperCase()}
                          </div>
                        ) : (
                          <div className="customer-photo-placeholder">
                            {booking.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="customer-name">
                            {booking.name}
                            {/* NEW badge for bookings less than 24 hours old */}
                            {new Date() - new Date(booking.createdAt) < 24 * 60 * 60 * 1000 && (
                              <span className="new-booking-badge">NEW</span>
                            )}
                          </div>
                          <div className="customer-details">
                            <span><Phone size={12} /> {booking.agentPhone || booking.mobile}</span>
                            {booking.email && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Mail size={12} /> {booking.email}
                                <button onClick={() => { setEditEmailModal({ bookingId: booking.id, currentEmail: booking.email }); setEditEmailInput(booking.email || ''); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)', display: 'inline-flex' }}
                                  title="Edit email" data-testid="edit-email-btn">
                                  <Edit3 size={11} />
                                </button>
                              </span>
                            )}
                            {!booking.email && (
                              <button onClick={() => { setEditEmailModal({ bookingId: booking.id, currentEmail: '' }); setEditEmailInput(''); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(255,165,0,0.1)', border: '1px solid rgba(255,165,0,0.3)', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer', color: '#FFA500', fontSize: '11px', fontWeight: 600 }}
                                data-testid="add-email-btn">
                                <Mail size={11} /> Add Email
                              </button>
                            )}
                            {booking.nationality && <span><Globe size={12} /> {booking.nationality}</span>}
                            {booking.telegram && (
                              <span
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#229ED9', fontWeight: 600 }}
                                title="Client's Telegram (provided at booking)"
                                data-testid="booking-telegram-display"
                              >
                                <Send size={12} /> {booking.telegram}
                              </span>
                            )}
                          </div>
                          {/* Additional details: First/Last name, Passport */}
                          <div className="customer-extra-details">
                            {booking.firstName && booking.lastName && (
                              <span className="extra-detail">
                                <User size={12} /> {booking.firstName} {booking.lastName}
                              </span>
                            )}
                            {booking.passportNumber && (
                              <span className="extra-detail">
                                <FileText size={12} /> {booking.passportNumber}
                              </span>
                            )}
                            {booking.agentName && (
                              <span className="extra-detail" style={{ color: '#FFD700' }}>
                                <Briefcase size={12} /> Agent: {booking.agentName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="service-cell" data-label="Service">
                      <div className="service-name">{booking.service}</div>
                      <div className="service-price">{booking.servicePrice}</div>
                      {booking.debitCard === 'yes' && (
                        <div className="debit-card-badge" data-testid="debit-card-badge" style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginTop: '4px',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          background: 'rgba(94, 252, 232, 0.12)',
                          border: '1px solid rgba(94, 252, 232, 0.3)',
                          fontSize: '11px',
                          color: '#5EFCE8',
                          fontWeight: '600'
                        }}>
                          <CreditCard size={11} /> Debit Card +AED {booking.debitCardFee || '500'}
                        </div>
                      )}
                    </td>
                    <td className="date-cell" data-label="Travel Date">
                      <span className="date-cell-inner" data-testid="booking-travel-date">
                        <Calendar size={14} />
                        {booking.date}
                      </span>
                    </td>
                    <td className="documents-cell" data-label="Documents">
                      <div className="docs-row">
                        <div className="user-docs">
                          <span className="docs-label">User:</span>
                          {booking.passportFile && (
                            <a 
                              href={getServiceImageUrl(booking.passportFile)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="doc-link"
                              title="View Passport"
                            >
                              <FileText size={16} />
                            </a>
                          )}
                          {booking.photoFile && (
                            <a 
                              href={getServiceImageUrl(booking.photoFile)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="doc-link"
                              title="View Photo"
                            >
                              <Image size={16} />
                            </a>
                          )}
                          {!booking.passportFile && !booking.photoFile && (
                            <span className="no-docs">None</span>
                          )}
                          {/* Show UPDATED badge if document was re-uploaded */}
                          {booking.documentReuploadedAt && (
                            <div className="doc-updated-badge">
                              <span className="updated-label">UPDATED</span>
                            </div>
                          )}
                        </div>
                        <div className="admin-docs">
                          <span className="docs-label">Admin:</span>
                          {booking.adminDocument ? (
                            <>
                              <a 
                                href={getServiceImageUrl(booking.adminDocument)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="doc-link admin-doc"
                                title="View Admin Document"
                              >
                                <Download size={16} />
                              </a>
                              <button
                                className="doc-action-btn edit"
                                onClick={() => openUploadModal(booking)}
                                title="Replace Document"
                              >
                                <Upload size={14} />
                              </button>
                              <button
                                className="doc-action-btn delete"
                                onClick={() => handleDeleteAdminDocument(booking)}
                                title="Delete Document"
                              >
                                <XCircle size={14} />
                              </button>
                            </>
                          ) : (
                            <span className="no-docs">None</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="status-cell" data-label="Status">
                      <span 
                        className="status-badge"
                        style={{ 
                          backgroundColor: `${getStatusColor(booking.status)}20`,
                          color: getStatusColor(booking.status),
                          border: `1px solid ${getStatusColor(booking.status)}`
                        }}
                      >
                        {getStatusIcon(booking.status)}
                        {booking.status.toUpperCase()}
                      </span>
                      {/* Show paid amount if status is paid */}
                      {booking.status === 'paid' && booking.paidAmount && (
                        <div className="paid-amount-badge">
                          <DollarSign size={12} />
                          AED {booking.paidAmount.toLocaleString()}
                        </div>
                      )}
                      {booking.invoiceNumber && (
                        <div className="invoice-ref">
                          {booking.invoiceNumber}
                        </div>
                      )}
                    </td>
                    <td className="actions-cell" data-label="Actions">
                      <div className="action-buttons">
                        {bookingView === 'freelance' ? (<>
                          {(() => {
                            const steps = ['pending', 'visa_approved', 'option_change', 'medical_test', 'medical_done', 'emirates_id', 'residence_visa', 'ready'];
                            // Use freelanceStep if available, otherwise fall back to status
                            const currentStep = booking.freelanceStep || (steps.includes(booking.status) ? booking.status : 'pending');
                            const currentIdx = steps.indexOf(currentStep);
                            const nextStep = currentIdx < steps.length - 1 ? steps[currentIdx + 1] : null;
                            const stepConfig = [
                              { key: 'visa_approved', label: 'Visa Approved', icon: <CheckCircle size={16} />, color: '#00FF7F', bg: 'rgba(0, 255, 127, 0.12)' },
                              { key: 'option_change', label: 'Option Change', icon: <RefreshCw size={16} />, color: '#FFD700', bg: 'rgba(255, 215, 0, 0.12)' },
                              { key: 'medical_test', label: 'Medical Test', icon: <Heart size={16} />, color: '#FF69B4', bg: 'rgba(255, 105, 180, 0.12)' },
                              { key: 'emirates_id', label: 'Emirates ID', icon: <CreditCard size={16} />, color: '#9B59B6', bg: 'rgba(155, 89, 182, 0.12)' },
                              { key: 'residence_visa', label: 'Residence Visa', icon: <FileText size={16} />, color: '#00BFFF', bg: 'rgba(0, 191, 255, 0.12)' },
                              { key: 'ready', label: 'Ready', icon: <CheckCircle size={16} />, color: '#00FF88', bg: 'rgba(0, 255, 136, 0.12)' },
                            ];
                            const completedIdx = steps.indexOf(currentStep);
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                {stepConfig.map((step, i) => {
                                  const stepIdx = steps.indexOf(step.key);
                                  const isDone = stepIdx <= completedIdx && completedIdx > 0;
                                  const isNext = step.key === nextStep;
                                  const isCurrent = currentStep === step.key;
                                  return (
                                    <React.Fragment key={step.key}>
                                      {i > 0 && <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>›</span>}
                                      <button
                                        onClick={() => isNext ? openFreelanceStepModal(booking, step.key) : null}
                                        title={isDone ? `${step.label} ✓` : isNext ? `Next: ${step.label}` : step.label}
                                        disabled={!isNext}
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: '4px',
                                          padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                                          cursor: isNext ? 'pointer' : 'default',
                                          border: isNext ? `2px solid ${step.color}` : isCurrent ? `2px solid ${step.color}` : '1px solid var(--border-color, #333)',
                                          background: isCurrent ? step.bg : isDone ? step.bg : 'transparent',
                                          color: isDone || isCurrent ? step.color : isNext ? step.color : 'var(--text-muted)',
                                          opacity: isDone || isCurrent || isNext ? 1 : 0.4,
                                        }}
                                      >
                                        {step.icon}
                                        {isDone && !isCurrent ? '✓' : ''}
                                      </button>
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          {/* Medical Step Action Buttons */}
                          {(() => {
                            const currentStep = booking.freelanceStep || booking.status;
                            if (currentStep === 'medical_test') {
                              return (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                  <button
                                    onClick={() => openFreelanceStepModal(booking, 'medical_done')}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                      padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                      cursor: 'pointer', border: '2px solid #FF69B4',
                                      background: 'rgba(255, 105, 180, 0.12)', color: '#FF69B4',
                                    }}
                                    data-testid="medical-done-btn"
                                  >
                                    <Heart size={14} /> Medical Done
                                  </button>
                                  <button
                                    onClick={() => openFreelanceStepModal(booking, 'medical_done')}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                      padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                      cursor: 'pointer', border: '2px solid #00BFFF',
                                      background: 'rgba(0, 191, 255, 0.12)', color: '#00BFFF',
                                    }}
                                    data-testid="medical-test-done-btn"
                                  >
                                    <CheckCircle size={14} /> Medical Test Done
                                  </button>
                                </div>
                              );
                            }
                            if (currentStep === 'medical_done') {
                              return (
                                <div style={{ marginTop: '8px' }}>
                                  <button
                                    onClick={() => openFreelanceStepModal(booking, 'emirates_id')}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                      padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                      cursor: 'pointer', border: '2px solid #9B59B6',
                                      background: 'rgba(155, 89, 182, 0.12)', color: '#9B59B6',
                                    }}
                                    data-testid="upload-emirates-id-btn"
                                  >
                                    <CreditCard size={14} /> Upload Emirates ID
                                  </button>
                                </div>
                              );
                            }
                            if (currentStep === 'emirates_id') {
                              return (
                                <div style={{ marginTop: '8px' }}>
                                  <button
                                    onClick={() => openFreelanceStepModal(booking, 'residence_visa')}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                      padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                      cursor: 'pointer', border: '2px solid #00BFFF',
                                      background: 'rgba(0, 191, 255, 0.12)', color: '#00BFFF',
                                    }}
                                    data-testid="upload-residence-visa-btn"
                                  >
                                    <FileText size={14} /> Upload Residence Visa
                                  </button>
                                </div>
                              );
                            }
                            if (currentStep === 'residence_visa') {
                              return (
                                <div style={{ marginTop: '8px' }}>
                                  <button
                                    onClick={() => {
                                      // Ready step doesn't need a file, just update status
                                      axios.patch(
                                        `${BACKEND_URL}/api/admin/bookings/${booking.id}/status`,
                                        { status: 'ready' },
                                        { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
                                      ).then(() => {
                                        setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, status: 'ready', freelanceStep: 'ready' } : b));
                                      }).catch(err => alert('Failed: ' + (err.response?.data?.detail || err.message)));
                                    }}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                      padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                      cursor: 'pointer', border: '2px solid #00FF88',
                                      background: 'rgba(0, 255, 136, 0.12)', color: '#00FF88',
                                    }}
                                    data-testid="mark-ready-btn"
                                  >
                                    <CheckCircle size={14} /> Mark as Ready
                                  </button>
                                </div>
                              );
                            }
                            return null;
                          })()}
                          {/* Generate Application Button */}
                          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleGenerateApplication(booking)}
                              data-testid="generate-application-btn"
                              style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '7px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                                cursor: 'pointer', border: '2px solid #2E7D32',
                                background: 'rgba(46, 125, 50, 0.12)', color: '#4CAF50',
                              }}
                            >
                              <FileText size={13} /> Generate Application
                            </button>
                            <button
                              onClick={() => handleSendApplication(booking)}
                              data-testid="send-application-btn"
                              title={booking.email ? `Send to ${booking.email}` : 'No email'}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '7px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                                cursor: booking.email ? 'pointer' : 'not-allowed',
                                border: '2px solid #0288D1',
                                background: 'rgba(2, 136, 209, 0.12)', color: '#29B6F6',
                                opacity: booking.email ? 1 : 0.4,
                              }}
                            >
                              <Send size={13} /> Send to Client
                            </button>
                          </div>
                          {/* Mark as Ready (always-visible shortcut) — finalizes booking */}
                          {booking.status !== 'ready' && (
                            <div style={{ marginTop: '8px' }}>
                              <button
                                onClick={() => {
                                  const confirm1 = window.confirm(
                                    `Mark booking #${booking.bookingNumber} as READY?\n\n` +
                                    `⚠️ This will:\n` +
                                    `  • Mark ALL freelance visa steps as completed\n` +
                                    `  • LOCK the booking — status cannot be changed again\n\n` +
                                    `Are you sure?`
                                  );
                                  if (!confirm1) return;
                                  const confirm2 = window.confirm(
                                    `FINAL CONFIRMATION\n\n` +
                                    `Once marked as Ready, you cannot revert this booking.\n\n` +
                                    `Click OK to proceed, Cancel to abort.`
                                  );
                                  if (!confirm2) return;
                                  axios.patch(
                                    `${BACKEND_URL}/api/admin/bookings/${booking.id}/status`,
                                    { status: 'ready' },
                                    { headers: { 'X-Admin-Key': ADMIN_PASSWORD } }
                                  ).then(() => {
                                    setBookings(prev => prev.map(b => b.id === booking.id ? {
                                      ...b,
                                      status: 'ready',
                                      freelanceStep: 'ready',
                                      isLocked: true,
                                      readyAt: new Date().toISOString(),
                                    } : b));
                                  }).catch(err => alert('Failed: ' + (err.response?.data?.detail || err.message)));
                                }}
                                data-testid="quick-ready-btn"
                                title="Finalize booking — marks all steps complete and locks status"
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '8px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                                  cursor: 'pointer', border: '2px solid #10B981',
                                  background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(0,255,136,0.10))',
                                  color: '#10B981',
                                  boxShadow: '0 2px 8px rgba(16,185,129,0.18)',
                                }}
                              >
                                <CheckCircle size={14} /> Mark as Ready (Final)
                              </button>
                            </div>
                          )}
                          {booking.status === 'ready' && (
                            <div
                              data-testid="ready-locked-indicator"
                              style={{
                                marginTop: '8px',
                                padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                                border: '2px solid #10B981',
                                background: 'rgba(16,185,129,0.18)', color: '#10B981',
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                              }}
                            >
                              <CheckCircle size={14} /> Ready — Locked
                            </div>
                          )}
                          {/* Remaining Balance section */}
                          {(() => {
                            const totalPrice = parseFloat(String(booking.servicePrice || '0').replace(/[^0-9.]/g, '')) || 0;
                            const paidAmount = booking.paidAmount || 0;
                            const remaining = totalPrice - paidAmount;
                            return (
                              <div className="booking-pay-row">
                                {totalPrice > 0 && (
                                  <div className={`pay-pill ${remaining <= 0 ? 'paid' : 'due'}`} data-testid="booking-balance-pill">
                                    <DollarSign size={12} />
                                    {remaining <= 0 ? 'Fully Paid' : `Remaining: ${remaining.toLocaleString()} AED`}
                                  </div>
                                )}
                                {paidAmount > 0 && (
                                  <span style={{ fontSize: '10px', color: '#10B981' }}>
                                    Paid: {paidAmount.toLocaleString()} AED
                                  </span>
                                )}
                                {renderLastPaymentPill(booking.paymentHistory, `fl-${booking.id}`)}
                                <button
                                  onClick={() => {
                                    const totalPrice = parseFloat(String(booking.servicePrice || '0').replace(/[^0-9.]/g, '')) || 0;
                                    setBalanceModal({ bookingId: booking.id, totalPrice, paidAmount: booking.paidAmount || 0 });
                                    setBalanceInput('');
                                  }}
                                  title="Record Payment"
                                  className="pay-btn add"
                                  data-testid="add-payment-btn"
                                >
                                  <DollarSign size={12} /> Add Payment
                                </button>
                                <button
                                  onClick={() => {
                                    setDeductModal({ bookingId: booking.id, currentPrice: totalPrice, bookingNumber: booking.bookingNumber });
                                    setDeductInput('');
                                  }}
                                  title="Deduct from Price"
                                  className="pay-btn deduct"
                                  data-testid="deduct-price-btn"
                                >
                                  <Minus size={12} /> Deduct Price
                                </button>
                              </div>
                            );
                          })()}
                        </>) : (<>
                        <div className="action-group bk-status-actions" data-testid="booking-status-actions">
                        <button
                          className="action-btn checked"
                          onClick={() => updateStatus(booking.id, 'checked')}
                          title="Mark as Checked"
                          disabled={booking.status === 'checked'}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="action-btn doc-required"
                          onClick={() => updateStatus(booking.id, 'document_required')}
                          title="Request Document"
                          disabled={booking.status === 'document_required'}
                        >
                          <AlertCircle size={16} />
                        </button>
                        <button
                          className="action-btn approved"
                          onClick={() => updateStatus(booking.id, 'approved')}
                          title="Approve"
                          disabled={booking.status === 'approved'}
                        >
                          <CheckCircle size={16} />
                        </button>
                        <button
                          className="action-btn rejected"
                          onClick={() => updateStatus(booking.id, 'rejected')}
                          title="Reject"
                          disabled={booking.status === 'rejected'}
                        >
                          <XCircle size={16} />
                        </button>
                        <button
                          className="action-btn pending-payment"
                          onClick={() => updateStatus(booking.id, 'pending_payment')}
                          title="Pending Payment"
                          disabled={booking.status === 'pending_payment'}
                        >
                          <CreditCard size={16} />
                        </button>
                        <button
                          className="action-btn paid"
                          onClick={() => updateStatus(booking.id, 'paid')}
                          title="Mark as Paid"
                          disabled={booking.status === 'paid'}
                        >
                          <DollarSign size={16} />
                        </button>
                        </div>
                        {/* Balance & Deduct for non-freelance */}
                        {(() => {
                          const totalPrice = parseFloat(String(booking.servicePrice || '0').replace(/[^0-9.]/g, '')) || 0;
                          const paidAmount = booking.paidAmount || 0;
                          const remaining = totalPrice - paidAmount;
                          return (
                            <div className="booking-pay-row" data-testid="booking-pay-row">
                              {totalPrice > 0 && (
                                <div className={`pay-pill ${remaining <= 0 ? 'paid' : 'due'}`} data-testid="booking-balance-pill">
                                  <DollarSign size={12} />
                                  {remaining <= 0 ? 'Fully Paid' : `Remaining: ${remaining.toLocaleString()} AED`}
                                </div>
                              )}
                              {renderLastPaymentPill(booking.paymentHistory, `gen-${booking.id}`)}
                              <button
                                onClick={() => {
                                  setBalanceModal({ bookingId: booking.id, totalPrice, paidAmount: booking.paidAmount || 0 });
                                  setBalanceInput('');
                                }}
                                title="Record Payment"
                                className="pay-btn add"
                                data-testid="add-payment-btn-general"
                              >
                                <DollarSign size={12} /> Add Payment
                              </button>
                              <button
                                onClick={() => {
                                  setDeductModal({ bookingId: booking.id, currentPrice: totalPrice, bookingNumber: booking.bookingNumber });
                                  setDeductInput('');
                                }}
                                title="Deduct from Price"
                                className="pay-btn deduct"
                                data-testid="deduct-price-btn-general"
                              >
                                <Minus size={12} /> Deduct Price
                              </button>
                            </div>
                          );
                        })()}
                        </>)}
                        <div className="action-group bk-tool-actions" data-testid="booking-tool-actions">
                        <button
                          className="action-btn view"
                          onClick={() => openEditDetails(booking)}
                          title="Edit Booking Details"
                          data-testid={`edit-booking-details-btn-${booking.id}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="action-btn upload"
                          onClick={() => openUploadModal(booking)}
                          title="Upload Document to User"
                        >
                          <Upload size={16} />
                        </button>
                        <a
                          className="action-btn whatsapp"
                          href={`https://wa.me/${(booking.agentPhone || booking.mobile)?.replace(/\D/g, '')}?text=${encodeURIComponent(
                            `Hello ${booking.name}!\n\nYour booking #${booking.bookingNumber} for ${booking.service} has been ${booking.status}.\n\nRegistan Tourism`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Contact via WhatsApp"
                        >
                          <MessageCircle size={16} />
                        </a>
                        <button
                          className="action-btn email"
                          onClick={() => openEmailComposer(booking.email)}
                          title="Send Email"
                        >
                          <Mail size={16} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => setWarningModal({ bookingId: booking.id, bookingNumber: booking.bookingNumber, customerName: booking.name || booking.fullName, customerEmail: booking.email })}
                          title="Send Warning"
                          style={{ background: 'rgba(255, 165, 0, 0.15)', color: '#FFA500' }}
                          data-testid="warning-btn"
                        >
                          <AlertCircle size={16} />
                        </button>
                        <button
                          className="action-btn"
                          onClick={() => openInvoiceModal(booking)}
                          title="Generate Invoice"
                          style={{ background: 'rgba(0, 184, 159, 0.15)', color: '#00B89F' }}
                          data-testid="invoice-btn"
                        >
                          <Receipt size={16} />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => deleteBooking(booking)}
                          title="Delete Booking"
                        >
                          <Trash2 size={16} />
                        </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}

      {/* Tour Bookings View */}
      {bookingView === 'tour' && (
        <div className="admin-content" data-testid="tour-bookings-view">
          {tourBookings.length === 0 ? (
            <div className="empty-state">
              <Compass size={48} />
              <h3>No tour bookings yet</h3>
              <p>When customers book tours, they will appear here.</p>
            </div>
          ) : (
            <div className="bookings-table-container" style={{ overflowX: 'auto' }}>
              <table className="bookings-table" data-testid="tour-bookings-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Tour</th>
                    <th>Booking Date</th>
                    <th>People</th>
                    <th>Mobile</th>
                    <th>Location</th>
                    <th>Price/Person</th>
                    <th>Total Price</th>
                    <th>Status</th>
                    <th>Booked On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tourBookings
                    .filter(b => {
                      if (!searchQuery) return true;
                      const q = searchQuery.toLowerCase();
                      return (b.guest_name || '').toLowerCase().includes(q) ||
                        (b.tour_title || '').toLowerCase().includes(q) ||
                        (b.mobile || '').includes(q) ||
                        (b.current_location || '').toLowerCase().includes(q);
                    })
                    .map((b) => (
                    <tr key={b.id} data-testid={`tour-booking-row-${b.id}`}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{b.guest_name}</td>
                      <td>{b.tour_title}</td>
                      <td>{b.booking_date || '—'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{b.number_of_people}</td>
                      <td><a href={`tel:${b.agentPhone || b.mobile}`} style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>{b.agentPhone || b.mobile}</a></td>
                      <td>{b.current_location || '—'}</td>
                      <td>{b.price_per_person > 0 ? `${b.price_per_person} AED` : '—'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>{b.total_price > 0 ? `${b.total_price} AED` : '—'}</td>
                      <td>
                        <select value={b.status} onChange={(e) => updateTourBookingStatus(b.id, e.target.value)}
                          data-testid={`tour-booking-status-${b.id}`}
                          style={{
                            padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                            border: '1px solid var(--border-color)', cursor: 'pointer',
                            background: b.status === 'confirmed' ? 'rgba(16, 185, 129, 0.15)' : b.status === 'cancelled' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                            color: b.status === 'confirmed' ? '#10B981' : b.status === 'cancelled' ? '#EF4444' : '#F59E0B'
                          }}>
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <button onClick={() => deleteTourBooking(b)} title="Delete"
                          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#EF4444', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer' }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {bookingView === 'services' && (
        <div className="admin-section" data-testid="bookings-services-view">
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '14px', padding: '16px 18px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Total Value</div>
              <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '6px', color: 'var(--text-primary)' }}>
                {bookingServices.reduce((s, c) => s + Number(c.total_amount || 0), 0).toLocaleString()} AED
              </div>
            </div>
            <div style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.18)', borderRadius: '14px', padding: '16px 18px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#10B981', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Paid</div>
              <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '6px', color: '#10B981' }}>
                {bookingServices.reduce((s, c) => s + Number(c.paid_amount || 0), 0).toLocaleString()} AED
              </div>
            </div>
            <div style={{ background: 'rgba(255,184,0,0.07)', border: '1px solid rgba(255,184,0,0.18)', borderRadius: '14px', padding: '16px 18px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#FFB800', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Remaining</div>
              <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '6px', color: '#FFB800' }}>
                {bookingServices.reduce((s, c) => s + Number(c.remaining || 0), 0).toLocaleString()} AED
              </div>
            </div>
            <div style={{ background: 'rgba(94,252,232,0.07)', border: '1px solid rgba(94,252,232,0.18)', borderRadius: '14px', padding: '16px 18px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#5EFCE8', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Records</div>
              <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '6px', color: '#5EFCE8' }}>{bookingServices.length}</div>
            </div>
          </div>

          {/* Add Service Form (always visible, inline) */}
          <div style={{ background: 'var(--card-bg, #1a2235)', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '14px', padding: '18px 20px', marginBottom: '20px' }} data-testid="services-add-form">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <Plus size={18} style={{ color: '#5EFCE8' }} />
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>Add New Service Record</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Service Name *</label>
                <input type="text" value={bksForm.service} onChange={(e) => setBksForm({ ...bksForm, service: e.target.value })} placeholder="e.g. Freelance Visa, Visa Change" data-testid="bks-service-name"
                  style={{ width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', marginTop: '4px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Date *</label>
                <input type="date" value={bksForm.start_date} onChange={(e) => setBksForm({ ...bksForm, start_date: e.target.value })} data-testid="bks-service-date"
                  style={{ width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', marginTop: '4px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Client Name *</label>
                <input type="text" value={bksForm.client} onChange={(e) => setBksForm({ ...bksForm, client: e.target.value })} placeholder="Client full name" data-testid="bks-service-client"
                  style={{ width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', marginTop: '4px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Client Email</label>
                <input type="email" value={bksForm.client_email} onChange={(e) => setBksForm({ ...bksForm, client_email: e.target.value })} placeholder="client@example.com" data-testid="bks-service-email"
                  style={{ width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', marginTop: '4px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>Service Amount (AED) *</label>
                <input type="number" value={bksForm.total_amount} onChange={(e) => setBksForm({ ...bksForm, total_amount: e.target.value })} placeholder="e.g. 5500" data-testid="bks-service-amount"
                  style={{ width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', marginTop: '4px' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button onClick={createBookingService} disabled={!bksForm.client || !bksForm.service || !bksForm.total_amount} data-testid="bks-service-submit"
                  style={{
                    width: '100%', padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: (!bksForm.client || !bksForm.service || !bksForm.total_amount) ? 'not-allowed' : 'pointer',
                    background: (!bksForm.client || !bksForm.service || !bksForm.total_amount) ? 'rgba(255,255,255,0.06)' : '#5EFCE8',
                    color: (!bksForm.client || !bksForm.service || !bksForm.total_amount) ? 'var(--text-secondary)' : '#0a1628',
                    fontWeight: 700, fontSize: '13px',
                  }}>
                  <Plus size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Add Record
                </button>
              </div>
            </div>
          </div>

          {/* Records list */}
          {loadingBookingServices ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading…</div>
          ) : bookingServices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <Receipt size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>No service records yet. Use the form above to add one.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {bookingServices.map(svc => {
                const startDateLabel = (() => {
                  const raw = svc.start_date || svc.created_at;
                  if (!raw) return null;
                  const d = new Date(raw);
                  if (isNaN(d)) return null;
                  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                })();
                return (
                  <div key={svc.id} data-testid={`bks-service-card-${svc.id}`}
                    style={{
                      background: 'var(--card-bg, #1a2235)', borderRadius: '14px', padding: '16px 20px',
                      border: `1px solid ${svc.status === 'paid' ? 'rgba(94,252,232,0.25)' : svc.status === 'partial' ? 'rgba(255,184,0,0.25)' : 'var(--border-color, rgba(255,255,255,0.08))'}`,
                    }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px' }}>
                      <div style={{ flex: '1 1 250px', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>{svc.client}</span>
                          <span style={{
                            padding: '2px 9px', borderRadius: '6px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                            background: svc.status === 'paid' ? 'rgba(94,252,232,0.12)' : svc.status === 'partial' ? 'rgba(255,184,0,0.12)' : 'rgba(255,255,255,0.06)',
                            color: svc.status === 'paid' ? '#5EFCE8' : svc.status === 'partial' ? '#FFB800' : 'var(--text-secondary)',
                          }}>{svc.status}</span>
                          {startDateLabel && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '2px 9px', borderRadius: '999px', fontSize: '10px', fontWeight: 600,
                              background: 'rgba(96,165,250,0.10)', color: '#93C5FD',
                              border: '1px solid rgba(96,165,250,0.25)',
                            }}>
                              <Calendar size={10} /> {startDateLabel}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{svc.service}</div>
                        {svc.client_email && (
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                            <Mail size={10} style={{ verticalAlign: 'middle', marginRight: '3px' }} />{svc.client_email}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{Number(svc.total_amount).toLocaleString()} AED</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Paid</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#10B981' }}>{Number(svc.paid_amount).toLocaleString()} AED</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#FFB800', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Remaining</div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: svc.remaining > 0 ? '#FFB800' : '#5EFCE8' }}>{Number(svc.remaining).toLocaleString()} AED</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => { setCsPayMode('payment'); setCsPayModal(svc.id); setCsPayForm({ amount: '', date: new Date().toISOString().split('T')[0], invoice_file: null, reason: '' }); }}
                          data-testid={`bks-pay-btn-${svc.id}`}
                          style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', border: 'none', background: '#5EFCE8', color: '#0a1628', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <DollarSign size={12} /> Record
                        </button>
                        <button onClick={() => { setCsPayMode('deduction'); setCsPayModal(svc.id); setCsPayForm({ amount: '', date: new Date().toISOString().split('T')[0], invoice_file: null, reason: '' }); }}
                          data-testid={`bks-deduct-btn-${svc.id}`}
                          style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', background: 'rgba(255,99,71,0.12)', color: '#FF6347', border: '1px solid rgba(255,99,71,0.3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Minus size={12} /> Deduct
                        </button>
                        <button onClick={() => { setExtraModal(svc.id); setExtraForm({ name: '', amount: '', date: new Date().toISOString().split('T')[0] }); }}
                          data-testid={`bks-extra-btn-${svc.id}`}
                          title="Add additional service line (raises total balance)"
                          style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', background: 'rgba(96,165,250,0.12)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Plus size={12} /> Add Extra
                        </button>
                        <button onClick={() => downloadServiceReceipt(svc.id, svc.client)}
                          data-testid={`bks-receipt-btn-${svc.id}`}
                          title="Download branded PDF receipt"
                          style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', background: 'rgba(212,165,116,0.12)', color: '#D4A574', border: '1px solid rgba(212,165,116,0.3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FileText size={12} /> Receipt
                        </button>
                        <button onClick={() => deleteBookingService(svc.id)} data-testid={`bks-del-btn-${svc.id}`}
                          style={{ padding: '8px', borderRadius: '8px', cursor: 'pointer', background: 'rgba(255,77,106,0.1)', border: '1px solid rgba(255,77,106,0.25)', color: '#FF4D6A' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Extras (additional service lines) */}
                    {svc.extras && svc.extras.length > 0 && (
                      <div style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))', marginTop: '14px', paddingTop: '12px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#60A5FA', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Additional Services</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {svc.extras.map((ex, i) => {
                            const dateStr = ex.date ? new Date(ex.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '8px', background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)', fontSize: '12px' }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(96,165,250,0.18)', color: '#60A5FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700 }}>+</div>
                                <div style={{ minWidth: '90px', color: 'var(--text-secondary)' }}>{dateStr}</div>
                                <div style={{ flex: 1, color: 'var(--text-primary)', fontWeight: 600 }}>{ex.name}</div>
                                <div style={{ color: '#60A5FA', fontWeight: 700 }}>+ {Math.abs(Number(ex.amount) || 0).toLocaleString()} AED</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Payment history */}
                    {svc.payments && svc.payments.length > 0 && (
                      <div style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))', marginTop: '14px', paddingTop: '12px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>History</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {svc.payments.map((p, i) => {
                            const isDed = Number(p.amount) < 0;
                            const dateStr = p.date ? new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', fontSize: '12px', flexWrap: 'wrap' }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: isDed ? 'rgba(255,99,71,0.12)' : 'rgba(94,252,232,0.12)', color: isDed ? '#FF6347' : '#5EFCE8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700 }}>{i + 1}</div>
                                <div style={{ minWidth: '90px', color: 'var(--text-secondary)' }}>{dateStr}</div>
                                <div style={{ flex: 1, minWidth: 0, color: isDed ? '#FF6347' : '#5EFCE8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <span>{isDed ? '−' : '+'} {Math.abs(Number(p.amount)).toLocaleString()} AED</span>
                                  {isDed && p.reason && (
                                    <span style={{
                                      fontSize: '10px', fontWeight: 600, color: '#FF8A6E',
                                      background: 'rgba(255,99,71,0.08)', padding: '2px 7px', borderRadius: '5px',
                                      border: '1px solid rgba(255,99,71,0.18)', textTransform: 'none', letterSpacing: 0,
                                    }} title={p.reason}>
                                      Reason: {p.reason.length > 60 ? p.reason.slice(0, 60) + '…' : p.reason}
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{p.type || (isDed ? 'Deduction' : 'Payment')}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

        </>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="admin-content">
          <div className="services-admin-header">
            <h2>Visa Services ({services.length})</h2>
            <button 
              className="btn-add-service"
              onClick={openAddServiceModal}
              data-testid="add-service-btn"
            >
              <Plus size={18} />
              Add Service
            </button>
          </div>
          
          {services.length === 0 ? (
            <div className="empty-state">
              <Briefcase size={48} />
              <h3>No services yet</h3>
              <p>Add your first visa service to get started.</p>
              <button 
                className="btn-primary"
                onClick={openAddServiceModal}
              >
                <Plus size={18} />
                {t('addService')}
              </button>
            </div>
          ) : (
            <div className="services-admin-grid">
              {services.map((service, index) => (
                <div key={service.id} className="service-admin-card" data-testid={`service-card-${service.id}`}>
                  {/* Position controls */}
                  <div className="service-position-controls">
                    <button 
                      className="btn-position"
                      onClick={() => moveService(index, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ChevronUp size={18} />
                    </button>
                    <span className="position-number">{index + 1}</span>
                    <button 
                      className="btn-position"
                      onClick={() => moveService(index, 'down')}
                      disabled={index === services.length - 1}
                      title="Move down"
                    >
                      <ChevronDown size={18} />
                    </button>
                  </div>
                  <div className="service-image-container">
                    {service.image ? (
                      <img 
                        src={getServiceImageUrl(service.image)} 
                        alt={service.name}
                        className="service-image"
                      />
                    ) : (
                      <div className="service-image-placeholder">
                        <Briefcase size={48} />
                      </div>
                    )}
                    <span className="service-category-badge">{service.category || 'Visa'}</span>
                  </div>
                  <div className="service-card-content">
                    <h3 className="service-name">{(() => { const keys = serviceTranslationMap[service.name]; return keys ? t(keys.nameKey) : service.name; })()}</h3>
                    {service.subtitle && <p className="service-subtitle">{service.subtitle}</p>}
                    <p className="service-description">{(() => { const keys = serviceTranslationMap[service.name]; return keys ? t(keys.descKey) : service.description; })()}</p>
                    <div className="service-meta">
                      <span className="service-duration">
                        <Clock size={14} />
                        {service.duration}
                      </span>
                      <span className="service-price">
                        <DollarSign size={14} />
                        {service.price}
                      </span>
                    </div>
                    {service.steps && service.steps.length > 0 && (
                      <div className="service-steps-badge">
                        <List size={14} />
                        {service.steps.length} {t('nSteps')}
                      </div>
                    )}
                    {/* Language badges + Created date */}
                    <div className="service-lang-badges">
                      <span className="lang-badge">🇬🇧 EN</span>
                      {service.name_ru && <span className="lang-badge">🇷🇺 RU</span>}
                      {service.name_uz && <span className="lang-badge">🇺🇿 UZ</span>}
                      {service.createdAt && (() => {
                        const d = new Date(service.createdAt);
                        if (isNaN(d)) return null;
                        const today = new Date();
                        const isToday = d.toDateString() === today.toDateString();
                        const dayMs = 24 * 60 * 60 * 1000;
                        const diffDays = Math.floor((today - d) / dayMs);
                        const isYesterday = diffDays === 1 && !isToday;
                        const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                        const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                        const labelText = isToday
                          ? `Added today · ${timeStr}`
                          : isYesterday
                            ? `Added yesterday`
                            : `Added ${dateStr}`;
                        return (
                          <span
                            data-testid={`service-created-${service.id}`}
                            title={`Created on ${dateStr} at ${timeStr}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              padding: '3px 10px', borderRadius: '999px',
                              fontSize: '10px', fontWeight: '600', letterSpacing: '0.3px',
                              background: isToday
                                ? 'linear-gradient(135deg, rgba(94,252,232,0.20), rgba(94,252,232,0.06))'
                                : 'rgba(96,165,250,0.10)',
                              color: isToday ? '#5EFCE8' : '#93C5FD',
                              border: `1px solid ${isToday ? 'rgba(94,252,232,0.4)' : 'rgba(96,165,250,0.25)'}`,
                              boxShadow: isToday ? '0 0 14px rgba(94,252,232,0.18)' : 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Calendar size={10} style={{ opacity: 0.9 }} />
                            {labelText}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="service-card-actions">
                    <button 
                      className="btn-steps-service"
                      onClick={() => openStepsModal(service)}
                      data-testid={`steps-service-${service.id}`}
                    >
                      <List size={16} />
                      {t('stepsBtn')}
                    </button>
                    <button 
                      className="btn-edit-service"
                      onClick={() => openEditServiceModal(service)}
                      data-testid={`edit-service-${service.id}`}
                    >
                      <Edit size={16} />
                      {t('editBtn')}
                    </button>
                    <button 
                      className="btn-delete-service"
                      onClick={() => deleteService(service)}
                      data-testid={`delete-service-${service.id}`}
                    >
                      <Trash2 size={16} />
                      {t('deleteBtn')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Tours Tab */}
      {activeTab === 'tours' && (
        <div className="admin-content" data-testid="admin-tours-section">
          <div className="services-admin-header">
            <h2><Compass size={22} /> Tours ({tours.length})</h2>
            <button className="btn-add-service" onClick={openAddTourModal} data-testid="add-tour-btn">
              <Plus size={18} /> Add Tour
            </button>
          </div>

          {tours.length === 0 ? (
            <div className="empty-state">
              <Compass size={48} />
              <h3>No tours yet</h3>
              <p>Add your first tour to display on the Tours page.</p>
              <button className="btn-primary" onClick={openAddTourModal}>
                <Plus size={18} /> Add Tour
              </button>
            </div>
          ) : (
            <div className="services-admin-grid">
              {tours.map((tour, index) => (
                <div key={tour.id} className="service-admin-card" data-testid={`tour-card-${tour.id}`}>
                  <div className="service-image-container">
                    {tour.image ? (
                      <img src={getServiceImageUrl(tour.image)} alt={tour.title} className="service-image" />
                    ) : (
                      <div className="service-image-placeholder"><MapPin size={48} /></div>
                    )}
                    {tour.category && <span className="service-category-badge">{tour.category}</span>}
                  </div>
                  <div className="service-card-content">
                    <h3 className="service-name">{tour.title}</h3>
                    <p className="service-description">{tour.description}</p>
                    <div className="service-meta">
                      {tour.duration && (
                        <span className="service-duration"><Clock size={14} /> {tour.duration}</span>
                      )}
                      {tour.price > 0 && (
                        <span className="service-duration"><DollarSign size={14} /> {tour.price} AED / person</span>
                      )}
                    </div>
                    {tour.highlights && tour.highlights.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                        {tour.highlights.map((h, i) => (
                          <span key={i} style={{
                            padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                            background: 'rgba(91, 141, 239, 0.15)', color: 'var(--accent-color, #5B8DEF)',
                            border: '1px solid rgba(91, 141, 239, 0.3)'
                          }}>{h}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="service-card-actions">
                    <button className="btn-edit-service" onClick={() => openEditTourModal(tour)} data-testid={`edit-tour-${tour.id}`}>
                      <Edit size={16} /> Edit
                    </button>
                    <button className="btn-delete-service" onClick={() => deleteTour(tour)} data-testid={`delete-tour-${tour.id}`}>
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {tourModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setTourModal(null); }}>
          <div className="service-modal" data-testid="tour-modal">
            <div className="modal-header">
              <h2>{tourModal === 'add' ? 'Add New Tour' : 'Edit Tour'}</h2>
              <button className="btn-close" onClick={() => setTourModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input type="text" value={tourForm.title} onChange={(e) => setTourForm({ ...tourForm, title: e.target.value })}
                  placeholder="e.g., Dubai Desert Safari" data-testid="tour-title-input" />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input type="text" value={tourForm.category} onChange={(e) => setTourForm({ ...tourForm, category: e.target.value })}
                  placeholder="e.g., Adventure, Sightseeing, Water Sports" data-testid="tour-category-input" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={tourForm.description} onChange={(e) => setTourForm({ ...tourForm, description: e.target.value })}
                  placeholder="Describe the tour experience..." rows={3} data-testid="tour-description-input" />
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Duration</label>
                  <input type="text" value={tourForm.duration} onChange={(e) => setTourForm({ ...tourForm, duration: e.target.value })}
                    placeholder="e.g., 6 Hours" data-testid="tour-duration-input" />
                </div>
                <div className="form-group">
                  <label>Price per Person (AED)</label>
                  <input type="number" min="0" value={tourForm.price} onChange={(e) => setTourForm({ ...tourForm, price: e.target.value })}
                    placeholder="e.g., 250" data-testid="tour-price-input" />
                </div>
                <div className="form-group">
                  <label>Highlights (comma-separated)</label>
                  <input type="text" value={tourForm.highlights} onChange={(e) => setTourForm({ ...tourForm, highlights: e.target.value })}
                    placeholder="Dune Bashing, Camel Ride, BBQ" data-testid="tour-highlights-input" />
                </div>
              </div>
              <div className="form-group">
                <label>Tour Image</label>
                <div className="image-upload-area" onClick={() => tourImageInputRef.current?.click()} style={{ cursor: 'pointer' }}>
                  {tourImagePreview ? (
                    <img src={tourImagePreview} alt="Preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }} />
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={32} />
                      <p>Click to upload image</p>
                    </div>
                  )}
                  <input type="file" ref={tourImageInputRef} onChange={handleTourImageSelect} accept="image/*" style={{ display: 'none' }} data-testid="tour-image-input" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setTourModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={saveTour} disabled={savingTour || !tourForm.title} data-testid="tour-save-btn">
                {savingTour ? 'Saving...' : (<><CheckCircle size={18} /> {tourModal === 'add' ? 'Add Tour' : 'Save Changes'}</>)}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Team Tab */}
      {activeTab === 'team' && (
        <div className="admin-content" data-testid="admin-team-section">
          <div className="services-admin-header">
            <h2><Users size={22} /> Team ({teamMembers.length})</h2>
            <button className="btn-add-service" onClick={openAddTeamModal} data-testid="add-team-btn">
              <Plus size={18} /> Add Member
            </button>
          </div>

          {teamMembers.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>No team members yet</h3>
              <p>Add your team members to showcase on the website.</p>
              <button className="btn-primary" onClick={openAddTeamModal}>
                <Plus size={18} /> Add Member
              </button>
            </div>
          ) : (
            <div className="services-admin-grid">
              {teamMembers.map((member, index) => (
                <div key={member.id} className="service-admin-card" data-testid={`team-card-${member.id}`}>
                  {/* Position controls */}
                  <div className="service-position-controls">
                    <button className="btn-position" onClick={() => moveTeamMember(index, 'up')} disabled={index === 0} title="Move up">
                      <ChevronUp size={18} />
                    </button>
                    <span className="position-number">{index + 1}</span>
                    <button className="btn-position" onClick={() => moveTeamMember(index, 'down')} disabled={index === teamMembers.length - 1} title="Move down">
                      <ChevronDown size={18} />
                    </button>
                  </div>
                  <div className="service-image-container">
                    {member.image ? (
                      <img src={getServiceImageUrl(member.image)} alt={member.name} className="service-image" style={{ objectFit: 'cover' }} />
                    ) : (
                      <div className="service-image-placeholder"><User size={48} /></div>
                    )}
                    {member.position_title && <span className="service-category-badge">{member.position_title}</span>}
                  </div>
                  <div className="service-card-content">
                    <h3 className="service-name">{member.name}</h3>
                    {member.description && <p className="service-description">{member.description}</p>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px', fontSize: '13px' }}>
                      {member.position_title && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                          <Briefcase size={14} style={{ flexShrink: 0 }} />
                          <span style={{ fontWeight: '600', minWidth: '60px' }}>Position:</span>
                          <span style={{ color: 'var(--text-primary)' }}>{member.position_title}</span>
                        </div>
                      )}
                      {member.mobile && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                          <Phone size={14} style={{ flexShrink: 0 }} />
                          <span style={{ fontWeight: '600', minWidth: '60px' }}>Mobile:</span>
                          <span style={{ color: 'var(--text-primary)' }}>{member.mobile}</span>
                        </div>
                      )}
                      {member.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                          <Mail size={14} style={{ flexShrink: 0 }} />
                          <span style={{ fontWeight: '600', minWidth: '60px' }}>Email:</span>
                          <span style={{ color: 'var(--text-primary)' }}>{member.email}</span>
                        </div>
                      )}
                      {member.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                          <MapPin size={14} style={{ flexShrink: 0 }} />
                          <span style={{ fontWeight: '600', minWidth: '60px' }}>Location:</span>
                          <span style={{ color: 'var(--text-primary)' }}>{member.location}</span>
                        </div>
                      )}
                    </div>
                    {(member.tiktok || member.instagram) && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                        {member.instagram && (
                          <a href={member.instagram.startsWith('http') ? member.instagram : `https://instagram.com/${member.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: 'rgba(225, 48, 108, 0.15)', color: '#E1306C', border: '1px solid rgba(225, 48, 108, 0.3)', textDecoration: 'none' }}>
                            Instagram
                          </a>
                        )}
                        {member.tiktok && (
                          <a href={member.tiktok.startsWith('http') ? member.tiktok : `https://tiktok.com/@${member.tiktok.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: 'rgba(0, 0, 0, 0.15)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', textDecoration: 'none' }}>
                            TikTok
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="service-card-actions">
                    <button className="btn-edit-service" onClick={() => openEditTeamModal(member)} data-testid={`edit-team-${member.id}`}>
                      <Edit size={16} /> Edit
                    </button>
                    <button className="btn-delete-service" onClick={() => deleteTeamMember(member)} data-testid={`delete-team-${member.id}`}>
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Team Add/Edit Modal */}
      {teamModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setTeamModal(null); }}>
          <div className="service-modal" data-testid="team-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>{teamModal === 'add' ? 'Add Team Member' : 'Edit Team Member'}</h2>
              <button className="btn-close" onClick={() => setTeamModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input type="text" value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                  placeholder="Full name" data-testid="team-name-input" />
              </div>
              <div className="form-group">
                <label>🇬🇧 Position / Title</label>
                <input type="text" value={teamForm.position_title} onChange={(e) => setTeamForm({ ...teamForm, position_title: e.target.value })}
                  placeholder="e.g., CEO, Visa Consultant, Tour Guide" data-testid="team-position-input" />
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>🇷🇺 Position / Title (RU)</label>
                  <input type="text" value={teamForm.position_title_ru} onChange={(e) => setTeamForm({ ...teamForm, position_title_ru: e.target.value })}
                    placeholder="напр., Генеральный директор" data-testid="team-position-ru-input" />
                </div>
                <div className="form-group">
                  <label>🇺🇿 Position / Title (UZ)</label>
                  <input type="text" value={teamForm.position_title_uz} onChange={(e) => setTeamForm({ ...teamForm, position_title_uz: e.target.value })}
                    placeholder="masalan, Bosh direktor" data-testid="team-position-uz-input" />
                </div>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>Mobile Number</label>
                  <input type="text" value={teamForm.mobile} onChange={(e) => setTeamForm({ ...teamForm, mobile: e.target.value })}
                    placeholder="+971 XX XXX XXXX" data-testid="team-mobile-input" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={teamForm.email} onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })}
                    placeholder="name@registantours.com" data-testid="team-email-input" />
                </div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" value={teamForm.location} onChange={(e) => setTeamForm({ ...teamForm, location: e.target.value })}
                  placeholder="e.g., Dubai, UAE" data-testid="team-location-input" />
              </div>
              <div className="form-group">
                <label>🇬🇧 Description</label>
                <textarea value={teamForm.description} onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
                  placeholder="Short bio or description..." rows={3} data-testid="team-description-input" />
              </div>
              <div className="form-group">
                <label>🇷🇺 Description (RU)</label>
                <textarea value={teamForm.description_ru} onChange={(e) => setTeamForm({ ...teamForm, description_ru: e.target.value })}
                  placeholder="Краткая биография..." rows={3} data-testid="team-description-ru-input" />
              </div>
              <div className="form-group">
                <label>🇺🇿 Description (UZ)</label>
                <textarea value={teamForm.description_uz} onChange={(e) => setTeamForm({ ...teamForm, description_uz: e.target.value })}
                  placeholder="Qisqa biografiya..." rows={3} data-testid="team-description-uz-input" />
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>TikTok</label>
                  <input type="text" value={teamForm.tiktok} onChange={(e) => setTeamForm({ ...teamForm, tiktok: e.target.value })}
                    placeholder="@username or full URL" data-testid="team-tiktok-input" />
                </div>
                <div className="form-group">
                  <label>Instagram</label>
                  <input type="text" value={teamForm.instagram} onChange={(e) => setTeamForm({ ...teamForm, instagram: e.target.value })}
                    placeholder="@username or full URL" data-testid="team-instagram-input" />
                </div>
              </div>
              <div className="form-group">
                <label>Photo</label>
                <div className="image-upload-area" onClick={() => teamImageInputRef.current?.click()} style={{ cursor: 'pointer' }}>
                  {teamImagePreview ? (
                    <img src={teamImagePreview} alt="Preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }} />
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={32} />
                      <p>Click to upload photo</p>
                    </div>
                  )}
                  <input type="file" ref={teamImageInputRef} onChange={handleTeamImageSelect} accept="image/*" style={{ display: 'none' }} data-testid="team-image-input" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setTeamModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={saveTeamMember} disabled={savingTeam || !teamForm.name} data-testid="team-save-btn">
                {savingTeam ? 'Saving...' : (<><CheckCircle size={18} /> {teamModal === 'add' ? 'Add Member' : 'Save Changes'}</>)}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Text Sections Tab */}
      {activeTab === 'text-sections' && (
        <div className="admin-content">
          <div className="text-sections-header">
            <h2>Text Sections ({textSections.length})</h2>
            <button 
              className="btn-add-text-section"
              onClick={openAddTextSectionModal}
              data-testid="add-text-section-btn"
            >
              <Plus size={18} />
              Add Text Section
            </button>
          </div>
          
          <p className="text-sections-info">
            Add text sections with decorative lines that appear on your website. Use these for announcements, promotions, or important messages.
          </p>
          
          {textSections.length === 0 ? (
            <div className="empty-state">
              <Type size={48} />
              <h3>No text sections yet</h3>
              <p>Add your first text section to display on the website.</p>
              <button 
                className="btn-primary"
                onClick={openAddTextSectionModal}
              >
                <Plus size={18} />
                Add Text Section
              </button>
            </div>
          ) : (
            <div className="text-sections-list">
              {textSections.map((section, index) => (
                <div 
                  key={section.id} 
                  className={`text-section-card ${!section.isActive ? 'inactive' : ''}`}
                  data-testid={`text-section-${section.id}`}
                >
                  <div className="text-section-position-controls">
                    <button 
                      className="btn-position"
                      onClick={() => moveTextSection(index, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <ChevronUp size={18} />
                    </button>
                    <span className="position-number">{index + 1}</span>
                    <button 
                      className="btn-position"
                      onClick={() => moveTextSection(index, 'down')}
                      disabled={index === textSections.length - 1}
                      title="Move down"
                    >
                      <ChevronDown size={18} />
                    </button>
                  </div>
                  
                  <div className="text-section-preview">
                    <div className="text-section-line"></div>
                    <span className="text-section-text">{section.text_en || section.text}</span>
                    <div className="text-section-line"></div>
                    <span className="placement-badge">
                      {section.placement === 'stories' ? '📸 Above Stories' : 
                       section.placement === 'services' ? '🛂 Above Services' : 
                       section.placement === 'reviews' ? '⭐ Above Reviews' : '📸 Above Stories'}
                    </span>
                    {section.text_ru && <span className="lang-badge">🇷🇺 RU</span>}
                    {section.text_uz && <span className="lang-badge">🇺🇿 UZ</span>}
                  </div>
                  
                  <div className="text-section-actions">
                    <button 
                      className={`btn-toggle ${section.isActive ? 'active' : ''}`}
                      onClick={() => toggleTextSectionActive(section)}
                      title={section.isActive ? 'Click to hide' : 'Click to show'}
                    >
                      {section.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                      {section.isActive ? 'Active' : 'Hidden'}
                    </button>
                    <button 
                      className="btn-edit-text-section"
                      onClick={() => openEditTextSectionModal(section)}
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button 
                      className="btn-delete-text-section"
                      onClick={() => deleteTextSection(section)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text Section Modal (Add/Edit) */}
      {textSectionModal && (
        <div className="modal-overlay" onClick={closeTextSectionModal}>
          <div className="text-section-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeTextSectionModal}>
              <X size={24} />
            </button>
            
            <div className="text-section-modal-header">
              <Type size={32} className="text-section-icon" />
              <h2>{textSectionModal === 'add' ? 'Add Text Section' : 'Edit Text Section'}</h2>
            </div>

            <div className="text-section-modal-content">
              <div className="form-group">
                <label>🇬🇧 English Text *</label>
                <input
                  type="text"
                  value={textSectionForm.text_en}
                  onChange={(e) => setTextSectionForm(prev => ({ ...prev, text_en: e.target.value }))}
                  placeholder="e.g., Special Offer: 20% Off All Visas!"
                  data-testid="text-section-input-en"
                />
              </div>

              <div className="form-group">
                <label>🇷🇺 Russian Text</label>
                <input
                  type="text"
                  value={textSectionForm.text_ru}
                  onChange={(e) => setTextSectionForm(prev => ({ ...prev, text_ru: e.target.value }))}
                  placeholder="e.g., Специальное предложение: скидка 20%!"
                  data-testid="text-section-input-ru"
                />
              </div>

              <div className="form-group">
                <label>🇺🇿 Uzbek Text</label>
                <input
                  type="text"
                  value={textSectionForm.text_uz}
                  onChange={(e) => setTextSectionForm(prev => ({ ...prev, text_uz: e.target.value }))}
                  placeholder="e.g., Maxsus taklif: 20% chegirma!"
                  data-testid="text-section-input-uz"
                />
              </div>

              <div className="form-group">
                <label>Display Location *</label>
                <select
                  value={textSectionForm.placement}
                  onChange={(e) => setTextSectionForm(prev => ({ ...prev, placement: e.target.value }))}
                  className="placement-select"
                  data-testid="text-section-placement"
                >
                  <option value="stories">Above Stories</option>
                  <option value="services">Above Services</option>
                  <option value="reviews">Above Reviews</option>
                </select>
              </div>

              <div className="form-group">
                <label>Preview (English)</label>
                <div className="text-section-preview-large">
                  <div className="text-section-line"></div>
                  <span className="text-section-text">
                    {textSectionForm.text_en || 'Your text will appear here'}
                  </span>
                  <div className="text-section-line"></div>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={textSectionForm.isActive}
                    onChange={(e) => setTextSectionForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Show on website
                </label>
              </div>
            </div>

            <div className="text-section-modal-actions">
              <button className="btn-secondary" onClick={closeTextSectionModal}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={saveTextSection}
                disabled={savingTextSection}
                data-testid="save-text-section-btn"
              >
                {savingTextSection ? 'Saving...' : (
                  <>
                    <CheckCircle size={18} />
                    {textSectionModal === 'add' ? 'Add Section' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stories Tab */}
      {activeTab === 'stories' && (
        <div className="admin-content">
          <div className="reviews-admin-header">
            <h2>Stories ({stories.length})</h2>
            <button 
              className="btn-add-review"
              onClick={() => openStoryModal('add')}
              data-testid="add-story-btn"
            >
              <Plus size={18} />
              Add Story
            </button>
          </div>
          
          <p className="stories-info">
            <Film size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Each story can have up to 10 images/videos. Images: 5 sec, Videos: 20 sec with sound.
          </p>
          
          {stories.length === 0 ? (
            <div className="empty-state">
              <Film size={48} />
              <h3>No stories yet</h3>
              <p>Create a story with up to 10 images or videos.</p>
              <button 
                className="btn-primary"
                onClick={() => openStoryModal('add')}
              >
                <Plus size={18} />
                Add First Story
              </button>
            </div>
          ) : (
            <div className="stories-admin-list">
              {stories.map((story) => (
                <div key={story.id} className="story-admin-item" data-testid={`story-card-${story.id}`}>
                  <div className="story-admin-header-row">
                    <div className="story-admin-title-section">
                      <h4>{story.title || 'Untitled Story'}</h4>
                      <span className="story-media-count">{story.media?.length || 0}/10 slides</span>
                      {!story.active && <span className="story-inactive-tag">Inactive</span>}
                    </div>
                    <div className="story-admin-actions">
                      <label className="btn-add-media">
                        <Plus size={14} />
                        Add Media
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={(e) => handleAddMoreMedia(e, story.id)}
                          style={{ display: 'none' }}
                          disabled={addingMedia || (story.media?.length || 0) >= 10}
                        />
                      </label>
                      <button 
                        className="btn-edit-small"
                        onClick={() => openStoryModal(story)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn-delete-small"
                        onClick={() => handleDeleteStory(story.id)}
                        title="Delete Story"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="story-media-grid">
                    {(story.media || []).map((media, index) => (
                      <div key={index} className="story-media-item">
                        {media.type === 'video' ? (
                          <video src={getServiceImageUrl(media.url)} className="story-media-thumb" />
                        ) : (
                          <img 
                            src={getServiceImageUrl(media.url)} 
                            alt={`Slide ${index + 1}`} 
                            className="story-media-thumb"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%2310b981" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="white" font-size="20">{index+1}</text></svg>';
                            }}
                          />
                        )}
                        <div className="story-media-overlay">
                          <span className="media-type-badge">
                            {media.type === 'video' ? <><Play size={10} /> {media.duration}s</> : <><Image size={10} /> {media.duration}s</>}
                          </span>
                          <button 
                            className="media-delete-btn"
                            onClick={() => handleDeleteMediaItem(story.id, index)}
                            title="Delete this slide"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <span className="media-index">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Story Modal */}
      {storyModal && (
        <div className="modal-overlay" onClick={() => setStoryModal(null)}>
          <div className="modal-content service-modal story-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{storyModal === 'add' ? 'Create New Story' : 'Edit Story'}</h3>
              <button className="modal-close" onClick={() => setStoryModal(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Story Title *</label>
                <input
                  type="text"
                  value={storyForm.title}
                  onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })}
                  placeholder="e.g., Dubai Special Offers, Jet Ski Tours..."
                />
              </div>
              
              {storyModal === 'add' && (
                <div className="form-group">
                  <label>Upload Media (up to 10 images/videos)</label>
                  <div className="multi-upload-area" onClick={() => storyMediaInputRef.current?.click()}>
                    {storyMediaPreviews.length > 0 ? (
                      <div className="multi-preview-grid">
                        {storyMediaPreviews.map((preview, index) => (
                          <div key={index} className="preview-item">
                            {preview.type === 'video' ? (
                              <video src={preview.url} className="preview-thumb" />
                            ) : (
                              <img src={preview.url} alt={`Preview ${index + 1}`} className="preview-thumb" />
                            )}
                            <span className="preview-index">{index + 1}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <Upload size={32} />
                        <p>Click to select images or videos</p>
                        <small>Hold Ctrl/Cmd to select multiple files (max 10)</small>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={storyMediaInputRef}
                    onChange={handleStoryMediaSelect}
                    accept="image/*,video/*"
                    multiple
                    style={{ display: 'none' }}
                  />
                  {storyMediaPreviews.length > 0 && (
                    <p className="upload-count">{storyMediaPreviews.length} file(s) selected</p>
                  )}
                </div>
              )}
              
              {storyModal !== 'add' && (
                <>
                  <div className="form-group">
                    <label>Current Media ({storyModal.media?.length || 0}/10)</label>
                    <div className="existing-media-grid">
                      {(storyModal.media || []).map((media, index) => (
                        <div key={index} className="existing-media-item">
                          {media.type === 'video' ? (
                            <video src={getServiceImageUrl(media.url)} className="existing-media-thumb" />
                          ) : (
                            <img src={getServiceImageUrl(media.url)} alt={`Slide ${index + 1}`} className="existing-media-thumb" />
                          )}
                          <span className="existing-media-index">{index + 1}</span>
                        </div>
                      ))}
                    </div>
                    <p className="media-hint">Use "Add Media" button on the story card to add more slides</p>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={storyForm.active}
                        onChange={(e) => setStoryForm({ ...storyForm, active: e.target.checked })}
                      />
                      Active (visible to users)
                    </label>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setStoryModal(null)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveStory}
                disabled={savingStory}
              >
                {savingStory ? 'Saving...' : (storyModal === 'add' ? 'Create Story' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div className="admin-content">
          <div className="reviews-admin-header">
            <h2>Customer Reviews ({reviews.length})</h2>
            <button 
              className="btn-add-review"
              onClick={openAddReviewModal}
              data-testid="add-review-btn"
            >
              <Plus size={18} />
              Add Review
            </button>
          </div>
          
          {reviews.length === 0 ? (
            <div className="empty-state">
              <Star size={48} />
              <h3>No reviews yet</h3>
              <p>Add your first customer review.</p>
              <button 
                className="btn-primary"
                onClick={openAddReviewModal}
              >
                <Plus size={18} />
                Add Review
              </button>
            </div>
          ) : (
            <div className="reviews-admin-list">
              {reviews.map((review) => (
                <div key={review.id} className="review-admin-card">
                  <div className="review-admin-header">
                    <div className="review-author-info">
                      {review.avatar_url ? (
                        <div className="review-media-removable">
                          <img src={review.avatar_url.startsWith('http') ? review.avatar_url : `${BACKEND_URL}${review.avatar_url}`} alt={review.author} className="review-avatar has-img" onError={(e) => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                          <button className="btn-remove-media-x" title="Remove avatar" onClick={() => removeReviewMedia(review.id, 'avatar')}>
                            <X size={10} />
                          </button>
                        </div>
                      ) : null}
                      <div className="review-avatar" style={{ display: review.avatar_url ? 'none' : 'flex' }}>{review.author?.charAt(0)?.toUpperCase()}</div>
                      <div>
                        <h4>{review.author}</h4>
                        <p className="review-email">{review.userEmail || (review.isAdminCreated ? 'Admin Created' : 'N/A')}</p>
                      </div>
                    </div>
                    <div className="review-meta">
                      <div className="review-stars">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            size={16} 
                            fill={i < review.rating ? '#FFD700' : 'none'}
                            color={i < review.rating ? '#FFD700' : '#666'}
                          />
                        ))}
                      </div>
                      <span className="review-date">{review.date}</span>
                    </div>
                  </div>
                  <p className="review-text">{review.text}</p>
                  {(review.image_url || review.video_url || review.image) && (
                    <div style={{marginTop: '10px', marginBottom: '10px'}}>
                      {review.video_url ? (
                        <div className="review-media-removable inline-block">
                          <video 
                            src={review.video_url.startsWith('http') ? review.video_url : `${BACKEND_URL}${review.video_url}`}
                            controls
                            controlsList="nodownload"
                            disablePictureInPicture
                            onContextMenu={(e) => e.preventDefault()}
                            preload="metadata"
                            style={{maxWidth: '240px', maxHeight: '160px', borderRadius: '8px', background: '#000'}}
                          />
                          <button className="btn-remove-media" title="Remove video" onClick={() => removeReviewMedia(review.id, 'video')}>
                            <X size={12} /> Remove Video
                          </button>
                        </div>
                      ) : (
                        <div className="review-media-removable inline-block">
                          <img 
                            src={(() => {
                              const url = review.image_url || review.image;
                              return url.startsWith('http') ? url : `${BACKEND_URL}/api/uploads/${url}`;
                            })()}
                            alt={`${review.author}'s review`}
                            style={{maxWidth: '240px', maxHeight: '160px', objectFit: 'cover', borderRadius: '8px'}}
                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                          />
                          <button className="btn-remove-media" title="Remove image" onClick={() => removeReviewMedia(review.id, 'image')}>
                            <X size={12} /> Remove Image
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="review-admin-actions">
                    <button 
                      className="btn-edit-review"
                      onClick={() => openEditReviewModal(review)}
                      data-testid={`edit-review-${review.id}`}
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button 
                      className="btn-delete-review"
                      onClick={() => deleteReview(review.id)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Review Modal (Add/Edit) */}
      {reviewModal && (
        <div className="modal-overlay" onClick={closeReviewModal}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeReviewModal}>
              <X size={24} />
            </button>
            
            <div className="review-modal-header">
              <Star size={32} className="review-icon" />
              <h2>{reviewModal === 'add' ? 'Add New Review' : 'Edit Review'}</h2>
            </div>

            <div className="review-modal-content">
              <div className="review-form-grid">
                {/* Avatar + Author Name inline */}
                <div className="form-group full-width">
                  <label>Author Name *</label>
                  <div className="author-with-avatar">
                    {reviewAvatarPreview ? (
                      <div className="admin-avatar-circle has-image" data-testid="admin-avatar-preview">
                        <img src={reviewAvatarPreview} alt="Avatar" />
                        <button type="button" className="avatar-remove-btn" onClick={() => { setReviewAvatar(null); setReviewAvatarPreview(null); }}>
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <label className="admin-avatar-circle empty" data-testid="admin-avatar-upload">
                        <Camera size={20} />
                        <input type="file" accept="image/*" onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
                          setReviewAvatar(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setReviewAvatarPreview(reader.result);
                          reader.readAsDataURL(file);
                        }} style={{ display: 'none' }} />
                      </label>
                    )}
                    <input
                      type="text"
                      value={reviewForm.author}
                      onChange={(e) => handleReviewFormChange('author', e.target.value)}
                      placeholder="Customer name"
                      data-testid="review-author-input"
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Rating *</label>
                  <div className="star-rating-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= reviewForm.rating ? 'active' : ''}`}
                        onClick={() => handleReviewFormChange('rating', star)}
                      >
                        <Star 
                          size={28} 
                          fill={star <= reviewForm.rating ? '#FFD700' : 'none'}
                          color={star <= reviewForm.rating ? '#FFD700' : '#666'}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Review Text *</label>
                  <textarea
                    value={reviewForm.text}
                    onChange={(e) => handleReviewFormChange('text', e.target.value)}
                    placeholder="Write the customer review..."
                    rows="4"
                    data-testid="review-text-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Attachment Image (Optional)</label>
                  <div className="review-image-upload">
                    {reviewImagePreview ? (
                      <div className="image-preview-container">
                        <img src={reviewImagePreview} alt="Preview" className="image-preview" />
                        <button className="btn-remove-image" onClick={() => { setReviewImage(null); setReviewImagePreview(null); if (reviewImageInputRef.current) reviewImageInputRef.current.value = ''; }}>
                          <X size={16} /> Remove
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-zone" onClick={() => reviewImageInputRef.current?.click()} style={{padding: '12px'}}>
                        <Upload size={24} />
                        <p>Upload image</p>
                        <span>JPG, PNG (max 10MB)</span>
                      </div>
                    )}
                    <input ref={reviewImageInputRef} type="file" accept="image/*" onChange={handleReviewImageSelect} style={{ display: 'none' }} />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Attachment Video (Optional)</label>
                  <div className="review-image-upload">
                    {reviewVideoPreview ? (
                      <div className="image-preview-container">
                        <video src={reviewVideoPreview} controls style={{maxWidth: '200px', maxHeight: '140px', borderRadius: '8px'}} />
                        <button className="btn-remove-image" onClick={() => { setReviewVideo(null); setReviewVideoPreview(null); }}>
                          <X size={16} /> Remove
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-zone" onClick={() => document.getElementById('admin-video-input')?.click()} style={{padding: '12px'}}>
                        <Video size={24} />
                        <p>Upload video</p>
                        <span>MP4, MOV (max 50MB)</span>
                      </div>
                    )}
                    <input id="admin-video-input" type="file" accept="video/*" onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 50 * 1024 * 1024) { alert('Max 50MB'); return; }
                      setReviewVideo(file);
                      setReviewVideoPreview(URL.createObjectURL(file));
                    }} style={{ display: 'none' }} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Instagram Username (Optional)</label>
                  <input
                    type="text"
                    value={reviewForm.instagram}
                    onChange={(e) => handleReviewFormChange('instagram', e.target.value)}
                    placeholder="@username"
                    data-testid="review-instagram-input"
                  />
                </div>

                <div className="form-group">
                  <label>TikTok Username (Optional)</label>
                  <input
                    type="text"
                    value={reviewForm.tiktok}
                    onChange={(e) => handleReviewFormChange('tiktok', e.target.value)}
                    placeholder="@username"
                    data-testid="review-tiktok-input"
                  />
                </div>
              </div>
            </div>

            <div className="review-modal-actions">
              <button className="btn-secondary" onClick={closeReviewModal}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={saveReview}
                disabled={savingReview}
                data-testid="save-review-btn"
              >
                {savingReview ? 'Saving...' : (
                  <>
                    <CheckCircle size={18} />
                    {reviewModal === 'add' ? 'Add Review' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {uploadModal && (
        <div className="modal-overlay" onClick={closeUploadModal}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeUploadModal}>
              <X size={24} />
            </button>
            
            <div className="upload-modal-header">
              <Upload size={32} className="upload-icon" />
              <h2>Send Document to User</h2>
              <p>Upload a document for booking #{uploadModal.bookingNumber}</p>
              <p className="customer-info">Customer: {uploadModal.name}</p>
            </div>

            <div className="upload-modal-content">
              {!uploadFile ? (
                <div 
                  className="upload-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={48} />
                  <p>Click to upload document</p>
                  <span>PDF, JPG, PNG (max 10MB)</span>
                </div>
              ) : (
                <div className="upload-preview">
                  {uploadPreview ? (
                    <img src={uploadPreview} alt="Preview" className="preview-image" />
                  ) : (
                    <div className="file-info">
                      <FileText size={48} />
                      <span>{uploadFile.name}</span>
                    </div>
                  )}
                  <button 
                    className="btn-remove-file"
                    onClick={() => {
                      setUploadFile(null);
                      setUploadPreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    <X size={16} /> Remove
                  </button>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <div className="location-link-field" data-testid="location-link-field">
                <label><MapPin size={15} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '5px' }} />Location Link (Optional)</label>
                <input
                  type="url"
                  value={locationLink}
                  onChange={(e) => setLocationLink(e.target.value)}
                  placeholder="Paste Google Maps link here..."
                  className="location-link-input"
                  data-testid="location-link-input"
                />
              </div>
            </div>

            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={closeUploadModal}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleAdminUpload}
                disabled={!uploadFile || uploading}
              >
                {uploading ? 'Uploading...' : (
                  <>
                    <Send size={18} />
                    Send to User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Tab */}
      {activeTab === 'storage' && (
        <div className="admin-content" data-testid="storage-view" style={{ padding: '24px' }}>
          {!storageStats ? (
            <div className="empty-state"><HardDrive size={48} /><h3>Loading storage data...</h3></div>
          ) : (
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <HardDrive size={24} style={{ color: '#8B5CF6' }} /> Supabase Storage
                </h2>
                <button onClick={loadStorageStats} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(139, 92, 246, 0.3)',
                  background: 'rgba(139, 92, 246, 0.12)', color: '#8B5CF6'
                }} data-testid="refresh-storage-btn">
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>

              {/* Total Storage Card */}
              <div style={{
                padding: '24px', borderRadius: '16px', marginBottom: '24px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.1))',
                border: '1px solid rgba(139, 92, 246, 0.25)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Storage Used</div>
                    <div style={{ fontSize: '36px', fontWeight: 800, color: '#8B5CF6' }}>
                      {storageStats.totalSize >= 1073741824
                        ? (storageStats.totalSize / 1073741824).toFixed(2) + ' GB'
                        : (storageStats.totalSize / (1024 * 1024)).toFixed(1) + ' MB'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Files</div>
                    <div style={{ fontSize: '36px', fontWeight: 800, color: '#3B82F6' }}>
                      {storageStats.totalFiles}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Bucket</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {storageStats.bucket}
                    </div>
                  </div>
                </div>
              </div>

              {/* Folder Breakdown */}
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>
                Storage by Folder
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
                {storageStats.folders && Object.entries(storageStats.folders)
                  .sort(([,a], [,b]) => b.size - a.size)
                  .map(([folder, data]) => {
                    const pct = storageStats.totalSize > 0 ? ((data.size / storageStats.totalSize) * 100) : 0;
                    const folderColors = {
                      bookings: '#F59E0B', stories: '#EC4899', reviews: '#10B981',
                      profiles: '#3B82F6', team: '#8B5CF6', tours: '#06B6D4', root: '#6B7280'
                    };
                    const color = folderColors[folder] || '#8B5CF6';
                    return (
                      <div key={folder} style={{
                        padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)',
                        background: 'var(--card-bg, rgba(255,255,255,0.04))'
                      }} data-testid={`storage-folder-${folder}`}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700, color, textTransform: 'capitalize' }}>
                            {folder}
                          </span>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {data.files} files
                          </span>
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>
                          {data.size >= 1048576
                            ? (data.size / (1024 * 1024)).toFixed(1) + ' MB'
                            : (data.size / 1024).toFixed(1) + ' KB'}
                        </div>
                        <div style={{ width: '100%', height: '6px', borderRadius: '3px', background: 'var(--border-color)', overflow: 'hidden' }}>
                          <div style={{ width: `${Math.max(2, pct)}%`, height: '100%', borderRadius: '3px', background: color, transition: 'width 0.5s ease' }} />
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>{pct.toFixed(1)}% of total</div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Local Server Files Section */}
          <div style={{ maxWidth: '900px', margin: '32px auto 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <HardDrive size={24} style={{ color: '#F59E0B' }} /> Server Files
              </h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedLocalFiles.size > 0 && (
                  <button onClick={deleteSelectedLocalFiles} disabled={deletingFile === 'bulk'} style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px',
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(239, 68, 68, 0.3)',
                    background: 'rgba(239, 68, 68, 0.15)', color: '#EF4444'
                  }} data-testid="delete-selected-files-btn">
                    <Trash2 size={14} /> Delete {selectedLocalFiles.size} selected
                  </button>
                )}
                <button onClick={loadLocalFiles} disabled={localFilesLoading} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px',
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(245, 158, 11, 0.3)',
                  background: 'rgba(245, 158, 11, 0.12)', color: '#F59E0B'
                }} data-testid="refresh-local-files-btn">
                  <RefreshCw size={14} className={localFilesLoading ? 'spinning' : ''} /> Refresh
                </button>
              </div>
            </div>

            {localFilesLoading && !localFiles ? (
              <div className="empty-state"><HardDrive size={48} /><h3>Loading files...</h3></div>
            ) : localFiles ? (
              <>
                {/* Summary bar */}
                <div style={{
                  padding: '16px 20px', borderRadius: '12px', marginBottom: '16px',
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(239, 68, 68, 0.08))',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
                }}>
                  <div>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Total: </span>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: '#F59E0B' }}>{formatFileSize(localFiles.totalSize)}</span>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)', marginLeft: '16px' }}>Files: </span>
                    <span style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' }}>{localFiles.totalFiles}</span>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={localFiles.files.length > 0 && selectedLocalFiles.size === localFiles.files.length}
                      onChange={toggleSelectAll} style={{ accentColor: '#F59E0B', width: '16px', height: '16px' }} data-testid="select-all-files" />
                    Select All
                  </label>
                </div>

                {/* File list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {localFiles.files.map(file => (
                    <div key={file.path} style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                      borderRadius: '10px', border: '1px solid var(--border-color)',
                      background: selectedLocalFiles.has(file.path) ? 'rgba(245, 158, 11, 0.08)' : 'var(--card-bg, rgba(255,255,255,0.03))',
                      transition: 'background 0.15s'
                    }} data-testid={`file-row-${file.name}`}>
                      <input type="checkbox" checked={selectedLocalFiles.has(file.path)}
                        onChange={() => toggleFileSelection(file.path)}
                        style={{ accentColor: '#F59E0B', width: '16px', height: '16px', flexShrink: 0 }} />
                      <span style={{ fontSize: '18px', flexShrink: 0 }}>{getFileIcon(file.type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {file.path.includes('/') ? file.path.split('/')[0] + '/' : 'root'}
                        </div>
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flexShrink: 0, minWidth: '65px', textAlign: 'right' }}>
                        {formatFileSize(file.size)}
                      </span>
                      <button onClick={() => deleteLocalFile(file.path)} disabled={deletingFile === file.path}
                        style={{
                          padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.25)',
                          background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', cursor: 'pointer',
                          fontSize: '12px', fontWeight: 600, flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px'
                        }} data-testid={`delete-file-${file.name}`}>
                        <Trash2 size={12} /> {deletingFile === file.path ? '...' : 'Delete'}
                      </button>
                    </div>
                  ))}
                </div>

                {localFiles.files.length === 0 && (
                  <div className="empty-state"><HardDrive size={48} /><h3>No local files found</h3></div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                <HardDrive size={40} style={{ opacity: 0.4, marginBottom: '12px' }} />
                <p>Click Refresh to load server files</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Balance Payment Modal */}
      {balanceModal && (
        <div className="modal-overlay" onClick={() => { setBalanceModal(null); setBalanceInput(''); }}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <button className="modal-close-btn" onClick={() => { setBalanceModal(null); setBalanceInput(''); }}>
              <X size={24} />
            </button>
            <div className="upload-modal-header">
              <DollarSign size={32} className="upload-icon" style={{ color: '#5EFCE8' }} />
              <h2>Record Payment</h2>
            </div>
            <div className="upload-modal-content" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Total Price:</span>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{balanceModal.totalPrice.toLocaleString()} AED</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Already Paid:</span>
                <span style={{ fontWeight: 700, color: '#10B981' }}>{balanceModal.paidAmount.toLocaleString()} AED</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '14px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Remaining:</span>
                <span style={{ fontWeight: 700, color: '#FFA500' }}>{(balanceModal.totalPrice - balanceModal.paidAmount).toLocaleString()} AED</span>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 500 }}>Amount (AED) — use minus (-) to deduct</label>
                <input type="number" value={balanceInput} placeholder="e.g. 500 or -200"
                  onChange={(e) => setBalanceInput(e.target.value)}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '16px', fontWeight: 600, boxSizing: 'border-box' }}
                  data-testid="balance-amount-input" />
                {balanceInput && parseFloat(balanceInput) !== 0 && !isNaN(parseFloat(balanceInput)) && (
                  <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', 
                    background: parseFloat(balanceInput) < 0 ? 'rgba(255, 99, 71, 0.08)' : 'rgba(16, 185, 129, 0.08)', 
                    border: `1px solid ${parseFloat(balanceInput) < 0 ? 'rgba(255, 99, 71, 0.2)' : 'rgba(16, 185, 129, 0.2)'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{parseFloat(balanceInput) < 0 ? 'Deducting:' : 'Adding:'}</span>
                      <span style={{ fontWeight: 700, color: parseFloat(balanceInput) < 0 ? '#FF6347' : '#10B981' }}>
                        {parseFloat(balanceInput) < 0 ? '-' : '+'}{Math.abs(parseFloat(balanceInput)).toLocaleString()} AED
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>New Paid Amount:</span>
                      <span style={{ fontWeight: 700, color: parseFloat(balanceInput) < 0 ? '#FF6347' : '#10B981' }}>
                        {Math.max(0, balanceModal.paidAmount + parseFloat(balanceInput)).toLocaleString()} AED
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => { setBalanceModal(null); setBalanceInput(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleBalanceUpdate}
                disabled={!balanceInput || parseFloat(balanceInput) === 0 || isNaN(parseFloat(balanceInput))}
                style={balanceInput && parseFloat(balanceInput) < 0 ? { background: '#FF6347' } : {}}
                data-testid="balance-submit-btn">
                <DollarSign size={18} /> {balanceInput && parseFloat(balanceInput) < 0 ? 'Deduct Payment' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Price Modal */}
      {deductModal && (
        <div className="modal-overlay" onClick={() => { setDeductModal(null); setDeductInput(''); }}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <button className="modal-close-btn" onClick={() => { setDeductModal(null); setDeductInput(''); }}>
              <X size={24} />
            </button>
            <div className="upload-modal-header">
              <DollarSign size={32} className="upload-icon" style={{ color: deductInput && parseFloat(deductInput) > 0 ? '#10B981' : '#FF6347' }} />
              <h2>Adjust Price</h2>
              <p>Booking #{deductModal.bookingNumber}</p>
            </div>
            <div className="upload-modal-body" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Current Price:</span>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{deductModal.currentPrice.toLocaleString()} AED</span>
                </div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Amount (AED) — use minus (-) to deduct, plus (+) to add</label>
                <input
                  type="number"
                  value={deductInput}
                  onChange={(e) => setDeductInput(e.target.value)}
                  placeholder="e.g. +500 to add, -200 to deduct"
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '16px', fontWeight: 600, boxSizing: 'border-box' }}
                  data-testid="deduct-amount-input"
                />
                {deductInput && parseFloat(deductInput) !== 0 && !isNaN(parseFloat(deductInput)) && (
                  <div style={{ marginTop: '6px', padding: '10px', borderRadius: '8px', 
                    background: parseFloat(deductInput) > 0 ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 99, 71, 0.08)', 
                    border: `1px solid ${parseFloat(deductInput) > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 99, 71, 0.2)'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{parseFloat(deductInput) > 0 ? 'Adding:' : 'Deducting:'}</span>
                      <span style={{ fontWeight: 700, color: parseFloat(deductInput) > 0 ? '#10B981' : '#FF6347' }}>
                        {parseFloat(deductInput) > 0 ? '+' : ''}{parseFloat(deductInput).toLocaleString()} AED
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>New Price:</span>
                      <span style={{ fontWeight: 700, color: parseFloat(deductInput) > 0 ? '#10B981' : '#FF6347' }}>
                        {Math.max(0, deductModal.currentPrice + parseFloat(deductInput)).toLocaleString()} AED
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => { setDeductModal(null); setDeductInput(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleDeductPrice}
                disabled={!deductInput || parseFloat(deductInput) === 0 || isNaN(parseFloat(deductInput))}
                style={deductInput && parseFloat(deductInput) < 0 ? { background: '#FF6347' } : {}}
                data-testid="deduct-submit-btn">
                <DollarSign size={18} /> {deductInput && parseFloat(deductInput) > 0 ? 'Add to Price' : 'Deduct Price'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Edit Accounting Order Modal */}
      {csEditModal && (
        <div className="modal-overlay" onClick={() => setCsEditModal(null)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }} data-testid="cs-edit-modal">
            <button className="modal-close-btn" onClick={() => setCsEditModal(null)} data-testid="cs-edit-close">
              <X size={24} />
            </button>
            <div className="upload-modal-header">
              <Edit size={32} className="upload-icon" />
              <h2>Edit Order</h2>
            </div>
            <div className="upload-modal-body" style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                ['client', 'Client Name', 'text', 'Client full name'],
                ['client_email', 'Client Email', 'email', 'client@example.com'],
                ['service', 'Service', 'text', 'Service name'],
                ['start_date', 'Start Date', 'date', ''],
                ['receiver', 'Paying To', 'text', 'Receiver name'],
                ['receiver_email', 'Receiver Email', 'email', 'receiver@example.com'],
                ['total_amount', 'Total Price (AED)', 'number', '0'],
              ].map(([key, label, type, ph]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{label}</label>
                  <input type={type} value={csEditModal[key]} placeholder={ph} min={type === 'number' ? '0' : undefined}
                    onChange={(e) => setCsEditModal({ ...csEditModal, [key]: e.target.value })}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box' }}
                    data-testid={`cs-edit-${key}-input`} />
                </div>
              ))}
              <div style={{ alignSelf: 'end', fontSize: '12px', color: 'var(--text-secondary)', paddingBottom: '12px' }} data-testid="cs-edit-balance-preview">
                Paid: {Number(csEditModal.paid).toLocaleString()} AED · Remaining: {Math.max((parseFloat(csEditModal.total_amount) || 0) - csEditModal.paid, 0).toLocaleString()} AED
              </div>
              <div style={{ gridColumn: '1 / -1', fontSize: '12px', color: 'var(--text-secondary)' }}>
                The client and receiver will get an email showing what was changed.
              </div>
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => setCsEditModal(null)} data-testid="cs-edit-cancel">Cancel</button>
              <button className="btn-primary" onClick={saveCsEdit}
                disabled={csEditSaving || !csEditModal.client.trim() || !csEditModal.service.trim() || !(parseFloat(csEditModal.total_amount) > 0)}
                data-testid="cs-edit-save">
                <Check size={18} /> {csEditSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Booking Details Modal */}
      {editDetailsModal && (
        <div className="modal-overlay" onClick={() => setEditDetailsModal(null)}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }} data-testid="edit-booking-details-modal">
            <button className="modal-close-btn" onClick={() => setEditDetailsModal(null)} data-testid="edit-booking-details-close">
              <X size={24} />
            </button>
            <div className="upload-modal-header">
              <Edit size={32} className="upload-icon" />
              <h2>Edit Booking Details</h2>
            </div>
            <div className="upload-modal-body" style={{ padding: '20px', display: 'grid', gap: '12px' }}>
              {[
                ['name', 'Full Name', 'Client full name'],
                ['nationality', 'Nationality', 'e.g. Uzbekistan'],
                ['passportNumber', 'Passport Number', 'e.g. FA1234567'],
                ['agentName', 'Agent Name', 'Agent full name'],
                ['agentPhone', 'Agent Number', 'e.g. +998 90 123 45 67'],
              ].map(([key, label, ph]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{label}</label>
                  <input type="text" value={editDetailsModal[key]} placeholder={ph}
                    onChange={(e) => setEditDetailsModal({ ...editDetailsModal, [key]: e.target.value })}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box' }}
                    data-testid={`edit-booking-${key}-input`} />
                </div>
              ))}
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => setEditDetailsModal(null)} data-testid="edit-booking-details-cancel">Cancel</button>
              <button className="btn-primary" onClick={handleSaveDetails}
                disabled={!editDetailsModal.name.trim() || editDetailsSaving}
                data-testid="edit-booking-details-save">
                <Check size={18} /> {editDetailsSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Email Modal */}
      {editEmailModal && (
        <div className="modal-overlay" onClick={() => { setEditEmailModal(null); setEditEmailInput(''); }}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <button className="modal-close-btn" onClick={() => { setEditEmailModal(null); setEditEmailInput(''); }}>
              <X size={24} />
            </button>
            <div className="upload-modal-header">
              <Mail size={32} className="upload-icon" style={{ color: '#3B82F6' }} />
              <h2>{editEmailModal.currentEmail ? 'Edit Email' : 'Add Email'}</h2>
            </div>
            <div className="upload-modal-body" style={{ padding: '20px' }}>
              {editEmailModal.currentEmail && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '12px' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Current:</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{editEmailModal.currentEmail}</span>
                </div>
              )}
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                {editEmailModal.currentEmail ? 'New Email' : 'Email Address'}
              </label>
              <input type="email" value={editEmailInput} onChange={(e) => setEditEmailInput(e.target.value)}
                placeholder="client@example.com"
                style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)', color: 'var(--text-primary)', fontSize: '16px', fontWeight: 600, boxSizing: 'border-box' }}
                data-testid="edit-email-input" />
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => { setEditEmailModal(null); setEditEmailInput(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleEditEmail}
                disabled={!editEmailInput.trim() || !editEmailInput.includes('@')}
                data-testid="edit-email-submit">
                <Mail size={18} /> Save Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      {warningModal && (
        <div className="modal-overlay" onClick={() => { setWarningModal(null); setWarningReason(''); }}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <button className="modal-close-btn" onClick={() => { setWarningModal(null); setWarningReason(''); }}>
              <X size={24} />
            </button>
            <div className="upload-modal-header" style={{ background: 'linear-gradient(135deg, rgba(255,165,0,0.15), rgba(255,69,0,0.1))' }}>
              <AlertCircle size={36} style={{ color: '#FFA500' }} />
              <h2 style={{ color: '#FFA500' }}>Send Warning</h2>
              <p>Booking #{warningModal.bookingNumber} - {warningModal.customerName}</p>
            </div>
            <div className="upload-modal-body" style={{ padding: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Warning Reason
              </label>
              <textarea
                value={warningReason}
                onChange={(e) => setWarningReason(e.target.value)}
                placeholder="Enter the warning reason to send to the client..."
                rows={4}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: '8px',
                  border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)',
                  color: 'var(--text-primary)', fontSize: '14px', resize: 'vertical',
                  boxSizing: 'border-box', fontFamily: 'inherit'
                }}
                data-testid="warning-reason-input"
              />
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                This warning will be sent to: <strong>{warningModal.customerEmail}</strong>
              </p>
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => { setWarningModal(null); setWarningReason(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleSendWarning}
                disabled={!warningReason.trim() || warningSending}
                style={{ background: '#FFA500' }}
                data-testid="warning-submit-btn">
                {warningSending ? 'Sending...' : <><AlertCircle size={18} /> Send Warning</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Generation Modal */}
      {bookingInvoiceModal && (
        <div className="modal-overlay" onClick={() => !invoiceGenerating && setBookingInvoiceModal(null)} data-testid="invoice-modal">
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '95vw' }}>
            <button className="modal-close-btn" onClick={() => !invoiceGenerating && setBookingInvoiceModal(null)}>
              <X size={24} />
            </button>
            <div className="upload-modal-header" style={{ background: 'linear-gradient(135deg, rgba(0,184,159,0.18), rgba(0,180,216,0.10))' }}>
              <Receipt size={34} style={{ color: '#00B89F' }} />
              <h2 style={{ color: '#00B89F' }}>Generate Invoice</h2>
              <p>Booking #{bookingInvoiceModal.booking.bookingNumber} — {bookingInvoiceModal.booking.name || bookingInvoiceModal.booking.fullName || 'Customer'}</p>
            </div>
            <div className="upload-modal-body" style={{ padding: '20px', maxHeight: '65vh', overflowY: 'auto' }}>
              {/* Totals: Total Price + Amount Paid + auto Remaining */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Total Price (AED)
                  </label>
                  <input
                    type="number"
                    value={bookingInvoiceModal.totalPrice}
                    onChange={(e) => setBookingInvoiceModal({ ...bookingInvoiceModal, totalPrice: e.target.value })}
                    placeholder="0.00"
                    data-testid="invoice-total-input"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                      border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)',
                      color: 'var(--text-primary)', fontSize: '15px', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Amount Paid (AED)
                  </label>
                  <input
                    type="number"
                    value={bookingInvoiceModal.paidAmount}
                    onChange={(e) => setBookingInvoiceModal({ ...bookingInvoiceModal, paidAmount: e.target.value })}
                    placeholder="0.00"
                    data-testid="invoice-paid-input"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: '8px',
                      border: '1px solid #229ED9', background: 'var(--input-bg, #0f0f1a)',
                      color: '#229ED9', fontSize: '15px', fontWeight: 600, boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Live Remaining preview */}
              {(() => {
                const tp = parseFloat(bookingInvoiceModal.totalPrice) || 0;
                const pa = parseFloat(bookingInvoiceModal.paidAmount) || 0;
                const rem = Math.max(0, tp - pa);
                const isFull = rem < 0.01;
                return (
                  <div
                    style={{
                      padding: '12px 14px',
                      background: isFull ? 'rgba(0,184,159,0.12)' : 'rgba(255,107,107,0.10)',
                      border: `1px solid ${isFull ? 'rgba(0,184,159,0.4)' : 'rgba(255,107,107,0.4)'}`,
                      borderRadius: '8px',
                      marginBottom: '18px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                    data-testid="invoice-remaining-preview"
                  >
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                      {isFull ? '✓ STATUS' : 'REMAINING BALANCE'}
                    </span>
                    <strong style={{ fontSize: '17px', color: isFull ? '#00B89F' : '#FF6B6B' }}>
                      {isFull ? 'PAID IN FULL' : `AED ${rem.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </strong>
                  </div>
                );
              })()}

              {/* Line items */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Line Items</label>
                <button
                  type="button"
                  onClick={addInvoiceLineItem}
                  data-testid="invoice-add-line"
                  style={{
                    background: 'rgba(0,184,159,0.15)', border: '1px solid rgba(0,184,159,0.4)',
                    color: '#00B89F', padding: '4px 10px', borderRadius: '6px',
                    fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <Plus size={12} /> Add line
                </button>
              </div>
              {bookingInvoiceModal.lineItems.map((li, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 28px', gap: '6px', marginBottom: '6px' }} data-testid={`invoice-line-${idx}`}>
                  <input
                    type="text"
                    value={li.description}
                    onChange={(e) => updateInvoiceLineItem(idx, 'description', e.target.value)}
                    placeholder="Description"
                    style={{
                      padding: '8px 10px', borderRadius: '6px',
                      border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)',
                      color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box'
                    }}
                  />
                  <input
                    type="number"
                    value={li.qty}
                    onChange={(e) => updateInvoiceLineItem(idx, 'qty', e.target.value)}
                    placeholder="Qty"
                    style={{
                      padding: '8px 10px', borderRadius: '6px',
                      border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)',
                      color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box'
                    }}
                  />
                  <input
                    type="number"
                    value={li.unit_price}
                    onChange={(e) => updateInvoiceLineItem(idx, 'unit_price', e.target.value)}
                    placeholder="AED"
                    style={{
                      padding: '8px 10px', borderRadius: '6px',
                      border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)',
                      color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeInvoiceLineItem(idx)}
                    disabled={bookingInvoiceModal.lineItems.length <= 1}
                    title="Remove line"
                    style={{
                      background: 'rgba(255,100,100,0.12)', border: '1px solid rgba(255,100,100,0.35)',
                      color: '#ff6b6b', borderRadius: '6px', cursor: bookingInvoiceModal.lineItems.length <= 1 ? 'not-allowed' : 'pointer',
                      opacity: bookingInvoiceModal.lineItems.length <= 1 ? 0.4 : 1
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}

              {/* Subtotal preview */}
              <div style={{ textAlign: 'right', marginTop: '10px', padding: '10px', background: 'rgba(0,184,159,0.08)', borderRadius: '8px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', marginRight: '10px' }}>Subtotal:</span>
                <strong style={{ color: '#00B89F', fontSize: '16px' }} data-testid="invoice-subtotal">
                  AED {invoiceSubtotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </div>

              {/* Notes */}
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '18px', marginBottom: '6px' }}>
                Notes (optional)
              </label>
              <textarea
                value={bookingInvoiceModal.notes}
                onChange={(e) => setBookingInvoiceModal({ ...bookingInvoiceModal, notes: e.target.value })}
                placeholder="Add any payment details, thank-you message, or reference."
                rows={3}
                data-testid="invoice-notes-input"
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: '8px',
                  border: '1px solid var(--border-color)', background: 'var(--input-bg, #0f0f1a)',
                  color: 'var(--text-primary)', fontSize: '14px', resize: 'vertical',
                  boxSizing: 'border-box', fontFamily: 'inherit'
                }}
              />
            </div>
            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => !invoiceGenerating && setBookingInvoiceModal(null)} disabled={invoiceGenerating}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={generateInvoice}
                disabled={invoiceGenerating || !(parseFloat(bookingInvoiceModal.totalPrice) > 0)}
                style={{ background: '#00B89F' }}
                data-testid="invoice-generate-btn"
              >
                {invoiceGenerating ? (
                  <>Generating...</>
                ) : (
                  <><Receipt size={18} /> Generate &amp; Download PDF</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Freelance Step Modal */}
      {freelanceStepModal && (
        <div className="modal-overlay" onClick={() => { setFreelanceStepModal(null); setFreelanceStepFile(null); }}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <button className="modal-close-btn" onClick={() => { setFreelanceStepModal(null); setFreelanceStepFile(null); }}>
              <X size={24} />
            </button>

            <div className="upload-modal-header">
              <CheckCircle size={32} className="upload-icon" style={{ color: '#5EFCE8' }} />
              <h2>{stepLabels[freelanceStepModal.nextStep] || freelanceStepModal.nextStep}</h2>
              <p>Booking #{freelanceStepModal.booking.bookingNumber}</p>
              <p className="customer-info">Customer: {freelanceStepModal.booking.name}</p>
            </div>

            <div className="upload-modal-content">
              {['emirates_id', 'residence_visa'].includes(freelanceStepModal.nextStep) ? (
                <p style={{ color: '#FF6347', fontSize: '14px', marginBottom: '16px', textAlign: 'center', fontWeight: 600 }}>
                  Document upload is required for this step
                </p>
              ) : (
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
                  Attach a document to send with this status update (optional)
                </p>
              )}
              {!freelanceStepFile ? (
                <div className="upload-dropzone" onClick={() => freelanceFileRef.current?.click()}>
                  <Upload size={48} />
                  <p>Click to upload document</p>
                  <span>PDF, JPG, PNG (max 10MB)</span>
                </div>
              ) : (
                <div className="upload-preview">
                  <div className="file-info">
                    <FileText size={48} />
                    <span>{freelanceStepFile.name}</span>
                  </div>
                  <button className="btn-remove-file" onClick={() => { setFreelanceStepFile(null); if (freelanceFileRef.current) freelanceFileRef.current.value = ''; }}>
                    <X size={16} /> Remove
                  </button>
                </div>
              )}
              <input ref={freelanceFileRef} type="file" accept="image/*,.pdf,.doc,.docx"
                onChange={(e) => { if (e.target.files[0]) setFreelanceStepFile(e.target.files[0]); }}
                style={{ display: 'none' }} />
            </div>

            <div className="upload-modal-actions">
              <button className="btn-secondary" onClick={() => { setFreelanceStepModal(null); setFreelanceStepFile(null); }}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleFreelanceStepConfirm}
                disabled={freelanceStepUploading || (['emirates_id', 'residence_visa'].includes(freelanceStepModal.nextStep) && !freelanceStepFile)}>
                {freelanceStepUploading ? 'Processing...' : (
                  <>
                    <Send size={18} />
                    {freelanceStepFile ? 'Send & Update Status' : 'Update Status'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Required Modal */}
      {docRequiredModal && (
        <div className="modal-overlay" onClick={() => setDocRequiredModal(null)}>
          <div className="doc-required-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setDocRequiredModal(null)}>
              <X size={24} />
            </button>
            
            <div className="doc-modal-header">
              <AlertCircle size={32} className="doc-icon" />
              <h2>Request Document Re-upload</h2>
              <p>Booking #{docRequiredModal.bookingNumber} - {docRequiredModal.name}</p>
            </div>

            <div className="doc-modal-content">
              <div className="form-group">
                <label>Which document is required?</label>
                <div className="doc-type-options">
                  <button 
                    className={`doc-type-btn ${requiredDocType === 'passport' ? 'active' : ''}`}
                    onClick={() => setRequiredDocType('passport')}
                  >
                    <FileText size={24} />
                    <span>Passport</span>
                  </button>
                  <button 
                    className={`doc-type-btn ${requiredDocType === 'photo' ? 'active' : ''}`}
                    onClick={() => setRequiredDocType('photo')}
                  >
                    <Image size={24} />
                    <span>Photo</span>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Reason for re-upload *</label>
                <textarea
                  value={docReason}
                  onChange={(e) => setDocReason(e.target.value)}
                  placeholder="E.g., Photo is blurry, passport is expired, wrong document uploaded..."
                  rows="4"
                />
              </div>

              <div className="whatsapp-preview">
                <span className="preview-label">WhatsApp message preview:</span>
                <p className="preview-text">
                  Hello {docRequiredModal.name}! Your booking #{docRequiredModal.bookingNumber} requires a new {requiredDocType}. 
                  Reason: {docReason || '[reason]'}. Please re-upload from your dashboard. - Registan Tourism
                </p>
              </div>
            </div>

            <div className="doc-modal-actions">
              <button className="btn-secondary" onClick={() => setDocRequiredModal(null)}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={submitDocumentRequired}
                disabled={!docReason.trim()}
              >
                <Send size={18} />
                Send Request
              </button>
              <a
                className="btn-whatsapp-send"
                href={`https://wa.me/${(docRequiredModal.agentPhone || docRequiredModal.mobile)?.replace(/\D/g, '')}?text=${encodeURIComponent(
                  `Hello ${docRequiredModal.name}!\n\nYour booking #${docRequiredModal.bookingNumber} for ${docRequiredModal.service} requires a new ${requiredDocType}.\n\nReason: ${docReason || 'Please re-upload the document'}\n\nPlease login to your dashboard and re-upload the required document.\n\nRegistan Tourism`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={18} />
                Send via WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="admin-content">
          <div className="users-header">
            <h2>Registered Users ({stats?.total_users || 0})</h2>
            <div className="users-stats">
              <span className="online-indicator">
                <Activity size={16} />
                {stats?.online_users || 0} Online Now
              </span>
            </div>
          </div>
          
          {!stats?.users || stats.users.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>No users registered</h3>
              <p>Users will appear here once they register.</p>
            </div>
          ) : (
            <div className="users-list">
              {stats.users.map((user, index) => (
                <div key={index} className="user-card">
                  <div className="user-avatar">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="user-info">
                    <h4>{user.name || 'Unknown'}</h4>
                    <p className="user-email">{user.email}</p>
                    <p className="user-joined">
                      Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="user-actions-row">
                    <div className="user-status">
                      {user.lastActive && new Date(user.lastActive) > new Date(Date.now() - 15 * 60 * 1000) ? (
                        <span className="status-online">
                          <Activity size={14} /> Online
                        </span>
                      ) : (
                        <span className="status-offline">Offline</span>
                      )}
                    </div>
                    <button
                      className="btn-tg-link"
                      title={user.telegram_chat_id ? `Telegram linked: ${user.telegram_chat_id}` : "Link Telegram chat ID"}
                      onClick={async () => {
                        const current = user.telegram_chat_id || '';
                        const next = window.prompt(
                          `Telegram Chat ID for ${user.email}\n\n` +
                          `Ask the user to message your bot with /start — the bot will reply with their chat ID.\n` +
                          `Leave empty to unlink.`,
                          current
                        );
                        if (next === null) return;
                        try {
                          await axios.put(
                            `${BACKEND_URL}/api/admin/users/${encodeURIComponent(user.email)}/telegram`,
                            { chat_id: next.trim() },
                            { headers: { 'X-Admin-Key': adminPassword } }
                          );
                          alert(next.trim() ? '✅ Telegram linked. Confirmation sent to user.' : '✅ Telegram unlinked.');
                          loadStats();
                        } catch (err) {
                          alert('Failed: ' + (err.response?.data?.detail || err.message));
                        }
                      }}
                      data-testid={`link-telegram-${index}`}
                      style={{
                        background: user.telegram_chat_id ? 'linear-gradient(135deg,#229ED9,#1B7DBA)' : 'rgba(255,255,255,0.06)',
                        color: user.telegram_chat_id ? '#fff' : 'rgba(255,255,255,0.65)',
                        border: '1px solid rgba(94,252,232,0.18)',
                        borderRadius: 8, width: 36, height: 36, marginRight: 6,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        fontSize: 16, fontWeight: 700,
                      }}
                    >
                      ✈
                    </button>
                    <button
                      className="btn-delete-user"
                      onClick={async () => {
                        if (window.confirm(`Delete user "${user.name || user.email}"? This cannot be undone.`)) {
                          try {
                            await axios.delete(`${BACKEND_URL}/api/admin/users/${user.id}`, {
                              headers: { 'X-Admin-Key': adminPassword }
                            });
                            loadStats();
                          } catch (err) {
                            alert('Failed to delete user: ' + (err.response?.data?.detail || err.message));
                          }
                        }
                      }}
                      data-testid={`delete-user-${index}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="admin-content">
          {/* Pending Payment Bookings Section */}
          <div className="pending-payments-section">
            <div className="section-header-invoice">
              <h2>
                <AlertCircle size={22} className="warning-icon" />
                Pending Payment Bookings
              </h2>
              <span className="pending-count">
                {bookings.filter(b => b.status === 'pending_payment').length} awaiting invoice
              </span>
            </div>
            
            {bookings.filter(b => b.status === 'pending_payment').length === 0 ? (
              <div className="no-pending-payments">
                <CheckCircle size={24} />
                <span>No pending payment bookings</span>
              </div>
            ) : (
              <div className="pending-payments-list">
                {bookings.filter(b => b.status === 'pending_payment').map((booking) => (
                  <div key={booking.id || booking.bookingNumber} className="pending-payment-card">
                    <div className="pending-payment-info">
                      <div className="customer-details">
                        <strong>{booking.name}</strong>
                        <span className="customer-email">{booking.email}</span>
                        <span className="customer-phone">{booking.agentPhone || booking.mobile}</span>
                      </div>
                      <div className="booking-details">
                        <span className="booking-number">{booking.bookingNumber}</span>
                        <span className="service-name">{booking.service}</span>
                        <span className="service-price">{booking.servicePrice}</span>
                        {booking.debitCard === 'yes' && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: 'rgba(94, 252, 232, 0.12)',
                            border: '1px solid rgba(94, 252, 232, 0.3)',
                            fontSize: '11px',
                            color: '#5EFCE8',
                            fontWeight: '600'
                          }}>
                            Debit Card +AED {booking.debitCardFee || '500'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="pending-payment-actions">
                      <button 
                        className="btn-send-invoice"
                        onClick={() => {
                          // Parse price from string like "AED 20 000"
                          const priceStr = booking.servicePrice || '';
                          const priceNum = parseFloat(priceStr.replace(/[^0-9.]/g, '')) || 0;
                          
                          setInvoiceForm({
                            user_email: booking.email || '',
                            user_name: booking.name || '',
                            items: [{ 
                              description: booking.service || 'Visa Service', 
                              quantity: 1, 
                              price: priceNum
                            }],
                            notes: `Booking: ${booking.bookingNumber}`,
                            due_date: '',
                            booking_id: booking.id  // Link invoice to booking
                          });
                          setInvoiceModal('add');
                        }}
                      >
                        <Send size={16} />
                        Send Invoice
                      </button>
                      <button 
                        className="btn-mark-paid"
                        onClick={() => {
                          updateStatus(booking.id, 'paid');
                        }}
                        title="Mark as Paid & Notify User"
                      >
                        <DollarSign size={16} />
                        Mark Paid
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invoices List Section */}
          <div className="invoices-header">
            <h2>Sent Invoices ({invoices.length})</h2>
            <button className="btn-add-invoice" onClick={openAddInvoiceModal}>
              <Plus size={18} />
              Create Invoice
            </button>
          </div>
          
          {invoices.length === 0 ? (
            <div className="empty-state">
              <CreditCard size={48} />
              <h3>No invoices yet</h3>
              <p>Create invoices for your customers and they will appear here.</p>
            </div>
          ) : (
            <div className="invoices-list">
              {invoices.map((invoice) => {
                const total = invoice.total || 0;
                const paidAmount = invoice.paid_amount || 0;
                const remaining = total - paidAmount;
                const paymentProgress = total > 0 ? (paidAmount / total) * 100 : 0;
                
                return (
                <div key={invoice.id} className={`invoice-card status-${invoice.status}`}>
                  <div className="invoice-header">
                    <span className="invoice-number">{invoice.invoice_number}</span>
                    <span className={`invoice-status ${invoice.status}`}>
                      {invoice.status === 'partial' ? 'PARTIAL PAID' : invoice.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="invoice-body">
                    <div className="invoice-customer">
                      <strong>{invoice.user_name}</strong>
                      <span>{invoice.user_email}</span>
                    </div>
                    <div className="invoice-items">
                      {invoice.items.map((item, idx) => (
                        <div key={idx} className="invoice-item">
                          <span>{item.description}</span>
                          <span>x{item.quantity}</span>
                          <span>AED {item.price}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Payment Progress Section */}
                    <div className="invoice-payment-section">
                      <div className="payment-progress-bar">
                        <div 
                          className="payment-progress-fill" 
                          style={{ width: `${paymentProgress}%` }}
                        />
                      </div>
                      <div className="payment-details">
                        <div className="payment-row">
                          <span>Total Amount:</span>
                          <strong>AED {total.toLocaleString()}</strong>
                        </div>
                        <div className="payment-row paid">
                          <span>Paid:</span>
                          <strong className="text-green">AED {paidAmount.toLocaleString()}</strong>
                        </div>
                        <div className="payment-row remaining">
                          <span>Remaining Balance:</span>
                          <strong className={remaining > 0 ? 'text-yellow' : 'text-green'}>
                            AED {remaining.toLocaleString()}
                          </strong>
                        </div>
                      </div>
                    </div>

                    {/* Payment History */}
                    {invoice.payments && invoice.payments.length > 0 && (
                      <div className="payment-history">
                        <small className="payment-history-title">Payment History:</small>
                        {invoice.payments.map((payment, idx) => (
                          <div key={idx} className="payment-history-item">
                            <span>{new Date(payment.paid_at).toLocaleDateString()}</span>
                            <span className="payment-method">{payment.payment_method}</span>
                            <span className="payment-amount">AED {payment.amount.toLocaleString()}</span>
                            {payment.invoice_url && (
                              <a href={payment.invoice_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <FileText size={12} /> Invoice
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {invoice.notes && (
                      <div className="invoice-notes">
                        <small>Notes: {invoice.notes}</small>
                      </div>
                    )}
                    <div className="invoice-date">
                      Created: {new Date(invoice.created_at).toLocaleDateString()}
                      {invoice.due_date && ` | Due: ${invoice.due_date}`}
                    </div>
                  </div>
                  <div className="invoice-actions">
                    {(invoice.status === 'pending' || invoice.status === 'partial') && remaining > 0 && (
                      <button 
                        className="btn-add-payment"
                        onClick={() => openPaymentModal(invoice)}
                      >
                        <DollarSign size={16} />
                        Add Payment
                      </button>
                    )}
                    {invoice.status === 'paid' && (
                      <button 
                        className="btn-mark-pending"
                        onClick={() => updateInvoiceStatus(invoice.id, 'pending')}
                      >
                        <Clock size={16} />
                        Reopen
                      </button>
                    )}
                    <button 
                      className="btn-delete-invoice"
                      onClick={() => deleteInvoice(invoice.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="admin-content">
          <div className="messages-section">
            <div className="section-header-messages">
              <h2>
                <MessageCircle size={22} />
                Customer Messages
              </h2>
              <span className="messages-count">
                {messages.filter(m => m.status === 'unread').length} unread
              </span>
            </div>

            {messages.length === 0 ? (
              <div className="no-messages">
                <MessageCircle size={48} />
                <p>No messages yet</p>
              </div>
            ) : (
              <div className="messages-list">
                {messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`message-card ${msg.status}`}
                    data-testid={`message-${msg.id}`}
                  >
                    <div className="message-header">
                      <div className="message-sender">
                        <div className="sender-avatar">{msg.name.charAt(0).toUpperCase()}</div>
                        <div className="sender-info">
                          <strong>{msg.name}</strong>
                          <span className="sender-phone">
                            <Phone size={14} />
                            {msg.phone}
                          </span>
                        </div>
                      </div>
                      <div className="message-meta">
                        <span className={`status-badge ${msg.status}`}>
                          {msg.status === 'unread' ? 'New' : msg.status === 'read' ? 'Read' : 'Replied'}
                        </span>
                        <span className="message-date">
                          {new Date(msg.createdAt).toLocaleDateString()} {new Date(msg.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                    </div>

                    <div className="message-content">
                      <p>{msg.message}</p>
                      {/* Show attachment if exists */}
                      {msg.attachment && (
                        <div className="message-attachment-admin">
                          {msg.attachment.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <a href={`${BACKEND_URL}${msg.attachment}`} target="_blank" rel="noopener noreferrer">
                              <img 
                                src={`${BACKEND_URL}${msg.attachment}`} 
                                alt={msg.attachmentName || 'Attachment'} 
                                className="admin-attachment-image"
                              />
                            </a>
                          ) : (
                            <a 
                              href={`${BACKEND_URL}${msg.attachment}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="admin-attachment-file"
                            >
                              📎 {msg.attachmentName || 'Download attachment'}
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Show replies */}
                    {msg.replies && msg.replies.length > 0 && (
                      <div className="message-replies">
                        <strong>Replies:</strong>
                        {msg.replies.map((reply, idx) => (
                          <div key={idx} className={`reply-item ${reply.isAdmin ? 'admin-reply' : 'user-reply'}`}>
                            <span className="reply-badge">{reply.isAdmin ? 'Admin' : 'User'}</span>
                            <p>{reply.text}</p>
                            {/* Show attachment in reply */}
                            {reply.attachment && (
                              <div className="reply-attachment">
                                {reply.attachment.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <a href={`${BACKEND_URL}${reply.attachment}`} target="_blank" rel="noopener noreferrer">
                                    <img 
                                      src={`${BACKEND_URL}${reply.attachment}`} 
                                      alt={reply.attachmentName || 'Attachment'} 
                                      className="reply-attachment-image"
                                    />
                                  </a>
                                ) : (
                                  <a 
                                    href={`${BACKEND_URL}${reply.attachment}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="reply-attachment-file"
                                  >
                                    📎 {reply.attachmentName || 'Download attachment'}
                                  </a>
                                )}
                              </div>
                            )}
                            <small>{new Date(reply.createdAt).toLocaleString()}</small>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply form */}
                    {replyingTo === msg.id ? (
                      <div className="reply-form">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          rows={3}
                          disabled={sendingReply}
                        />
                        <div className="reply-actions">
                          <button 
                            className="btn-cancel-reply"
                            onClick={() => { setReplyingTo(null); setReplyText(''); }}
                            disabled={sendingReply}
                          >
                            Cancel
                          </button>
                          <button 
                            className="btn-send-reply"
                            onClick={() => handleReply(msg.id)}
                            disabled={sendingReply || !replyText.trim()}
                          >
                            <Send size={16} />
                            {sendingReply ? 'Sending...' : 'Send Reply'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="message-actions">
                        {msg.status === 'unread' && (
                          <button 
                            className="btn-mark-read"
                            onClick={() => markAsRead(msg.id)}
                          >
                            <Eye size={16} />
                            Mark Read
                          </button>
                        )}
                        <button 
                          className="btn-reply"
                          onClick={() => setReplyingTo(msg.id)}
                        >
                          <Send size={16} />
                          Reply
                        </button>
                        <button 
                          className="btn-delete-message"
                          onClick={() => deleteMessage(msg.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send Email Tab */}
      {activeTab === 'send-email' && (
        <div className="admin-section">
          {/* New Email Alert */}
          {newEmailAlert && (
            <div className="new-email-alert" onClick={() => { setNewEmailAlert(false); loadInboxEmails(); setEmailSubTab('inbox'); }}>
              <Mail size={20} />
              <span>You received a new email!</span>
              <button onClick={(e) => { e.stopPropagation(); setNewEmailAlert(false); }}>
                <X size={16} />
              </button>
            </div>
          )}
          
          <div className="section-header">
            <h2><Mail size={24} /> Email</h2>
            <div className="header-actions">
              <button className="btn-refresh" onClick={() => { loadInboxEmails(); loadSentEmails(); }} disabled={loadingInbox}>
                <RefreshCw size={18} className={loadingInbox ? 'spinning' : ''} />
                Refresh
              </button>
              <button className="btn-add-service" onClick={() => openEmailComposer('')} data-testid="email-compose-btn">
                <Plus size={18} />
                Compose
              </button>
            </div>
          </div>

          {/* Email Sub-tabs */}
          <div className="email-sub-tabs">
            <button 
              className={`email-sub-tab ${emailSubTab === 'inbox' ? 'active' : ''}`}
              onClick={() => { setEmailSubTab('inbox'); loadInboxEmails(); }}
              data-testid="email-inbox-tab"
            >
              <Mail size={18} />
              Inbox
              {lastInboxCount > 0 && (
                <span className="sub-tab-badge" data-testid="inbox-unread-subtab-badge">{lastInboxCount}</span>
              )}
            </button>
            <button 
              className={`email-sub-tab ${emailSubTab === 'sent' ? 'active' : ''}`}
              onClick={() => { setEmailSubTab('sent'); loadSentEmails(); }}
              data-testid="email-sent-tab"
            >
              <Send size={18} />
              Sent
              <span className="sub-tab-count">({sentEmails.length})</span>
            </button>
            <button 
              className={`email-sub-tab ${emailSubTab === 'delivery-log' ? 'active' : ''}`}
              onClick={() => { setEmailSubTab('delivery-log'); loadDeliveryLogs(); }}
              data-testid="email-delivery-log-tab"
            >
              <Activity size={18} />
              Delivery Log
            </button>
          </div>
          
          {/* Inbox View */}
          {emailSubTab === 'inbox' && (
            <div className="email-panel">
              <div className="email-panel-header">
                <h3>
                  <Mail size={20} />
                  Inbox (support@registantours.com)
                </h3>
              </div>
              
              {loadingInbox ? (
                <div className="loading-inbox">
                  <RefreshCw size={24} className="spinning" />
                  <p>Loading emails...</p>
                </div>
              ) : inboxError ? (
                <div className="no-emails" data-testid="inbox-error">
                  <AlertCircle size={48} />
                  <p>{inboxError}</p>
                </div>
              ) : inboxEmails.length === 0 ? (
                <div className="no-emails">
                  <Mail size={48} />
                  <p>No emails in inbox</p>
                </div>
              ) : (
                <div className="email-list">
                  {inboxEmails.map((email) => (
                    <div 
                      key={email.id} 
                      className={`email-list-item ${!email.is_read ? 'unread' : ''}`}
                      data-testid={`inbox-email-${email.id}`}
                    >
                      <div className="email-list-content" onClick={() => openInboxEmail(email)} data-testid={`inbox-email-open-${email.id}`}>
                        <div className={`email-list-avatar ${!email.is_read ? 'unread' : ''}`}>
                          {email.from_name?.charAt(0) || 'U'}
                        </div>
                        <div className="email-list-info">
                          <div className="email-list-top">
                            <span className="email-list-from">{email.from_name || email.from_email}</span>
                            <span className="email-list-date">{new Date(email.date).toLocaleDateString()}</span>
                          </div>
                          <span className="email-list-subject">{email.subject}</span>
                          <span className="email-list-preview">{email.body?.substring(0, 80)}...</span>
                        </div>
                        {email.has_attachments && <span className="email-attachment-icon">📎</span>}
                      </div>
                      <button 
                        className="email-delete-btn"
                        onClick={(e) => { e.stopPropagation(); deleteInboxEmail(email.id); }}
                        title="Delete"
                        data-testid={`inbox-email-delete-${email.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sent View */}
          {emailSubTab === 'sent' && (
            <div className="email-panel">
              <div className="email-panel-header">
                <h3>
                  <Send size={20} />
                  Sent Emails
                </h3>
              </div>
              
              {sentEmails.length === 0 ? (
                <div className="no-emails">
                  <Send size={48} />
                  <p>No emails sent yet</p>
                </div>
              ) : (
                <div className="email-list">
                  {sentEmails.map((email) => (
                    <div key={email.id} className="email-list-item sent">
                      <div className="email-list-content">
                        <div className="email-list-avatar sent">
                          <Send size={18} />
                        </div>
                        <div className="email-list-info">
                          <div className="email-list-top">
                            <span className="email-list-from">To: {email.to_email}</span>
                            <span className={`email-delivery-badge ${email.delivery_status === 'delivered' ? 'delivered' : 'failed'}`}>
                              {email.delivery_status === 'delivered' ? '100% Delivered' : 'Not Delivered'}
                            </span>
                            <span className="email-list-date">{new Date(email.sent_at).toLocaleDateString()}</span>
                          </div>
                          <span className="email-list-subject">{email.subject}</span>
                          <span className="email-list-preview">{email.message?.substring(0, 80)}...</span>
                          {email.delivery_status === 'failed' && email.delivery_error && (
                            <span className="email-delivery-error">{email.delivery_error}</span>
                          )}
                        </div>
                        {email.attachments_count > 0 && <span className="email-attachment-icon">📎 {email.attachments_count}</span>}
                      </div>
                      <button 
                        className="email-delete-btn"
                        onClick={() => deleteSentEmail(email.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Delivery Log View */}
          {emailSubTab === 'delivery-log' && (
            <div className="email-panel" data-testid="delivery-log-panel">
              <div className="email-panel-header">
                <h3>
                  <Activity size={20} />
                  Email Delivery Log
                </h3>
                <div className="delivery-log-filters">
                  <select 
                    value={deliveryLogFilter} 
                    onChange={(e) => { setDeliveryLogFilter(e.target.value); loadDeliveryLogs(e.target.value); }}
                    className="delivery-filter-select"
                    data-testid="delivery-log-filter"
                  >
                    <option value="all">All Types</option>
                    <option value="booking_notification">Booking Notification</option>
                    <option value="booking_confirmation">Booking Confirmation</option>
                    <option value="status_update">Status Update</option>
                    <option value="invoice">Invoice</option>
                    <option value="invoice_payment">Invoice Payment</option>
                    <option value="payment_confirmation">Payment Confirmation</option>
                    <option value="payment_notification">Payment Notification</option>
                    <option value="payment_receipt">Payment Receipt</option>
                    <option value="password_reset">Password Reset</option>
                    <option value="document_request">Document Request</option>
                    <option value="visa_document">Visa Document</option>
                    <option value="admin_email">Admin Email</option>
                    <option value="chat_notification">Chat Notification</option>
                    <option value="booking_warning">Booking Warning</option>
                  </select>
                  <button 
                    className="btn-refresh" 
                    onClick={() => loadDeliveryLogs()} 
                    disabled={loadingDeliveryLogs}
                    data-testid="refresh-delivery-logs"
                  >
                    <RefreshCw size={16} className={loadingDeliveryLogs ? 'spinning' : ''} />
                  </button>
                </div>
              </div>

              {/* Summary Stats */}
              {deliveryLogs.length > 0 && (
                <div className="delivery-log-stats" data-testid="delivery-stats">
                  <div className="stat-pill delivered">
                    <span className="stat-dot delivered"></span>
                    {deliveryLogs.filter(l => l.delivery_status === 'delivered').length} Delivered
                  </div>
                  <div className="stat-pill failed">
                    <span className="stat-dot failed"></span>
                    {deliveryLogs.filter(l => l.delivery_status === 'failed').length} Failed
                  </div>
                  <div className="stat-pill total">
                    Total: {deliveryLogs.length}
                  </div>
                </div>
              )}

              {loadingDeliveryLogs ? (
                <div className="loading-inbox">
                  <RefreshCw size={24} className="spinning" />
                  <p>Loading delivery logs...</p>
                </div>
              ) : deliveryLogs.length === 0 ? (
                <div className="no-emails">
                  <Activity size={48} />
                  <p>No email delivery logs yet</p>
                  <p style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>Logs will appear here as system emails are sent</p>
                </div>
              ) : (
                <div className="email-list" data-testid="delivery-log-list">
                  {deliveryLogs.map((log) => (
                    <div key={log.id} className={`email-list-item delivery-log-item ${log.delivery_status}`} data-testid={`delivery-log-${log.id}`}>
                      <div className="email-list-content">
                        <div className={`email-list-avatar ${log.delivery_status === 'delivered' ? 'sent' : 'failed-avatar'}`}>
                          {log.delivery_status === 'delivered' 
                            ? <Check size={18} /> 
                            : <AlertCircle size={18} />
                          }
                        </div>
                        <div className="email-list-info">
                          <div className="email-list-top">
                            <span className="email-list-from">To: {log.to_email}</span>
                            <span className={`email-delivery-badge ${log.delivery_status === 'delivered' ? 'delivered' : 'failed'}`}>
                              {log.delivery_status === 'delivered' ? '100% Delivered' : 'Not Delivered'}
                            </span>
                            <span className="email-list-date">
                              {new Date(log.sent_at).toLocaleString()}
                            </span>
                          </div>
                          <span className="email-list-subject">{log.subject}</span>
                          <div className="delivery-log-meta">
                            <span className={`email-type-badge type-${log.email_type}`}>
                              {log.email_type?.replace(/_/g, ' ')}
                            </span>
                            <span className="delivery-from-info">From: {log.from_email}</span>
                          </div>
                          {log.delivery_status === 'failed' && log.error && (
                            <span className="email-delivery-error">{log.error}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inbox Email View Modal */}
      {selectedInboxEmail && (
        <div className="modal-overlay" onClick={() => setSelectedInboxEmail(null)}>
          <div className="email-view-modal" onClick={(e) => e.stopPropagation()} data-testid="inbox-email-modal">
            <button className="modal-close-btn" onClick={() => setSelectedInboxEmail(null)}>
              <X size={24} />
            </button>
            
            <div className="email-view-header">
              <h2>{selectedInboxEmail.subject}</h2>
              <div className="email-view-meta">
                <div className="email-from-info">
                  <div className="email-avatar">
                    {selectedInboxEmail.from_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <span className="email-from-name">{selectedInboxEmail.from_name}</span>
                    <span className="email-from-address">&lt;{selectedInboxEmail.from_email}&gt;</span>
                  </div>
                </div>
                <span className="email-date">{new Date(selectedInboxEmail.date).toLocaleString()}</span>
              </div>
            </div>
            
            <div className="email-view-body" data-testid="inbox-email-body">
              {selectedInboxEmail.body_html ? (
                <iframe
                  title="email-content"
                  sandbox=""
                  srcDoc={selectedInboxEmail.body_html}
                  style={{ width: '100%', minHeight: '360px', border: 'none', background: '#fff', borderRadius: '8px' }}
                />
              ) : (
                <pre>{selectedInboxEmail.body}</pre>
              )}
            </div>
            
            {selectedInboxEmail.attachments?.length > 0 && (
              <div className="email-view-attachments">
                <h4>📎 Attachments</h4>
                {selectedInboxEmail.attachments.map((filename, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="attachment-name"
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    onClick={() => downloadInboxAttachment(selectedInboxEmail.id, idx, filename)}
                    data-testid={`inbox-attachment-download-${idx}`}
                  >
                    <Download size={14} /> {filename}
                  </button>
                ))}
              </div>
            )}
            
            <div className="email-view-actions">
              <button className="btn-delete-email" onClick={() => deleteInboxEmail(selectedInboxEmail.id)} data-testid="inbox-email-modal-delete">
                <Trash2 size={18} />
                Delete
              </button>
              <button className="btn-reply" onClick={() => replyToEmail(selectedInboxEmail)} data-testid="inbox-email-reply-btn">
                <Send size={18} />
                Reply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statements Tab */}
      {activeTab === 'statements' && (
        <div className="admin-section">
          <div className="section-header">
            <h2><FileText size={24} /> Payment Statements</h2>
          </div>
          
          <div className="statements-container">
            {/* Summary Cards */}
            <div className="statements-summary">
              <div className="summary-card">
                <span className="summary-label">Total Services Value</span>
                <span className="summary-value">
                  AED {(bookings.reduce((sum, b) => {
                    const price = parseFloat(String(b.servicePrice || b.price || '0').replace(/[^0-9.]/g, '')) || 0;
                    return sum + price;
                  }, 0)
                    + bookingServices.reduce((s, c) => s + Number(c.total_amount || 0), 0)
                  ).toLocaleString()}
                </span>
              </div>
              <div className="summary-card paid">
                <span className="summary-label">Total Paid</span>
                <span className="summary-value">
                  AED {(bookings.reduce((sum, b) => sum + (parseFloat(b.paidAmount) || 0), 0)
                    + bookingServices.reduce((s, c) => s + Number(c.paid_amount || 0), 0)
                  ).toLocaleString()}
                </span>
              </div>
              <div className="summary-card pending">
                <span className="summary-label">Total Pending</span>
                <span className="summary-value">
                  AED {(bookings.reduce((sum, b) => {
                    const price = parseFloat(String(b.servicePrice || b.price || '0').replace(/[^0-9.]/g, '')) || 0;
                    const paid = parseFloat(b.paidAmount) || 0;
                    return sum + Math.max(0, price - paid);
                  }, 0)
                    + bookingServices.reduce((s, c) => s + Number(c.remaining || 0), 0)
                  ).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Statements Table */}
            <div className="statements-table-container">
              <table className="statements-table">
                <thead>
                  <tr>
                    <th>Application #</th>
                    <th>Customer</th>
                    <th>Passport</th>
                    <th>Mobile</th>
                    <th>Nationality</th>
                    <th>Service</th>
                    <th>Total Price</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => {
                    const totalPrice = parseFloat(String(booking.servicePrice || booking.price || '0').replace(/[^0-9.]/g, '')) || 0;
                    const paidAmount = parseFloat(booking.paidAmount) || 0;
                    const balance = Math.max(0, totalPrice - paidAmount);
                    const isPaid = balance === 0 && paidAmount > 0;
                    const isPartiallyPaid = paidAmount > 0 && balance > 0;
                    
                    return (
                      <tr key={booking.id} className={isPaid ? 'paid-row' : isPartiallyPaid ? 'partial-row' : ''}>
                        <td className="booking-number" data-label="App #">{booking.bookingNumber || booking.id}</td>
                        <td data-label="Customer">
                          <div className="customer-info">
                            <span className="customer-name">{booking.name || booking.fullName || 'N/A'}</span>
                          </div>
                        </td>
                        <td data-label="Passport">{booking.passportNumber || 'N/A'}</td>
                        <td data-label="Mobile">{booking.agentPhone || booking.mobile || booking.phone || 'N/A'}</td>
                        <td data-label="Nationality">{booking.nationality || 'N/A'}</td>
                        <td className="service-name" data-label="Service">
                          {booking.service || booking.serviceName || 'N/A'}
                          {booking.debitCard === 'yes' && (
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              marginLeft: '6px',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              background: 'rgba(94, 252, 232, 0.12)',
                              border: '1px solid rgba(94, 252, 232, 0.3)',
                              fontSize: '10px',
                              color: '#5EFCE8',
                              fontWeight: '600'
                            }}>
                              Debit Card
                            </div>
                          )}
                        </td>
                        <td className="amount" data-label="Total">AED {totalPrice.toLocaleString()}</td>
                        <td className="amount paid-amount" data-label="Paid">AED {paidAmount.toLocaleString()}</td>
                        <td className={`amount balance ${balance === 0 ? 'zero' : 'pending'}`} data-label="Balance">
                          AED {balance.toLocaleString()}
                        </td>
                        <td data-label="Status">
                          {isPaid ? (
                            <span className="status-badge fully-paid">
                              <CheckCircle size={14} />
                              Fully Paid
                            </span>
                          ) : isPartiallyPaid ? (
                            <span className="status-badge partial-paid">
                              <Clock size={14} />
                              Partial
                            </span>
                          ) : (
                            <span className="status-badge unpaid">
                              <AlertCircle size={14} />
                              Unpaid
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Custom Service Records — from Bookings → Services only (Accounting is excluded) */}
            {bookingServices.length > 0 && (
              <div className="statements-table-container" style={{ marginTop: '24px' }} data-testid="statements-services-section">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 14px', fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  <Receipt size={18} style={{ color: '#5EFCE8' }} />
                  Custom Services
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    ({bookingServices.length} record{bookingServices.length === 1 ? '' : 's'})
                  </span>
                </h3>
                <table className="statements-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Email</th>
                      <th>Service</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingServices.map(svc => {
                      const total = Number(svc.total_amount) || 0;
                      const paid = Number(svc.paid_amount) || 0;
                      const balance = Number(svc.remaining) || Math.max(0, total - paid);
                      const isPaid = balance === 0 && paid > 0;
                      const isPartial = paid > 0 && balance > 0;
                      const rawDate = svc.start_date || svc.created_at;
                      const dateStr = rawDate ? new Date(rawDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                      return (
                        <tr key={svc.id} className={isPaid ? 'paid-row' : isPartial ? 'partial-row' : ''}>
                          <td data-label="Date">{dateStr}</td>
                          <td data-label="Client">
                            <div className="customer-info">
                              <span className="customer-name">{svc.client || 'N/A'}</span>
                            </div>
                          </td>
                          <td data-label="Email">{svc.client_email || '—'}</td>
                          <td className="service-name" data-label="Service">{svc.service || '—'}</td>
                          <td className="amount" data-label="Total">AED {total.toLocaleString()}</td>
                          <td className="amount paid-amount" data-label="Paid">AED {paid.toLocaleString()}</td>
                          <td className={`amount balance ${balance === 0 ? 'zero' : 'pending'}`} data-label="Balance">AED {balance.toLocaleString()}</td>
                          <td data-label="Status">
                            {isPaid ? (
                              <span className="status-badge fully-paid"><CheckCircle size={14} /> Fully Paid</span>
                            ) : isPartial ? (
                              <span className="status-badge partial-paid"><Clock size={14} /> Partial</span>
                            ) : (
                              <span className="status-badge unpaid"><AlertCircle size={14} /> Unpaid</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pending Payments Only */}
            <div className="pending-payments-section">
              <h3>
                <AlertCircle size={20} />
                Pending Payments
              </h3>
              <div className="pending-list">
                {bookings.filter(b => {
                  const totalPrice = parseFloat(String(b.servicePrice || b.price || '0').replace(/[^0-9.]/g, '')) || 0;
                  const paidAmount = parseFloat(b.paidAmount) || 0;
                  return totalPrice > paidAmount;
                }).map((booking) => {
                  const totalPrice = parseFloat(String(booking.servicePrice || booking.price || '0').replace(/[^0-9.]/g, '')) || 0;
                  const paidAmount = parseFloat(booking.paidAmount) || 0;
                  const balance = totalPrice - paidAmount;
                  
                  return (
                    <div key={booking.id} className="pending-item">
                      <div className="pending-customer">
                        <div className="pending-avatar">
                          {(booking.name || booking.fullName || 'U').charAt(0)}
                        </div>
                        <div className="pending-details">
                          <span className="pending-name">{booking.name || booking.fullName}</span>
                          <span className="pending-booking">#{booking.bookingNumber || booking.id}</span>
                        </div>
                      </div>
                      <div className="pending-service">
                        {booking.service || booking.serviceName}
                      </div>
                      <div className="pending-amounts">
                        <div className="pending-total">
                          <span className="label">Total:</span>
                          <span className="value">AED {totalPrice.toLocaleString()}</span>
                        </div>
                        <div className="pending-paid">
                          <span className="label">Paid:</span>
                          <span className="value">AED {paidAmount.toLocaleString()}</span>
                        </div>
                        <div className="pending-balance">
                          <span className="label">Balance:</span>
                          <span className="value">AED {balance.toLocaleString()}</span>
                        </div>
                      </div>
                      <button 
                        className="btn-create-invoice"
                        onClick={() => {
                          setInvoiceForm({
                            user_email: booking.email || '',
                            user_name: booking.name || booking.fullName || '',
                            items: [{ 
                              description: booking.service || booking.serviceName || 'Visa Service', 
                              quantity: 1, 
                              price: balance 
                            }],
                            notes: `Payment for ${booking.service || booking.serviceName}. Application #${booking.bookingNumber || booking.id}`,
                            due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                            booking_id: booking.id
                          });
                          setInvoiceModal('add');
                        }}
                      >
                        <Plus size={16} />
                        Create Invoice
                      </button>
                    </div>
                  );
                })}
                {bookings.filter(b => {
                  const totalPrice = parseFloat(String(b.servicePrice || b.price || '0').replace(/[^0-9.]/g, '')) || 0;
                  const paidAmount = parseFloat(b.paidAmount) || 0;
                  return totalPrice > paidAmount;
                }).length === 0 && (
                  <div className="no-pending">
                    <CheckCircle size={48} />
                    <p>All payments are complete!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Visa Applications */}
      {activeTab === 'global-visa' && (
        <div className="admin-section" data-testid="admin-global-visa">
          <div className="section-header">
            <h2><Globe size={24} /> Global Visa Applications</h2>
            <button className="btn-secondary" onClick={loadVisaApplications} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          {loadingVisaApps ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              Loading applications...
            </div>
          ) : visaApplications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <Globe size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>No visa applications yet</p>
            </div>
          ) : (
            <div className="statements-table-container">
              <table className="statements-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Citizen</th>
                    <th>Travel Destination</th>
                    <th>Visa Type</th>
                    <th>UAE Resident</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visaApplications.map((app, idx) => (
                    <tr key={app.id}>
                      <td>{idx + 1}</td>
                      <td style={{ fontWeight: '600' }}>{app.name}</td>
                      <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{app.email || '-'}</td>
                      <td>{app.mobile}</td>
                      <td>
                        <span style={{ 
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '3px 10px', borderRadius: '6px',
                          background: 'rgba(96, 165, 250, 0.1)', 
                          border: '1px solid rgba(96, 165, 250, 0.2)',
                          fontSize: '12px', fontWeight: '600', color: '#60A5FA'
                        }}>
                          {app.passport_country}
                        </span>
                      </td>
                      <td>
                        <span style={{ 
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '3px 10px', borderRadius: '6px',
                          background: 'rgba(94, 252, 232, 0.1)', 
                          border: '1px solid rgba(94, 252, 232, 0.2)',
                          fontSize: '12px', fontWeight: '600', color: '#5EFCE8'
                        }}>
                          {app.destination_country}
                        </span>
                      </td>
                      <td>
                        <span style={{ 
                          padding: '3px 10px', borderRadius: '6px',
                          background: app.visa_type === 'eVisa' ? 'rgba(255, 184, 0, 0.1)' : 
                                     app.visa_type?.includes('Arrival') ? 'rgba(54, 211, 153, 0.1)' : 'rgba(96, 165, 250, 0.1)',
                          border: `1px solid ${app.visa_type === 'eVisa' ? 'rgba(255, 184, 0, 0.2)' : 
                                   app.visa_type?.includes('Arrival') ? 'rgba(54, 211, 153, 0.2)' : 'rgba(96, 165, 250, 0.2)'}`,
                          fontSize: '12px', fontWeight: '600',
                          color: app.visa_type === 'eVisa' ? '#FFB800' : 
                                 app.visa_type?.includes('Arrival') ? '#36D399' : '#60A5FA'
                        }}>
                          {app.visa_type}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                          background: app.uae_resident === 'yes' ? 'rgba(94, 252, 232, 0.1)' : 'rgba(255, 77, 106, 0.1)',
                          border: `1px solid ${app.uae_resident === 'yes' ? 'rgba(94, 252, 232, 0.2)' : 'rgba(255, 77, 106, 0.2)'}`,
                          color: app.uae_resident === 'yes' ? '#5EFCE8' : '#FF4D6A'
                        }}>
                          {app.uae_resident === 'yes' ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {app.created_at ? new Date(app.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                      </td>
                      <td>
                        <select
                          value={app.status || 'new'}
                          onChange={(e) => updateVisaAppStatus(app.id, e.target.value)}
                          data-testid={`visa-app-status-${app.id}`}
                          style={{
                            padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                            background: app.status === 'contacted' ? 'rgba(94, 252, 232, 0.15)' :
                                       app.status === 'processing' ? 'rgba(255, 184, 0, 0.15)' :
                                       app.status === 'completed' ? 'rgba(54, 211, 153, 0.15)' : 'rgba(96, 165, 250, 0.15)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: app.status === 'contacted' ? '#5EFCE8' :
                                   app.status === 'processing' ? '#FFB800' :
                                   app.status === 'completed' ? '#36D399' : '#60A5FA',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="processing">Processing</option>
                          <option value="completed">Completed</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td>
                        <button
                          onClick={() => deleteVisaApp(app.id)}
                          data-testid={`visa-app-delete-${app.id}`}
                          style={{
                            background: 'rgba(255, 77, 106, 0.1)', border: '1px solid rgba(255, 77, 106, 0.2)',
                            borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', color: '#FF4D6A'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Accounting */}
      {activeTab === 'accounting' && (
        <div className="admin-section" data-testid="admin-accounting">
          <div className="section-header">
            <h2><CreditCard size={24} /> Accounting</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-secondary" onClick={() => { loadAccounting(); loadClientServices(); }} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={16} /> Refresh
              </button>
            </div>
          </div>

          {/* ===== CLIENT SERVICES ===== */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
                  Track service contracts & partial payments
                </p>
                <button className="btn-primary" onClick={() => setShowAddService(true)} data-testid="add-service-btn"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Plus size={16} /> New Service
                </button>
              </div>

              {/* Summary cards for services */}
              {clientServices.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ padding: '16px', borderRadius: '14px', background: 'rgba(96, 165, 250, 0.08)', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
                    <div style={{ fontSize: '11px', color: '#60A5FA', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Value</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#60A5FA', marginTop: '4px' }}>
                      {clientServices.reduce((s, c) => s + Number(c.total_amount || 0), 0).toLocaleString()} AED
                    </div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '14px', background: 'rgba(94, 252, 232, 0.08)', border: '1px solid rgba(94, 252, 232, 0.2)' }}>
                    <div style={{ fontSize: '11px', color: '#5EFCE8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Collected</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#5EFCE8', marginTop: '4px' }}>
                      {clientServices.reduce((s, c) => s + Number(c.paid_amount || 0), 0).toLocaleString()} AED
                    </div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '14px', background: 'rgba(255, 184, 0, 0.08)', border: '1px solid rgba(255, 184, 0, 0.2)' }}>
                    <div style={{ fontSize: '11px', color: '#FFB800', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Outstanding</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: '#FFB800', marginTop: '4px' }}>
                      {clientServices.reduce((s, c) => s + Number(c.remaining || 0), 0).toLocaleString()} AED
                    </div>
                  </div>
                </div>
              )}

              {/* Add New Service Modal */}
              {showAddService && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
                  onClick={(e) => { if (e.target === e.currentTarget) setShowAddService(false); }}>
                  <div data-testid="add-service-modal" style={{
                    background: 'var(--card-bg, #1a2235)', borderRadius: '20px', padding: '28px 24px', width: '100%', maxWidth: '420px',
                    border: '1px solid var(--border-color, rgba(255,255,255,0.1))', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                  }}>
                    <button onClick={() => setShowAddService(false)} style={{ position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}>
                      <X size={20} />
                    </button>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', margin: '0 0 20px' }}>New Service Contract</h3>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Client Name</label>
                      <input type="text" value={csForm.client} onChange={(e) => setCsForm({ ...csForm, client: e.target.value })} placeholder="Client full name" data-testid="service-client" list="client-suggestions"
                        style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Service Name</label>
                      <input type="text" value={csForm.service} onChange={(e) => setCsForm({ ...csForm, service: e.target.value })} placeholder="e.g. Freelance Visa, Family Visa" data-testid="service-name"
                        style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Paying To (Receiver)</label>
                      <input type="text" value={csForm.receiver} onChange={(e) => setCsForm({ ...csForm, receiver: e.target.value })} placeholder="Receiver name" data-testid="service-receiver"
                        style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <div style={{ marginBottom: '14px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Receiver Email</label>
                      <input type="email" value={csForm.receiver_email} onChange={(e) => setCsForm({ ...csForm, receiver_email: e.target.value })} placeholder="payment@registantours.com" data-testid="service-receiver-email"
                        style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>Payment notifications will be sent to this email</div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Price (AED)</label>
                      <input type="number" value={csForm.total_amount} onChange={(e) => setCsForm({ ...csForm, total_amount: e.target.value })} placeholder="e.g. 5500" data-testid="service-total"
                        style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <button onClick={createClientService} disabled={!csForm.client || !csForm.service || !csForm.total_amount} data-testid="service-submit"
                      style={{
                        width: '100%', padding: '13px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                        background: (!csForm.client || !csForm.service || !csForm.total_amount) ? 'rgba(255,255,255,0.1)' : 'var(--accent-color, #5B8DEF)',
                        color: (!csForm.client || !csForm.service || !csForm.total_amount) ? 'var(--text-secondary)' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                      }}>
                      <Plus size={16} /> Create Service
                    </button>
                  </div>
                </div>
              )}

              {/* Payment Modal — lifted to top-level (see end of return) so it
                  works from any tab, including Bookings → Services. */}

              {/* Service Cards */}
              {loadingServices ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>
              ) : clientServices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                  <Briefcase size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
                  <p>No service contracts yet. Click "New Service" to create one.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {clientServices.map(svc => (
                    <div key={svc.id} data-testid={`service-card-${svc.id}`}
                      style={{
                        background: 'var(--card-bg, #1a2235)', borderRadius: '16px', overflow: 'hidden',
                        border: `1px solid ${svc.status === 'paid' ? 'rgba(94,252,232,0.25)' : svc.status === 'partial' ? 'rgba(255,184,0,0.25)' : 'var(--border-color, rgba(255,255,255,0.1))'}`,
                        transition: 'all 0.2s'
                      }}>
                      {/* Card Header */}
                      <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer' }}
                        onClick={() => setExpandedService(expandedService === svc.id ? null : svc.id)}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)' }}>{svc.client}</span>
                            <span style={{
                              padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
                              background: svc.status === 'paid' ? 'rgba(94,252,232,0.12)' : svc.status === 'partial' ? 'rgba(255,184,0,0.12)' : 'rgba(255,255,255,0.06)',
                              color: svc.status === 'paid' ? '#5EFCE8' : svc.status === 'partial' ? '#FFB800' : 'var(--text-secondary)',
                              border: `1px solid ${svc.status === 'paid' ? 'rgba(94,252,232,0.2)' : svc.status === 'partial' ? 'rgba(255,184,0,0.2)' : 'rgba(255,255,255,0.1)'}`
                            }}>
                              {svc.status}
                            </span>
                            {svc.created_at && (() => {
                              const d = new Date(svc.created_at);
                              if (isNaN(d)) return null;
                              const today = new Date();
                              const isToday = d.toDateString() === today.toDateString();
                              const dayMs = 24 * 60 * 60 * 1000;
                              const diffDays = Math.floor((today - d) / dayMs);
                              const isYesterday = diffDays === 1 && !isToday;
                              const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                              const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                              const labelText = isToday
                                ? `Added today · ${timeStr}`
                                : isYesterday
                                  ? `Added yesterday · ${timeStr}`
                                  : `Added ${dateStr}`;
                              return (
                                <span
                                  data-testid={`svc-created-${svc.id}`}
                                  title={`${dateStr} at ${timeStr}`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '2px 9px', borderRadius: '999px',
                                    fontSize: '10px', fontWeight: '600', letterSpacing: '0.3px',
                                    background: isToday
                                      ? 'linear-gradient(135deg, rgba(94,252,232,0.18), rgba(94,252,232,0.06))'
                                      : 'rgba(96,165,250,0.10)',
                                    color: isToday ? '#5EFCE8' : '#93C5FD',
                                    border: `1px solid ${isToday ? 'rgba(94,252,232,0.35)' : 'rgba(96,165,250,0.25)'}`,
                                    boxShadow: isToday ? '0 0 12px rgba(94,252,232,0.15)' : 'none'
                                  }}
                                >
                                  <Calendar size={10} style={{ opacity: 0.9 }} />
                                  {labelText}
                                </span>
                              );
                            })()}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{svc.service}</div>
                          {svc.receiver && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>Paying to: <span style={{ color: '#60A5FA' }}>{svc.receiver}</span> {svc.receiver_email && <span style={{ opacity: 0.6 }}>({svc.receiver_email})</span>}</div>}
                        </div>

                        {/* Progress bar */}
                        <div style={{ width: '120px', flexShrink: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            <span>{Number(svc.paid_amount).toLocaleString()}</span>
                            <span>{Number(svc.total_amount).toLocaleString()}</span>
                          </div>
                          <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: '3px', transition: 'width 0.3s',
                              width: `${Math.min((svc.paid_amount / svc.total_amount) * 100, 100)}%`,
                              background: svc.status === 'paid' ? '#5EFCE8' : '#FFB800'
                            }} />
                          </div>
                        </div>

                        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '90px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: svc.remaining > 0 ? '#FFB800' : '#5EFCE8' }}>
                            {Number(svc.remaining).toLocaleString()}
                          </div>
                          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Remaining</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          {svc.status !== 'paid' && (
                            <button onClick={(e) => { e.stopPropagation(); setCsPayModal(svc.id); setCsPayForm({ amount: '', date: new Date().toISOString().split('T')[0] }); }} data-testid={`pay-btn-${svc.id}`}
                              style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#5EFCE8', color: '#0a1628', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <DollarSign size={12} /> Pay
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); downloadServiceReceipt(svc.id, svc.client); }} data-testid={`receipt-btn-${svc.id}`}
                            title="Download branded PDF receipt"
                            style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', background: 'rgba(212,165,116,0.12)', color: '#D4A574', border: '1px solid rgba(212,165,116,0.3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FileText size={12} /> Receipt
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); openCsEdit(svc); }} data-testid={`cs-edit-btn-${svc.id}`}
                            title="Edit order"
                            style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', background: 'rgba(96,165,250,0.12)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.3)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Edit size={12} /> Edit
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteClientService(svc.id); }}
                            style={{ padding: '7px', borderRadius: '8px', cursor: 'pointer', background: 'rgba(255,77,106,0.1)', border: '1px solid rgba(255,77,106,0.2)', color: '#FF4D6A' }}>
                            <Trash2 size={14} />
                          </button>
                          {expandedService === svc.id ? <ChevronUp size={16} style={{ color: 'var(--text-secondary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-secondary)' }} />}
                        </div>
                      </div>

                      {/* Expanded: Payment History */}
                      {expandedService === svc.id && svc.payments && svc.payments.length > 0 && (
                        <div style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))', padding: '14px 20px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Payment History</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {svc.payments.map((p, i) => {
                              const runningPaid = svc.payments.slice(0, i + 1).reduce((s, pp) => s + Number(pp.amount), 0);
                              const runningRemaining = svc.total_amount - runningPaid;
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)' }}>
                                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(94,252,232,0.12)', border: '1px solid rgba(94,252,232,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: '#5EFCE8', flexShrink: 0 }}>
                                    {i + 1}
                                  </div>
                                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '80px' }}>
                                    {p.date ? new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                  </div>
                                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#5EFCE8' }}>
                                    +{Number(p.amount).toLocaleString()} AED
                                  </div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                                    Balance: <span style={{ fontWeight: '600', color: runningRemaining > 0 ? '#FFB800' : '#5EFCE8' }}>{Math.max(runningRemaining, 0).toLocaleString()} AED</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {expandedService === svc.id && (!svc.payments || svc.payments.length === 0) && (
                        <div style={{ borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))', padding: '14px 20px', textAlign: 'center', fontSize: '12px', color: 'var(--text-secondary)' }}>
                          No payments recorded yet
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
        </div>
      )}

      {/* Email Compose Modal */}
      {emailModal === 'compose' && (
        <div className="modal-overlay" onClick={() => setEmailModal(null)}>
          <div className="email-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setEmailModal(null)}>
              <X size={24} />
            </button>
            
            <div className="email-modal-header">
              <Mail size={32} className="email-icon" />
              <h2>Send Email</h2>
              <p className="email-sender">From: support@registantours.com</p>
            </div>

            <div className="email-modal-content">
              <div className="form-group">
                <label>To *</label>
                <input
                  type="email"
                  value={emailForm.to_email}
                  onChange={(e) => setEmailForm({...emailForm, to_email: e.target.value})}
                  placeholder="recipient@email.com"
                  data-testid="email-compose-to"
                />
              </div>
              
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                  placeholder="Email subject"
                  data-testid="email-compose-subject"
                />
              </div>
              
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                  placeholder="Type your message here..."
                  rows={8}
                  data-testid="email-compose-message"
                />
              </div>
              
              {/* Attachments */}
              <div className="form-group">
                <label>Attachments</label>
                <input
                  type="file"
                  ref={emailAttachmentRef}
                  onChange={handleEmailAttachment}
                  multiple
                  style={{ display: 'none' }}
                />
                <button 
                  type="button"
                  className="btn-attach"
                  onClick={() => emailAttachmentRef.current?.click()}
                  data-testid="email-compose-attach-btn"
                >
                  <Upload size={18} />
                  Add Attachment
                </button>
                
                {emailAttachments.length > 0 && (
                  <div className="attachments-list">
                    {emailAttachments.map((file, index) => (
                      <div key={index} className="attachment-item">
                        <span className="attachment-icon">📎</span>
                        <span className="attachment-name">{file.filename}</span>
                        <span className="attachment-size">({(file.size / 1024).toFixed(1)} KB)</span>
                        <button 
                          type="button"
                          className="btn-remove-attachment"
                          onClick={() => removeEmailAttachment(index)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="email-modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setEmailModal(null)}
                disabled={sendingEmail}
                data-testid="email-compose-cancel-btn"
              >
                Cancel
              </button>
              <button 
                className="btn-send-email"
                onClick={sendAdminEmail}
                data-testid="email-compose-send-btn"
                disabled={sendingEmail || !emailForm.to_email || !emailForm.subject || !emailForm.message}
              >
                <Send size={18} />
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {invoiceModal && (
        <div className="modal-overlay" onClick={closeInvoiceModal}>
          <div className="invoice-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeInvoiceModal}>
              <X size={24} />
            </button>
            
            <div className="invoice-modal-header">
              <CreditCard size={32} className="invoice-icon" />
              <h2>Create New Invoice</h2>
            </div>

            <div className="invoice-modal-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Customer Name *</label>
                  <input
                    type="text"
                    value={invoiceForm.user_name}
                    onChange={(e) => setInvoiceForm({...invoiceForm, user_name: e.target.value})}
                    placeholder="Enter customer name"
                  />
                </div>
                <div className="form-group">
                  <label>Customer Email *</label>
                  <input
                    type="email"
                    value={invoiceForm.user_email}
                    onChange={(e) => setInvoiceForm({...invoiceForm, user_email: e.target.value})}
                    placeholder="customer@email.com"
                  />
                </div>
              </div>

              <div className="invoice-items-section">
                <label>Invoice Items</label>
                {invoiceForm.items.map((item, index) => (
                  <div key={index} className="invoice-item-row">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateInvoiceItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    />
                    <input
                      type="number"
                      placeholder="Price (AED)"
                      min="0"
                      value={item.price}
                      onChange={(e) => updateInvoiceItem(index, 'price', parseFloat(e.target.value) || 0)}
                    />
                    {invoiceForm.items.length > 1 && (
                      <button 
                        type="button" 
                        className="btn-remove-item"
                        onClick={() => removeInvoiceItem(index)}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn-add-item" onClick={addInvoiceItem}>
                  <Plus size={16} />
                  Add Item
                </button>
              </div>

              <div className="invoice-total-display">
                <strong>Total: AED {calculateInvoiceTotal().toFixed(2)}</strong>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={invoiceForm.due_date}
                    onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={invoiceForm.notes}
                  onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                  placeholder="Add any notes for the customer..."
                  rows={3}
                />
              </div>
            </div>

            <div className="invoice-modal-footer">
              <button className="btn-cancel" onClick={closeInvoiceModal}>
                Cancel
              </button>
              <button 
                className="btn-send-invoice" 
                onClick={saveInvoice}
                disabled={savingInvoice}
              >
                {savingInvoice ? 'Sending...' : (
                  <>
                    <Send size={18} />
                    Send Invoice
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <div className="modal-overlay" onClick={() => setPaymentModal(null)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setPaymentModal(null)}>
              <X size={24} />
            </button>
            
            <div className="payment-modal-header">
              <DollarSign size={32} className="payment-icon" />
              <h2>Add Payment</h2>
              <p className="invoice-ref">Invoice: {paymentModal.invoice_number}</p>
            </div>

            <div className="payment-modal-summary">
              <div className="summary-row">
                <span>Customer:</span>
                <strong>{paymentModal.user_name}</strong>
              </div>
              <div className="summary-row">
                <span>Total Invoice:</span>
                <strong>AED {(paymentModal.total || 0).toLocaleString()}</strong>
              </div>
              <div className="summary-row">
                <span>Already Paid:</span>
                <strong className="text-green">AED {(paymentModal.paid_amount || 0).toLocaleString()}</strong>
              </div>
              <div className="summary-row highlight">
                <span>Remaining Balance:</span>
                <strong className="text-yellow">AED {((paymentModal.total || 0) - (paymentModal.paid_amount || 0)).toLocaleString()}</strong>
              </div>
            </div>

            <div className="payment-form">
              <div className="form-group">
                <label>Payment Amount (AED) *</label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="Enter payment amount"
                  min="0"
                  max={(paymentModal.total || 0) - (paymentModal.paid_amount || 0)}
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="online">Online Payment</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Any notes about this payment..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Upload Invoice / Receipt (Optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {paymentForm.invoice_file ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', flex: 1 }}>
                      <FileText size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{paymentForm.invoice_file.name}</span>
                      <button type="button" onClick={() => setPaymentForm({ ...paymentForm, invoice_file: null })} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
                      background: 'rgba(59,130,246,0.08)', border: '2px dashed rgba(59,130,246,0.3)',
                      borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#3B82F6', fontWeight: 600, flex: 1, justifyContent: 'center'
                    }}>
                      <Upload size={18} />
                      Upload Invoice / Receipt
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          if (e.target.files[0]) setPaymentForm({ ...paymentForm, invoice_file: e.target.files[0] });
                        }}
                        style={{ display: 'none' }}
                        data-testid="payment-invoice-upload"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="quick-amount-buttons">
                <span>Quick Amount:</span>
                <button 
                  type="button" 
                  onClick={() => setPaymentForm({ ...paymentForm, amount: ((paymentModal.total || 0) - (paymentModal.paid_amount || 0)).toString() })}
                  className="quick-btn full"
                >
                  Full Balance
                </button>
                <button 
                  type="button" 
                  onClick={() => setPaymentForm({ ...paymentForm, amount: (((paymentModal.total || 0) - (paymentModal.paid_amount || 0)) / 2).toString() })}
                  className="quick-btn half"
                >
                  50%
                </button>
              </div>
            </div>

            <div className="payment-modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setPaymentModal(null)}
              >
                Cancel
              </button>
              <button 
                className="btn-submit-payment"
                onClick={submitPayment}
                disabled={savingPayment || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
              >
                {savingPayment ? (
                  <>
                    <RefreshCw size={18} className="spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    Record Payment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <div className="tab-content" data-testid="seo-tab">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Search size={24} style={{ color: '#10B981' }} /> SEO Manager
            </h2>

            {seoEditing ? (
              <div style={{ padding: '28px', borderRadius: '14px', border: '1px solid rgba(16, 185, 129, 0.25)', background: 'var(--card-bg, rgba(255,255,255,0.03))' }} data-testid="seo-edit-form">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#10B981' }}>
                    Editing: {seoPages.find(p => p.slug === seoEditing)?.label || seoEditing}
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: '10px', fontWeight: 400 }}>
                      ({seoPages.find(p => p.slug === seoEditing)?.url})
                    </span>
                  </h3>
                  <button onClick={() => setSeoEditing(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}>
                    <X size={20} />
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Page Title</label>
                    <input
                      value={seoForm.title}
                      onChange={e => setSeoForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Page title for Google search results..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px' }}
                      data-testid="seo-title-input"
                    />
                    <div style={{ fontSize: '11px', color: seoForm.title.length > 60 ? '#EF4444' : 'var(--text-secondary)', marginTop: '4px' }}>{seoForm.title.length}/60 characters</div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Meta Description</label>
                    <textarea
                      value={seoForm.description}
                      onChange={e => setSeoForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Description shown in Google search results..."
                      rows={3}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px', resize: 'vertical' }}
                      data-testid="seo-description-input"
                    />
                    <div style={{ fontSize: '11px', color: seoForm.description.length > 160 ? '#EF4444' : 'var(--text-secondary)', marginTop: '4px' }}>{seoForm.description.length}/160 characters</div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Keywords</label>
                    <input
                      value={seoForm.keywords}
                      onChange={e => setSeoForm(prev => ({ ...prev, keywords: e.target.value }))}
                      placeholder="keyword1, keyword2, keyword3..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px' }}
                      data-testid="seo-keywords-input"
                    />
                  </div>

                  {/* Auto-translate button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                    <button
                      onClick={autoTranslateSeo}
                      disabled={seoTranslating}
                      title="Automatically translate EN → RU + UZ using AI"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        padding: '10px 16px', borderRadius: '10px', border: 'none', cursor: seoTranslating ? 'wait' : 'pointer',
                        background: 'linear-gradient(135deg, #6D28D9 0%, #2563EB 100%)',
                        color: '#fff', fontSize: '13px', fontWeight: 700,
                        boxShadow: '0 4px 14px rgba(109, 40, 217, 0.35)',
                        opacity: seoTranslating ? 0.7 : 1,
                      }}
                      data-testid="seo-auto-translate-btn"
                    >
                      <Globe size={16} />
                      {seoTranslating ? 'Translating…' : 'Auto-Translate EN → RU + UZ'}
                    </button>
                  </div>

                  {/* Russian SEO */}
                  <div style={{ borderTop: '2px solid rgba(59, 130, 246, 0.3)', paddingTop: '16px', marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '18px' }}>🇷🇺</span>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#3B82F6' }}>Russian / Русский</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Title (RU)</label>
                        <input
                          value={seoForm.title_ru}
                          onChange={e => setSeoForm(prev => ({ ...prev, title_ru: e.target.value }))}
                          placeholder="Заголовок страницы на русском..."
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.3)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px' }}
                          data-testid="seo-title-ru"
                        />
                        <div style={{ fontSize: '11px', color: (seoForm.title_ru?.length || 0) > 60 ? '#EF4444' : 'var(--text-secondary)', marginTop: '4px' }}>{seoForm.title_ru?.length || 0}/60</div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Description (RU)</label>
                        <textarea
                          value={seoForm.description_ru}
                          onChange={e => setSeoForm(prev => ({ ...prev, description_ru: e.target.value }))}
                          placeholder="Описание страницы на русском..."
                          rows={2}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.3)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px', resize: 'vertical' }}
                          data-testid="seo-description-ru"
                        />
                        <div style={{ fontSize: '11px', color: (seoForm.description_ru?.length || 0) > 160 ? '#EF4444' : 'var(--text-secondary)', marginTop: '4px' }}>{seoForm.description_ru?.length || 0}/160</div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Keywords (RU)</label>
                        <input
                          value={seoForm.keywords_ru}
                          onChange={e => setSeoForm(prev => ({ ...prev, keywords_ru: e.target.value }))}
                          placeholder="ключевое слово1, ключевое слово2..."
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.3)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px' }}
                          data-testid="seo-keywords-ru"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Uzbek SEO */}
                  <div style={{ borderTop: '2px solid rgba(16, 185, 129, 0.3)', paddingTop: '16px', marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '18px' }}>🇺🇿</span>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#10B981' }}>Uzbek / O'zbek</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Title (UZ)</label>
                        <input
                          value={seoForm.title_uz}
                          onChange={e => setSeoForm(prev => ({ ...prev, title_uz: e.target.value }))}
                          placeholder="Sahifa sarlavhasi o'zbek tilida..."
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.3)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px' }}
                          data-testid="seo-title-uz"
                        />
                        <div style={{ fontSize: '11px', color: (seoForm.title_uz?.length || 0) > 60 ? '#EF4444' : 'var(--text-secondary)', marginTop: '4px' }}>{seoForm.title_uz?.length || 0}/60</div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Description (UZ)</label>
                        <textarea
                          value={seoForm.description_uz}
                          onChange={e => setSeoForm(prev => ({ ...prev, description_uz: e.target.value }))}
                          placeholder="Sahifa tavsifi o'zbek tilida..."
                          rows={2}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.3)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px', resize: 'vertical' }}
                          data-testid="seo-description-uz"
                        />
                        <div style={{ fontSize: '11px', color: (seoForm.description_uz?.length || 0) > 160 ? '#EF4444' : 'var(--text-secondary)', marginTop: '4px' }}>{seoForm.description_uz?.length || 0}/160</div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Keywords (UZ)</label>
                        <input
                          value={seoForm.keywords_uz}
                          onChange={e => setSeoForm(prev => ({ ...prev, keywords_uz: e.target.value }))}
                          placeholder="kalit so'z1, kalit so'z2..."
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.3)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '14px' }}
                          data-testid="seo-keywords-uz"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Social Media Image (OG Image)</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {seoForm.ogImage && (
                        <div style={{ position: 'relative' }}>
                          <img src={seoForm.ogImage} alt="OG" style={{ width: '200px', height: '105px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-color)' }} />
                          <button onClick={() => setSeoForm(prev => ({ ...prev, ogImage: '' }))} style={{
                            position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%',
                            background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px'
                          }}><X size={12} /></button>
                        </div>
                      )}
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        width: '200px', height: '105px', borderRadius: '8px', border: '2px dashed rgba(16, 185, 129, 0.3)',
                        background: 'rgba(16, 185, 129, 0.05)', cursor: 'pointer', fontSize: '12px', color: '#10B981', fontWeight: 600
                      }}>
                        <Upload size={20} />
                        {seoUploading ? 'Uploading...' : 'Upload Image'}
                        <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>1200x630px recommended</span>
                        <input type="file" accept="image/*" onChange={uploadSeoImage} style={{ display: 'none' }} disabled={seoUploading} data-testid="seo-image-upload" />
                      </label>
                    </div>
                    {seoForm.ogImage && (
                      <input
                        value={seoForm.ogImage}
                        onChange={e => setSeoForm(prev => ({ ...prev, ogImage: e.target.value }))}
                        placeholder="Or paste image URL..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '12px', marginTop: '8px' }}
                      />
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Page Main Image (Landscape Banner)</label>
                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>This image appears below the header on the page. Recommended: landscape (1200x400px or wider).</p>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {seoForm.mainImage && (
                        <div style={{ position: 'relative' }}>
                          <img src={seoForm.mainImage} alt={seoForm.mainImageAlt || 'Main'} style={{ width: '100%', maxWidth: '400px', height: 'auto', borderRadius: '8px', border: '1px solid var(--border-color)' }} />
                          <button onClick={() => setSeoForm(prev => ({ ...prev, mainImage: '' }))} style={{
                            position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%',
                            background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px'
                          }}><X size={12} /></button>
                        </div>
                      )}
                      <label style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        width: '200px', height: '90px', borderRadius: '8px', border: '2px dashed rgba(59, 130, 246, 0.3)',
                        background: 'rgba(59, 130, 246, 0.05)', cursor: 'pointer', fontSize: '12px', color: '#3B82F6', fontWeight: 600
                      }}>
                        <Upload size={20} />
                        {seoUploading ? 'Uploading...' : 'Upload Banner'}
                        <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>1200x400px landscape</span>
                        <input type="file" accept="image/*" onChange={uploadSeoMainImage} style={{ display: 'none' }} disabled={seoUploading} data-testid="seo-main-image-upload" />
                      </label>
                    </div>
                    {seoForm.mainImage && (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Image Alt Text (SEO)</label>
                          <input
                            value={seoForm.mainImageAlt}
                            onChange={e => setSeoForm(prev => ({ ...prev, mainImageAlt: e.target.value }))}
                            placeholder="Describe the image for SEO..."
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '12px' }}
                            data-testid="seo-main-image-alt"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Image Title</label>
                          <input
                            value={seoForm.mainImageTitle}
                            onChange={e => setSeoForm(prev => ({ ...prev, mainImageTitle: e.target.value }))}
                            placeholder="Image title on hover..."
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--input-bg, rgba(255,255,255,0.05))', color: 'var(--text-primary)', fontSize: '12px' }}
                            data-testid="seo-main-image-title"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Google Preview */}
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px' }}>Google Search Preview</label>
                    <div style={{ padding: '16px', borderRadius: '10px', background: '#fff', border: '1px solid #dfe1e5' }}>
                      <div style={{ fontSize: '13px', color: '#202124', marginBottom: '2px' }}>registantours.com › {seoPages.find(p => p.slug === seoEditing)?.url?.replace('/', '') || ''}</div>
                      <div style={{ fontSize: '20px', color: '#1a0dab', fontWeight: 400, marginBottom: '4px', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {seoForm.title || 'Page Title'}
                      </div>
                      <div style={{ fontSize: '14px', color: '#4d5156', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {seoForm.description || 'Meta description will appear here...'}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button onClick={() => setSeoEditing(null)} style={{
                    padding: '10px 20px', borderRadius: '8px', border: '1px solid var(--border-color)',
                    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                  }}>Cancel</button>
                  <button onClick={saveSeo} disabled={seoSaving} style={{
                    padding: '10px 24px', borderRadius: '8px', border: 'none',
                    background: '#10B981', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700
                  }} data-testid="seo-save-btn">{seoSaving ? 'Saving...' : 'Save SEO'}</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {seoPages.map(page => {
                  const s = seoSettings[page.slug];
                  const hasData = s && (s.title || s.description);
                  return (
                    <div key={page.slug} onClick={() => startEditSeo(page.slug)} style={{
                      display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 18px',
                      borderRadius: '12px', border: '1px solid var(--border-color)',
                      background: 'var(--card-bg, rgba(255,255,255,0.03))', cursor: 'pointer',
                      transition: 'border-color 0.2s, background 0.2s'
                    }} data-testid={`seo-page-${page.slug}`}>
                      <div style={{
                        width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: hasData ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.1)',
                        color: hasData ? '#10B981' : '#EF4444'
                      }}>
                        {hasData ? <Check size={18} /> : <Search size={18} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{page.label}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s?.title || 'No SEO title set — using default'}
                        </div>
                      </div>
                      {s?.ogImage && (
                        <img src={s.ogImage} alt="" style={{ width: '48px', height: '25px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid var(--border-color)' }} />
                      )}
                      <ChevronRight size={18} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Service Modal (Add/Edit) */}
      {serviceModal && (
        <div className="modal-overlay" onClick={closeServiceModal}>
          <div className="service-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeServiceModal}>
              <X size={24} />
            </button>
            
            <div className="service-modal-header">
              <Briefcase size={32} className="service-icon" />
              <h2>{serviceModal === 'add' ? t('addService') : t('editService')}</h2>
            </div>

            <div className="service-modal-content">
              <div className="service-form-grid">
                <div className="form-group full-width">
                  <label>🇬🇧 {t('serviceName')} *</label>
                  <input
                    type="text"
                    value={serviceForm.name}
                    onChange={(e) => handleServiceFormChange('name', e.target.value)}
                    placeholder="e.g., Tourist Visa UAE"
                    data-testid="service-name-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>🇷🇺 Service Name (Russian)</label>
                  <input
                    type="text"
                    value={serviceForm.name_ru}
                    onChange={(e) => handleServiceFormChange('name_ru', e.target.value)}
                    placeholder="e.g., Туристическая виза ОАЭ"
                    data-testid="service-name-ru-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>🇺🇿 Service Name (Uzbek)</label>
                  <input
                    type="text"
                    value={serviceForm.name_uz}
                    onChange={(e) => handleServiceFormChange('name_uz', e.target.value)}
                    placeholder="e.g., BAA turist vizasi"
                    data-testid="service-name-uz-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t('price')} *</label>
                  <input
                    type="text"
                    value={serviceForm.price}
                    onChange={(e) => handleServiceFormChange('price', e.target.value)}
                    placeholder="e.g., $150"
                    data-testid="service-price-input"
                  />
                </div>

                <div className="form-group">
                  <label>🇬🇧 {t('duration')} *</label>
                  <input
                    type="text"
                    value={serviceForm.duration}
                    onChange={(e) => handleServiceFormChange('duration', e.target.value)}
                    placeholder="e.g., 3-5 days"
                    data-testid="service-duration-input"
                  />
                </div>

                <div className="form-group">
                  <label>🇷🇺 {t('duration')} (RU)</label>
                  <input
                    type="text"
                    value={serviceForm.duration_ru}
                    onChange={(e) => handleServiceFormChange('duration_ru', e.target.value)}
                    placeholder="напр., 3-5 рабочих дней"
                    data-testid="service-duration-ru-input"
                  />
                </div>

                <div className="form-group">
                  <label>🇺🇿 {t('duration')} (UZ)</label>
                  <input
                    type="text"
                    value={serviceForm.duration_uz}
                    onChange={(e) => handleServiceFormChange('duration_uz', e.target.value)}
                    placeholder="masalan, 3-5 ish kuni"
                    data-testid="service-duration-uz-input"
                  />
                </div>

                <div className="form-group">
                  <label>🇬🇧 {t('subtitle')}</label>
                  <input
                    type="text"
                    value={serviceForm.subtitle}
                    onChange={(e) => handleServiceFormChange('subtitle', e.target.value)}
                    placeholder="e.g., Fast Processing"
                  />
                </div>

                <div className="form-group">
                  <label>🇷🇺 {t('subtitle')} (RU)</label>
                  <input
                    type="text"
                    value={serviceForm.subtitle_ru}
                    onChange={(e) => handleServiceFormChange('subtitle_ru', e.target.value)}
                    placeholder="напр., Быстрая обработка"
                    data-testid="service-subtitle-ru-input"
                  />
                </div>

                <div className="form-group">
                  <label>🇺🇿 {t('subtitle')} (UZ)</label>
                  <input
                    type="text"
                    value={serviceForm.subtitle_uz}
                    onChange={(e) => handleServiceFormChange('subtitle_uz', e.target.value)}
                    placeholder="masalan, Tez ishlov berish"
                    data-testid="service-subtitle-uz-input"
                  />
                </div>

                <div className="form-group">
                  <label>{t('category')}</label>
                  <select
                    value={serviceForm.category}
                    onChange={(e) => handleServiceFormChange('category', e.target.value)}
                  >
                    <option value="">{t('selectCategory')}</option>
                    <option value="Tourist Visa">Tourist Visa</option>
                    <option value="Business Visa">Business Visa</option>
                    <option value="Work Visa">Work Visa</option>
                    <option value="Student Visa">Student Visa</option>
                    <option value="Transit Visa">Transit Visa</option>
                    <option value="Family Visa">Family Visa</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>🇬🇧 {t('description')} *</label>
                  <textarea
                    value={serviceForm.description}
                    onChange={(e) => handleServiceFormChange('description', e.target.value)}
                    placeholder="Describe the service details, requirements, and benefits..."
                    rows="3"
                    data-testid="service-description-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>🇷🇺 Description (Russian)</label>
                  <textarea
                    value={serviceForm.description_ru}
                    onChange={(e) => handleServiceFormChange('description_ru', e.target.value)}
                    placeholder="Описание услуги на русском..."
                    rows="3"
                    data-testid="service-description-ru-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>🇺🇿 Description (Uzbek)</label>
                  <textarea
                    value={serviceForm.description_uz}
                    onChange={(e) => handleServiceFormChange('description_uz', e.target.value)}
                    placeholder="Xizmat tavsifi o'zbekcha..."
                    rows="3"
                    data-testid="service-description-uz-input"
                  />
                </div>

                <div className="form-group full-width">
                  <label>{t('serviceImage')}</label>
                  <div className="service-image-upload">
                    {serviceImagePreview ? (
                      <div className="image-preview-container">
                        <img src={serviceImagePreview} alt="Preview" className="image-preview" />
                        <button 
                          className="btn-remove-image"
                          onClick={() => {
                            setServiceImage(null);
                            setServiceImagePreview(null);
                            if (serviceImageInputRef.current) serviceImageInputRef.current.value = '';
                          }}
                        >
                          <X size={16} /> Remove
                        </button>
                      </div>
                    ) : (
                      <div 
                        className="image-upload-zone"
                        onClick={() => serviceImageInputRef.current?.click()}
                      >
                        <Upload size={32} />
                        <p>Click to upload image</p>
                        <span>JPG, PNG (max 5MB)</span>
                      </div>
                    )}
                    <input
                      ref={serviceImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleServiceImageSelect}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="service-modal-actions">
              <button className="btn-secondary" onClick={closeServiceModal}>
                {t('cancel')}
              </button>
              <button 
                className="btn-primary"
                onClick={saveService}
                disabled={savingService}
                data-testid="save-service-btn"
              >
                {savingService ? t('loadingChat') : (
                  <>
                    <CheckCircle size={18} />
                    {serviceModal === 'add' ? t('addService') : t('saveChanges')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Steps Modal */}
      {stepsModal && (
        <div className="modal-overlay" onClick={closeStepsModal}>
          <div className="steps-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeStepsModal}>
              <X size={24} />
            </button>
            
            <h2>
              <List size={24} />
              Manage Steps - {stepsModal.name}
            </h2>
            <p className="steps-modal-info">
              Add steps that users will see when they click "View Steps" on this service.
            </p>

            <div className="steps-list">
              {serviceSteps.map((step, index) => (
                <div key={index} className="step-item">
                  <div className="step-header">
                    <span className="step-number">Step {index + 1}</span>
                    <button 
                      className="btn-remove-step"
                      onClick={() => removeStep(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  <div className="step-fields">
                    <div className="form-group">
                      <label>Icon</label>
                      <select
                        value={step.icon}
                        onChange={(e) => updateStep(index, 'icon', e.target.value)}
                      >
                        <option value="FileText">Document</option>
                        <option value="Upload">Upload</option>
                        <option value="CreditCard">Payment</option>
                        <option value="CheckCircle">Verify</option>
                        <option value="Clock">Processing</option>
                        <option value="Send">Submit</option>
                        <option value="Mail">Email</option>
                        <option value="Phone">Call</option>
                        <option value="Users">Interview</option>
                        <option value="Shield">Security</option>
                        <option value="Package">Receive</option>
                        <option value="Star">Complete</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>Title *</label>
                      <input
                        type="text"
                        value={step.title}
                        onChange={(e) => updateStep(index, 'title', e.target.value)}
                        placeholder="e.g., Submit Documents"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        value={step.description}
                        onChange={(e) => updateStep(index, 'description', e.target.value)}
                        placeholder="e.g., Upload your passport and photo"
                        rows={2}
                      />
                    </div>

                    <div style={{ borderTop: '1px solid rgba(59,130,246,0.2)', paddingTop: '10px', marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#3B82F6', marginBottom: '8px' }}>🇷🇺 Russian</div>
                      <div className="form-group" style={{ marginBottom: '8px' }}>
                        <input
                          type="text"
                          value={step.title_ru || ''}
                          onChange={(e) => updateStep(index, 'title_ru', e.target.value)}
                          placeholder="Заголовок на русском"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <textarea
                          value={step.description_ru || ''}
                          onChange={(e) => updateStep(index, 'description_ru', e.target.value)}
                          placeholder="Описание на русском"
                          rows={2}
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(16,185,129,0.2)', paddingTop: '10px', marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#10B981', marginBottom: '8px' }}>🇺🇿 Uzbek</div>
                      <div className="form-group" style={{ marginBottom: '8px' }}>
                        <input
                          type="text"
                          value={step.title_uz || ''}
                          onChange={(e) => updateStep(index, 'title_uz', e.target.value)}
                          placeholder="Sarlavha o'zbek tilida"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <textarea
                          value={step.description_uz || ''}
                          onChange={(e) => updateStep(index, 'description_uz', e.target.value)}
                          placeholder="Tavsif o'zbek tilida"
                          rows={2}
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {serviceSteps.length === 0 && (
                <div className="no-steps">
                  <List size={40} />
                  <p>No steps added yet</p>
                </div>
              )}
            </div>

            <button className="btn-add-step" onClick={addStep}>
              <Plus size={18} />
              Add Step
            </button>

            <div className="steps-modal-actions">
              <button className="btn-secondary" onClick={closeStepsModal}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={saveSteps}
                disabled={savingSteps}
              >
                {savingSteps ? 'Saving...' : (
                  <>
                    <CheckCircle size={18} />
                    Save Steps
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal — rendered at root so it works from ANY tab (Bookings → Services, Accounting, etc.) */}
      {csPayModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setCsPayModal(null); setCsPayMode('payment'); setCsPayForm({ amount: '', date: '', invoice_file: null, reason: '' }); } }}>
          <div data-testid="cs-payment-modal" style={{
            background: 'var(--card-bg, #1a2235)', borderRadius: '20px', padding: '28px 24px', width: '100%', maxWidth: '400px',
            border: '1px solid var(--border-color, rgba(255,255,255,0.1))', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {(() => {
              const svc = clientServices.find(s => s.id === csPayModal) || bookingServices.find(s => s.id === csPayModal);
              const isDeduct = csPayMode === 'deduction';
              return svc ? (
                <>
                  <h3 style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', margin: '0 0 6px' }}>
                    {isDeduct ? 'Deduct / Correct Payment' : 'Record Payment'}
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 4px' }}>
                    <strong>{svc.client}</strong> — {svc.service}
                  </p>
                  {svc.receiver && <p style={{ fontSize: '12px', color: '#60A5FA', margin: '0 0 6px' }}>Paying to: {svc.receiver} {svc.receiver_email && `(${svc.receiver_email})`}</p>}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '18px', padding: '12px', borderRadius: '10px', background: isDeduct ? 'rgba(255,99,71,0.06)' : 'rgba(255,184,0,0.06)', border: `1px solid ${isDeduct ? 'rgba(255,99,71,0.18)' : 'rgba(255,184,0,0.15)'}` }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#60A5FA' }}>{Number(svc.total_amount).toLocaleString()}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Paid</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#5EFCE8' }}>{Number(svc.paid_amount).toLocaleString()}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remaining</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#FFB800' }}>{Number(svc.remaining).toLocaleString()}</div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{isDeduct ? 'Correction Date' : 'Payment Date'}</label>
                    <input type="date" value={csPayForm.date} onChange={(e) => setCsPayForm({ ...csPayForm, date: e.target.value })} data-testid="cs-payment-date"
                      style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {isDeduct ? `Deduct from Outstanding Balance (Max: ${Number(svc.remaining).toLocaleString()} AED)` : `Amount (Max: ${Number(svc.remaining).toLocaleString()} AED)`}
                    </label>
                    <input type="number" value={csPayForm.amount} onChange={(e) => setCsPayForm({ ...csPayForm, amount: e.target.value })} placeholder={isDeduct ? `Max: ${Number(svc.remaining).toLocaleString()}` : `Max: ${Number(svc.remaining).toLocaleString()}`} data-testid="cs-payment-amount"
                      max={svc.remaining}
                      style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                    {csPayForm.amount && !isDeduct && Number(csPayForm.amount) > svc.remaining && (
                      <div style={{ fontSize: '11px', color: '#FF4D6A', marginTop: '4px' }}>Amount exceeds remaining balance!</div>
                    )}
                    {csPayForm.amount && isDeduct && Number(csPayForm.amount) > svc.remaining && (
                      <div style={{ fontSize: '11px', color: '#FF4D6A', marginTop: '4px' }}>Deduction exceeds outstanding balance!</div>
                    )}
                  </div>

                  {isDeduct && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Reason for Deduction <span style={{ color: '#FF6347' }}>*</span>
                      </label>
                      <textarea
                        value={csPayForm.reason}
                        onChange={(e) => setCsPayForm({ ...csPayForm, reason: e.target.value })}
                        placeholder="e.g. refunded customer, overcharge correction, cancellation"
                        rows={2}
                        data-testid="cs-payment-reason"
                        style={{ width: '100%', padding: '10px 14px', fontSize: '13px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                      {csPayForm.amount && !(csPayForm.reason || '').trim() && (
                        <div style={{ fontSize: '11px', color: '#FF4D6A', marginTop: '4px' }}>Please enter a reason for the deduction.</div>
                      )}
                    </div>
                  )}

                  {!isDeduct && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Upload Invoice (Optional)</label>
                      {csPayForm.invoice_file ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '10px' }}>
                          <FileText size={16} style={{ color: '#10B981', flexShrink: 0 }} />
                          <span style={{ fontSize: '13px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{csPayForm.invoice_file.name}</span>
                          <button type="button" onClick={() => setCsPayForm({ ...csPayForm, invoice_file: null })} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <label style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 14px',
                          background: 'rgba(59,130,246,0.06)', border: '2px dashed rgba(59,130,246,0.25)',
                          borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#3B82F6', fontWeight: 600
                        }}>
                          <Upload size={16} /> Upload Invoice
                          <input type="file" accept="image/*,.pdf" onChange={(e) => { if (e.target.files[0]) setCsPayForm({ ...csPayForm, invoice_file: e.target.files[0] }); }} style={{ display: 'none' }} data-testid="cs-payment-invoice" />
                        </label>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => { setCsPayModal(null); setCsPayMode('payment'); setCsPayForm({ amount: '', date: '', invoice_file: null, reason: '' }); }}
                      style={{ flex: 1, padding: '12px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)' }}>
                      Cancel
                    </button>
                    <button onClick={addServicePayment}
                      disabled={
                        csPaySubmitting || !csPayForm.amount || !csPayForm.date ||
                        Number(csPayForm.amount) > svc.remaining ||
                        (isDeduct && !(csPayForm.reason || '').trim())
                      }
                      data-testid="cs-payment-submit"
                      style={{
                        flex: 1, padding: '12px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: csPaySubmitting ? 'wait' : 'pointer',
                        background: (csPaySubmitting || !csPayForm.amount || !csPayForm.date) ? 'rgba(255,255,255,0.1)' : (isDeduct ? '#FF6347' : '#5EFCE8'),
                        color: (csPaySubmitting || !csPayForm.amount || !csPayForm.date) ? 'var(--text-secondary)' : (isDeduct ? '#fff' : '#0a1628'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        opacity: csPaySubmitting ? 0.6 : 1
                      }}>
                      {isDeduct ? <Minus size={14} /> : <DollarSign size={14} />} {csPaySubmitting ? 'Saving...' : (isDeduct ? 'Confirm Deduction' : 'Record Payment')}
                    </button>
                  </div>
                </>
              ) : null;
            })()}
          </div>
        </div>
      )}
      {/* Add Extra Service Modal — additional service line for Bookings → Services */}
      {extraModal && (() => {
        const svc = bookingServices.find(s => s.id === extraModal) || clientServices.find(s => s.id === extraModal);
        if (!svc) return null;
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
            onClick={(e) => { if (e.target === e.currentTarget) { setExtraModal(null); } }}>
            <div data-testid="extra-service-modal" style={{
              background: 'var(--card-bg, #1a2235)', borderRadius: '20px', padding: '28px 24px', width: '100%', maxWidth: '420px',
              border: '1px solid rgba(96,165,250,0.25)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: 700, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plus size={18} style={{ color: '#60A5FA' }} /> Add Additional Service
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                <strong>{svc.client}</strong> — adds to the current balance.
              </p>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '18px', padding: '12px', borderRadius: '10px', background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Total</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#60A5FA' }}>{Number(svc.total_amount).toLocaleString()} AED</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Remaining</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#FFB800' }}>{Number(svc.remaining).toLocaleString()} AED</div>
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Service Name *</label>
                <input type="text" value={extraForm.name} onChange={(e) => setExtraForm({ ...extraForm, name: e.target.value })}
                  placeholder="e.g. Document Translation, Express Fee" autoFocus
                  data-testid="extra-service-name"
                  style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount (AED) *</label>
                  <input type="number" value={extraForm.amount} onChange={(e) => setExtraForm({ ...extraForm, amount: e.target.value })}
                    placeholder="e.g. 300" data-testid="extra-service-amount"
                    style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</label>
                  <input type="date" value={extraForm.date} onChange={(e) => setExtraForm({ ...extraForm, date: e.target.value })}
                    data-testid="extra-service-date"
                    style={{ width: '100%', padding: '10px 14px', fontSize: '14px', background: 'var(--input-bg, rgba(255,255,255,0.06))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setExtraModal(null)}
                  style={{ flex: 1, padding: '12px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)' }}>
                  Cancel
                </button>
                <button onClick={addExtraServiceLine}
                  disabled={extraSubmitting || !extraForm.name.trim() || !(parseFloat(extraForm.amount) > 0)}
                  data-testid="extra-service-submit"
                  style={{
                    flex: 1, padding: '12px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700,
                    cursor: (extraSubmitting || !extraForm.name.trim() || !(parseFloat(extraForm.amount) > 0)) ? 'not-allowed' : 'pointer',
                    background: (extraSubmitting || !extraForm.name.trim() || !(parseFloat(extraForm.amount) > 0)) ? 'rgba(255,255,255,0.1)' : '#60A5FA',
                    color: (extraSubmitting || !extraForm.name.trim() || !(parseFloat(extraForm.amount) > 0)) ? 'var(--text-secondary)' : '#0a1628',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}>
                  <Plus size={14} /> {extraSubmitting ? 'Adding…' : 'Add Service'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AdminPage;
